import _get from "lodash/get";
import {
  displayValueWithoutSymbol,
  getDiscountValue,
  getDiscountValueWithoutPercentage
} from '../../utils/price';
import { readCookie, readCohortValueCookie, ManageCookies, readClusterCookie } from '../../components/common/Cookie';
import { getPageInfo, getPageNameForGA, readQueryParameter, getPageInfoForGTM } from '../../utils/pageInfo';
import isNode from '../ajio-util/isNode';
import { catalogIdToStoreName, ENUM } from '../../utils';
import {
  getReleaseGA4Flag,
  getMasterGA4Flag,
  getMasterGA3Flag,
  getEnableLinkCohort,
  getGA4AJIOEnable,
  getUserRelevancy,
  getSegmentTrackingEnabledFlag,
  getCopilotEnabledMsiteFlag,
  getCopilotEnabledDesktopFlag,
  getGSTFlag,
  getPlaAdsClientID,
  getBannerOsmosAdsConfig,
  getEnableAdsOnPlp,
  getEnableAdsOnSlp,
  getVisualFiltersConfig,
  getShowSimilarProducts,
  getMultiSellerSlaConfig
} from '../../refresh/selectors/featureFlagsSelector';
import { getCardsSavedCountFromState } from '../../myaccount/utils/savedPaymentCards';
import { getFeatureVariant } from '../../refresh/organism/ABFeature/Feature';
import { FLEEK_STORE } from '../../constants/constant';
import UTILS from '../../utils/jioAdsUtils';
import {
  getValidAttributionToken,
  trackCopilotMap,
  getStoredPageCategory,
  getCrossSellWidgetSource
} from '../../utils/common';
import { analytics } from './segment/segment';
import { amplitudeTrack } from './segment/amplitude';
import { isMobile } from '../../refresh/utils/deviceTypeUtils';
import { getAmplitudeVariantPayload, getAmplitudeExperimentVariant } from '../../utils/amplitudeCommon';
import { setCookie } from '../../refresh/utils/cookie'
import { storageAvailable } from '../../components/common/storageAvailable';
import { setAffiliateCookie } from '../../utils/affiliateUtils';
import { isCompanionProduct } from "../../utils/companionUtils";
import { getImpressionOosFlag } from '../../utils/products';
import { getSearchExpansionType } from '../../components/common/ListingPage/SearchMessageBanner';
import {
  getGstAddressSegmentPropsFromCart,
  isShippingGstApplicableFromGstInfo
} from './gst/gstAddressUtils';

const getPlaItemByCode = (code='') => {
    if (!storageAvailable('localStorage') || !code) return null;
    const PLA_PRODUCTS_STORAGE_KEY = 'osmosPlaProducts';

    try {
      const items = JSON.parse(localStorage.getItem(PLA_PRODUCTS_STORAGE_KEY) || '[]');

      if (!Array.isArray(items)) return null;

      return items.find(item => item.code === code) || null;
    } catch (err) {
      return null;
    }
};
export const getPlaItemByProductCode = (code='') => {
    if (!storageAvailable('localStorage') || !code) return null;
    const PLA_PRODUCTS_STORAGE_KEY = 'osmosPlaProducts';

    try {
      const items = JSON.parse(localStorage.getItem(PLA_PRODUCTS_STORAGE_KEY) || '[]');

      if (!Array.isArray(items)) return null;

      return items.find(item => item.productCode === code) || null;
    } catch (err) {
      return null;
    }
};

// ITM_SOURCE & ITM_SOURCE_DETAIL Utilities
// Persist to sessionStorage only when explicitly requested (shipping / pre-payment),
// so earlier pages keep values on the URL and do not pin them for the whole session.
export function getItmSourceParams(options = {}) {
  if (isNode()) return { itm_source: '', itm_source_detail: '' };

  const { persist = false } = typeof options === 'boolean' ? { persist: options } : options || {};

  try {
    // First check URL parameters
    const urlParams = readQueryParameter(window.location);
    const itm_source = urlParams?.itm_source || '';
    const itm_source_detail = urlParams?.itm_source_detail || '';

    if (itm_source) {
      // Store only at shipping / pre-payment so Thank You can read after gateway redirect
      if (persist && storageAvailable('sessionStorage')) {
        sessionStorage.setItem('itm_source', itm_source);
        sessionStorage.setItem('itm_source_detail', itm_source_detail || '');
      }
      return { itm_source, itm_source_detail };
    }

    // If not in URL, check sessionStorage (set at shipping)
    if (storageAvailable('sessionStorage')) {
      return {
        itm_source: sessionStorage.getItem('itm_source') || '',
        itm_source_detail: sessionStorage.getItem('itm_source_detail') || ''
      };
    }

    return { itm_source: '', itm_source_detail: '' };
  } catch (err) {
    return { itm_source: '', itm_source_detail: '' };
  }
}

export function appendItmSourceToURL(url = '', overrides = {}) {
  if (!url || isNode()) return url;
  if (
    url === '#' ||
    url === '/#' ||
    String(url)
      .toLowerCase()
      .startsWith('javascript:')
  ) {
    return url;
  }

  try {
    const hasOverride = overrides && Object.prototype.hasOwnProperty.call(overrides, 'itm_source');
    const params = hasOverride ? {} : getItmSourceParams();
    const itm_source = hasOverride ? overrides.itm_source : params.itm_source;
    const itm_source_detail = hasOverride
      ? overrides.itm_source_detail || ''
      : params.itm_source_detail;

    if (!itm_source) return url;

    const urlObj = new URL(url, window.location.origin);

    if (hasOverride) {
      urlObj.searchParams.set('itm_source', itm_source);
      if (itm_source_detail) {
        urlObj.searchParams.set('itm_source_detail', itm_source_detail);
      } else {
        urlObj.searchParams.delete('itm_source_detail');
      }
    } else {
      if (!urlObj.searchParams.has('itm_source')) {
        urlObj.searchParams.set('itm_source', itm_source);
      }
      if (!urlObj.searchParams.has('itm_source_detail') && itm_source_detail) {
        urlObj.searchParams.set('itm_source_detail', itm_source_detail);
      }
    }

    return urlObj.toString();
  } catch (err) {
    return url;
  }
}

export function clearItmSource() {
  if (isNode()) return;

  try {
    if (storageAvailable('sessionStorage')) {
      sessionStorage.removeItem('itm_source');
      sessionStorage.removeItem('itm_source_detail');
    }
  } catch (err) {
    // Silent fail
  }
}

// Segment events that should carry itm_source/itm_source_detail on their products array
const ITM_SOURCE_FALLBACK_EVENTS = [
  'Product Viewed',
  'Product Clicked',
  'Pdp Product Image Details',
  'Pdp Product Image Details Interaction',
  'Add To Bag Clicked',
  'Bag Details Viewed',
  'Bag Details Update',
  'Proceed to Payment Clicked',
  'Payment Page Product Details',
  'Pay Now Clicked',
  'Purchased',
  'Product Wishlisted',
  'Wishlisted button clicked'
];

// Why the bag last changed ('Size Update', 'Quantity Update', ...). Bag Details
// Update fires on an interval whenever the bag payload changes, so the reason is
// recorded by the handler that mutated the bag and kept until the next mutation.
let _bagUpdateReason = '';
export function setBagUpdateReason(reason = '') {
  _bagUpdateReason = reason;
}
export function getBagUpdateReason() {
  return _bagUpdateReason || 'Bag Refresh';
}

const storage = new ManageCookies();

const segmentTrackingEnabled = "ON";
const segmentTrackingFlag = getSegmentTrackingEnabledFlag(window?.__PRELOADED_STATE__?.flags);


function getStoreType(arr = []) {
  const storeType = [...new Set(arr)];
  return storeType.join(',');
}
export function getStoreName() {
  const Host = window?.location?.href || '';
  if (window?.__PRELOADED_STATE__?.metaData?.name) {
    return window?.__PRELOADED_STATE__?.metaData?.name;
  }
  if (
    Host != '' &&
    !Host.toLowerCase().includes('uat.') &&
    !Host.toLowerCase().includes('qa.') &&
    (Host.toLowerCase().includes('ajio.com') ||
      Host.toLowerCase().includes('ril.com') ||
      Host.toLowerCase().includes('web.ajio.com'))
  ) {
    let storeValue = '';
    storeValue = Host.replace(
      /(http(s){0,1}:\/\/)*((w){3,}.)*([a-z]*[0-9]{0,}-)*((.web){0,}(.services){0,}(.ajio){0,}(.ril){0,}.com(.*)){0,}/gi,
      ''
    );
    return storeValue;
  }
  return 'ajio';
}

export function getCurrentStoreType() {
  const storeName = getStoreName();
  const lowercasedStoreName = (storeName || '').toLowerCase();

  if (lowercasedStoreName === 'luxe' || lowercasedStoreName === 'ajiogram' || lowercasedStoreName === 'ajio') {
    return lowercasedStoreName;
  }

  return 'ajio';
}

export function getSisStore() {
  const storeName = getStoreName();
  const lowercasedStoreName = (storeName || '').toLowerCase();

  if (lowercasedStoreName === 'luxe' || lowercasedStoreName === 'ajiogram' || lowercasedStoreName === 'ajio') {
    return '';
  }

  return lowercasedStoreName;
}

export function getUserUUID() {
  const userUUID = readCookie('EI');
  return userUUID ? decodeURIComponent(userUUID) : '';
}



export function getUserTempId() {
  const userTempId = readCookie('deviceId');
  return userTempId ? decodeURIComponent(userTempId) : '';
}

export function getSkuAutoSelectSizeData(size) {
  const sizeSelect = sessionStorage.getItem('defaultSKUSizeSelection');
  return sizeSelect ? `${sizeSelect}_${size}` : size;
}
function getCustSegmentIds() {
  return readCookie('cohortSegment') || '';
}

function getCohortIds(store, serviceName) {
  const newStoreVal = store === 'AJIO' ? 'rilfnl' : store;
  return readCohortValueCookie('userCohortValues', newStoreVal, serviceName);
}

function gtmFixedpram() {
  const user_status = readCookie('U') || 'anonymous';
  const login_type = user_status !== 'anonymous' ? 'logged in' : ' not logged In';
  return {
    user_id: getUserUUID(),
    contains_store: getStoreName(),
    store_type: getStoreName(),
    pricing_cust_seg: getCustSegmentIds(),
    login_type,
    ...getPageUserInfo()
  };
}

function getPageUserInfo() {
  if (isNode()) return {};
  let user_status = readCookie('U') || 'anonymous';
  let user_ga;
  let user_base_category;
  const refered_Page = document?.referrer;
  const data = (localStorage && JSON.parse(localStorage.getItem('customerTypeData'))) || {};
  const isExcisting = data ? data.customerType === 'Existing' : false;
  const user_type = isExcisting ? 'Existing' : 'New';
  if (user_status !== 'anonymous') {
    user_status = decodeURIComponent(user_status);
    return {
      user_status: 'loggedin',
      user_type,
      user_ga: readCookie('_ga'),
      user_base_category: localStorage?.getItem('categoryPath'),
      refered_Page
    };
  }
  return {
    user_status: 'guest',
    refered_Page,
    user_type
  };
}

function getPageUserInfoForSegment() {
  if (isNode()) return {};
  let user_status = readCookie('U') || 'anonymous';
  let user_ga;
  let user_base_category;
  const refered_Page = document?.referrer;
  const data = (localStorage && JSON.parse(localStorage.getItem('customerTypeData'))) || {};
  const isExcisting = data ? data.customerType === 'Existing' : false;
  const user_type = isExcisting ? 'Existing' : 'New';
  if (user_status !== 'anonymous') {
    user_status = decodeURIComponent(user_status);
    return {
      loggedin_status: 'loggedin',
      loggedin_type: user_type,
      //user_ga: readCookie('_ga'),
      //user_base_category: localStorage?.getItem('categoryPath'),
      //page_url: refered_Page
    };
  }
  return {
    loggedin_status: 'guest',
    //page_url: refered_Page,
    loggedin_type: user_type,
  };
}

function checkGtmDatalayerExistStatus() {
  return !!(window && window.dataLayer);
}

function setUserStatus() {
  if (isNode()) return '';
  const user_status = readCookie('U') || 'anonymous';
  if (user_status !== 'anonymous') return 'loggedin';
  return 'guest';
}

export function gtmDatalayerPushData(obj, exceptionTitle) {
  try {
    if (checkGtmDatalayerExistStatus()) {
      if ('is_GA4' in obj && obj.is_GA4 === false) {
        delete obj.is_GA4;
      }
      const { pageType } = getPageInfo(
        window.location.pathname || '',
        false,
        obj?.store_type.toLowerCase() === FLEEK_STORE.toLowerCase()
      );
      const { pageName } = getPageNameForGA(
        window.location.pathname || '',
        false,
        obj?.store_type.toLowerCase() === FLEEK_STORE.toLowerCase()
      );
      obj = {
        ...obj,
        page_type: obj.page_type ? obj.page_type : pageType,
        page_name: obj.page_name ? obj.page_name : pageName
      };

      if (obj.hasOwnProperty('event_category') || obj.hasOwnProperty('eventCategory')) {
        obj.category = obj.event_category || obj.eventCategory;
        delete obj.event_category;
        delete obj.eventCategory;
      }

      if (obj.hasOwnProperty('event_action') || obj.hasOwnProperty('eventAction')) {
        obj.action = obj.event_action || obj.eventAction;
        delete obj.event_action;
        delete obj.eventAction;
      }

      if (obj.hasOwnProperty('event_label') || obj.hasOwnProperty('eventLabel')) {
        obj.label = obj.event_label || obj.eventLabel;
        delete obj.event_label;
        delete obj.eventLabel;
      }

      if (obj.event === 'pageload' && !obj.user_status) {
        obj.user_status = setUserStatus();
      }

      const ga4AjioEnable = getGA4AJIOEnable(window?.__PRELOADED_STATE__?.flags);
      if (ga4AjioEnable) {
        if (obj.event === 'pageload') obj.event = 'page_view';
        obj.isGA4 = true;
      }

      const userHybrisId = getUserUUID();
      if (userHybrisId) obj.user_hybris_id = userHybrisId;

      const userTempId = getUserTempId();
      if (userTempId) obj.user_temp_id = userTempId;
      obj.build_number = '25_11_0';

      window.dataLayer.push(obj);

    }
  } catch (e) {
    console.log(`${exceptionTitle} - Exception: ${e.message}`);
  }
}

let _lastPageViewedUrl = '';

export function segmentDatalayerPushData(obj, exceptionTitle) {
  try { 
      delete obj.is_GA4;      
      const { pageType } = getPageInfo(
        window.location.pathname || '',
        false,
        obj?.store_type?.toLowerCase() === FLEEK_STORE.toLowerCase()
      );
      const { pageName } = getPageNameForGA(
        window.location.pathname || '',
        false,
        obj?.store_type?.toLowerCase() === FLEEK_STORE.toLowerCase()
      );
      const _urlParams = new URLSearchParams(window.location.search);
      const _isTrendWidget = _urlParams.get('isTrendWidget') === 'true';
      const _isBrandPlp = !!_urlParams.get('brandPageCode');
      const _resolvedScreenName = _isTrendWidget
        ? 'widget_plp_screen'
        : _isBrandPlp
          ? 'brand plp screen'
          : obj.page_name ? obj.page_name : (obj.screen_name || pageName);
      obj = {
        ...obj,
        page_type: obj.page_type ? obj.page_type : pageType,
        screen_name: _resolvedScreenName
      };
      delete obj.page_name;

      if (obj.hasOwnProperty('event_category') || obj.hasOwnProperty('eventCategory')) {
        //obj.category = obj.event_category || obj.eventCategory;
        delete obj.event_category;
        delete obj.eventCategory;
      }

      if (obj.hasOwnProperty('event_action') || obj.hasOwnProperty('eventAction')) {
        //obj.action = obj.event_action || obj.eventAction;
        delete obj.event_action;
        delete obj.eventAction;
      }

      if (obj.hasOwnProperty('event_label') || obj.hasOwnProperty('eventLabel')) {
        //obj.label = obj.event_label || obj.eventLabel;
        delete obj.event_label;
        delete obj.eventLabel;
      }

      if (obj.event === 'pageload' && !obj.user_status) {
        obj.user_status = setUserStatus();
      }

      
      const userHybrisId = getUserUUID();
      //if (userHybrisId) obj.user_hybris_id = userHybrisId;

      //const userTempId = getUserTempId();
      //if (userTempId) obj.user_temp_id = userTempId;
      //obj.build_number = '25_11_0';

     // window.dataLayer.push(obj);

      const hit_numner = sessionStorage.getItem("hit_number") !== null ? parseInt(sessionStorage.getItem("hit_number")) : 0;
      sessionStorage.setItem("hit_number",hit_numner+1);

      obj.is_entrance_flag = checkEntranceFlag(); //if referer other than ajio.com or missing 
      if (!obj.previous_screen_name) {
        obj.previous_screen_name = getPreviousScreenName();
      }
      
    
      //obj.session_id = sessionStorage.getItem("sessionId");
      obj.persona_store_type = readClusterCookie('cluster', name);
     // obj.device_id = readCookie("deviceId");
      //obj.user_id = readCookie("uI");
     // if (userHybrisId) obj.customer_pk = userHybrisId;


      obj.hit_number = hit_numner+1;
      obj.redirection_type = obj.type_of_redirection || obj.redirection_type;
      delete obj.type_of_redirection;
      obj.plp_number_of_results = obj.number_results;
      delete obj.number_results;
    const affiliateFeatureConfig = getAmplitudeVariantPayload(
      'affiliatefeatureconfig',
      window?.__PRELOADED_STATE__,
      window?.__PRELOADED_STATE__?.flags?.affiliateFeatureConfig
    );
    const creatorprogramconfig = getAmplitudeVariantPayload(
      'creatorprogramconfig',
      window?.__PRELOADED_STATE__,
      window?.__PRELOADED_STATE__?.flags?.creatorprogramconfig
    );
    const affiliateAttributionWindow =
      getAmplitudeVariantPayload(
        'affiliateattributionwindow',
        window?.__PRELOADED_STATE__,
        window?.__PRELOADED_STATE__?.flags?.affiliateAttributionWindow
      ) || 24;
    const affiliateConfigForCookie = {
      ...(affiliateFeatureConfig || {}),
      attributionWindow: affiliateAttributionWindow
    };
    const { utm_medium } = readQueryParameter(window.location);
    if (utm_medium === 'affiliate') {
      setAffiliateCookie(affiliateConfigForCookie, creatorprogramconfig);
    } else if (utm_medium === 'creator' && creatorprogramconfig?.masterFlag) {
      setAffiliateCookie(affiliateConfigForCookie, creatorprogramconfig);
    }
      if (obj.event === 'Screen Viewed' && obj.referrer != 'orders') {
        //"number_of_sizes_OOS": 0,
        //"number_of_sizes": 5,
        obj.number_of_size_variant_available = obj.number_of_sizes;
        obj.number_of_size_variant_oos= obj.number_of_sizes_OOS;
        delete obj.number_of_sizes;
        delete obj.number_of_sizes_OOS;
        delete obj.number_of_coupons_shown;
        delete obj.Product_MRP;
        delete obj.product_selling_price;
        delete obj.product_trade_discount;
        delete obj.standard_size_guide;
        delete obj.bbs_widget;
        delete obj.bbs_widget_state;
        delete obj.bbs_banner;
        delete obj.pdp_td;
        delete obj.sale_discount;
        delete obj.sale_delta;
        delete obj.hybris_id;
        delete obj.contains_store;
        delete obj.pricing_cust_seg;
      
        delete obj.user_hybris_id;       
        delete obj.plp_source;
        if (obj.plp_source_details != null && obj.plp_source_details !== false) {
          obj.screen_source_detail = obj.plp_source_details;
        } else if (obj.screen_source_detail == null) {
          obj.screen_source_detail = '';
        }
        delete obj.plp_source_details;
        if(obj.plp_store_type) {
          obj.store_type = obj.plp_store_type;
          delete obj.plp_store_type;
        }
        const currentStoreType = getCurrentStoreType();
        if (currentStoreType === 'ajiogram' || currentStoreType === 'luxe') {
          obj.store_type = currentStoreType;
        } else if(!obj.store_type){
            obj.store_type = currentStoreType;
        }
        delete obj.zero_search_term;
        delete obj.banner_theme_extended;
        delete obj.user_search_term;
        delete obj.page_type;
        delete obj.user_id;
        delete obj.plp_category_id;
        delete obj.bbs_widget_title;

        if(obj.plp_page_title){
          obj.screen_title = obj.plp_page_title;
          delete obj.plp_page_title;
        }
       
        
        delete obj.coupon_available;
        delete obj.coupon_applicable;
        delete obj.store;
        delete obj.supercash_earn;

        if(obj.supercash_balance){
          obj.wallet_points_balance = Number(obj.supercash_balance|| 0);
        }
        if(obj.ajio_cash_balance){
          obj.wallet_cash_balance = Number(obj.ajio_cash_balance || 0);
        }

        delete obj.supercash_balance;
        delete obj.ajio_cash_balance;
        delete obj.coupon_not_applicable;
        delete obj.user_hybris_id;
        
      
       
        const { utm_source, utm_medium, utm_id, utm_campaign, utm_content } = readQueryParameter(window.location);
        obj.traffic_source = utm_source;
        obj.traffic_medium = utm_medium;
        obj.campaign_id = utm_id;
        obj.campaign_name = utm_campaign;
        obj.campaign_content = utm_content;
       // obj.campaign_ad_id = readQueryParameter('utm_medium');;
       // obj.campaign_ad_name = readQueryParameter('utm_medium');;
       // obj.campaign_adset_id = readQueryParameter('utm_medium');;
       // obj.campaign_adset_name =readQueryParameter('utm_medium');;
        if(!obj.store_type){
            obj.store_type = getCurrentStoreType();
        }

        if(!obj.sis_store){
            obj.sis_store = getSisStore();
        }

      if (!obj.store_name) {
        obj.store_name = getStoreName();
      }

        //facebook cookies
        obj.fbp = readCookie('_fbp') || '';
        obj.fbc = readCookie('_fbc') || '';

        const gclid = readQueryParameter(window.location).gclid;
        if (gclid) {
          setCookie('gclid', gclid)
        }

        const fbclid = readQueryParameter(window.location).fbclid;
        if (fbclid) {
          setCookie('fbclid', fbclid)
        }

        const gclidFromCookie = readCookie('gclid') || '';
        const fbclidFromCookie = readCookie('fbclid') || '';

        // Build ad tracking context
        const adTrackingContext = {
          ad_tracking_data: {
            gclid: gclidFromCookie || gclid || "NA",
            fbclid: fbclidFromCookie || fbclid || "NA",
            fbp_id: obj.fbp || "NA",
            fbc_id: obj.fbc || "NA"
          }
        };
     
        const currentUrl = window.location.href;
        const isGSTScreen = obj.screen_name?.includes('GST');
        if (_lastPageViewedUrl === currentUrl && !isGSTScreen) {
          return;
        }
        _lastPageViewedUrl = currentUrl;
        obj.event = "Page Viewed";
        amplitudeTrack(obj.event, obj);
        if(segmentTrackingFlag === "ON"){
          analytics?.page(obj.event, obj, {
            context: adTrackingContext
          });
        }
      } else {
          let adTrackingContext = {};
          delete obj.referrer;
          if(obj.event == "Product Clicked" || obj.event == "Product Viewed"){

              // commented out as it is assigning the deleted property page_name to screen_name
              // obj.screen_name = obj.page_name;
              // delete obj.page_name;
              // Only remap the legacy GTM keys when present. Product Clicked callers set
              // screen_title/screen_source directly and never set the plp_* aliases, so an
              // unconditional assignment here was overwriting both with undefined.
              if (obj.plp_page_title !== undefined) {
                obj.screen_title = obj.plp_page_title;
                delete obj.plp_page_title;
              }
              if (obj.plp_source_details !== undefined) {
                obj.screen_source = obj.plp_source_details;
                delete obj.plp_source_details;
              }
          }

          if(obj.event == "Coupon Select Clicked"){

            obj.cart_id = readCookie('C');
            //obj.click_cta_name = 'Apply';
          }

          if(obj.event == "Add To Bag Clicked"){
            //facebook cookies
            obj.fbp = readCookie('_fbp') || '';
            obj.fbc = readCookie('_fbc') || '';

            const gclid = readQueryParameter(window.location).gclid;
            if (gclid) {
              setCookie('gclid', gclid)
            }

            const fbclid = readQueryParameter(window.location).fbclid;
            if (fbclid) {
              setCookie('fbclid', fbclid)
            }

            const gclidFromCookie = readCookie('gclid') || '';
            const fbclidFromCookie = readCookie('fbclid') || '';

            // Build ad tracking context
            adTrackingContext = {
              ad_tracking_data: {
                gclid: gclidFromCookie || gclid || "NA",
                fbclid: fbclidFromCookie || fbclid || "NA",
                fbp_id: obj.fbp || "NA",
                fbc_id: obj.fbc || "NA"
              }
            };
          }

          if(obj.event == "Product Wishlisted"){
            //facebook cookies
            obj.fbp = readCookie('_fbp') || '';
            obj.fbc = readCookie('_fbc') || '';

            const gclid = readQueryParameter(window.location).gclid;
            if (gclid) {
              setCookie('gclid', gclid)
            }

            const fbclid = readQueryParameter(window.location).fbclid;
            if (fbclid) {
              setCookie('fbclid', fbclid)
            }

            const gclidFromCookie = readCookie('gclid') || '';
            const fbclidFromCookie = readCookie('fbclid') || '';

            // Build ad tracking context
            adTrackingContext = {
              ad_tracking_data: {
                gclid: gclidFromCookie || gclid || "NA",
                fbclid: fbclidFromCookie || fbclid || "NA",
                fbp_id: obj.fbp || "NA",
                fbc_id: obj.fbc || "NA"
              }
            };
          }

          if(obj.event == "InitiateCheckout"){
            //facebook cookies
            obj.fbp = readCookie('_fbp') || '';
            obj.fbc = readCookie('_fbc') || '';

            const gclid = readQueryParameter(window.location).gclid;
            if (gclid) {
              setCookie('gclid', gclid)
            }

            const fbclid = readQueryParameter(window.location).fbclid;
            if (fbclid) {
              setCookie('fbclid', fbclid)
            }

            const gclidFromCookie = readCookie('gclid') || '';
            const fbclidFromCookie = readCookie('fbclid') || '';

            // Build ad tracking context
            adTrackingContext = {
              ad_tracking_data: {
                gclid: gclidFromCookie || gclid || "NA",
                fbclid: fbclidFromCookie || fbclid || "NA",
                fbp_id: obj.fbp || "NA",
                fbc_id: obj.fbc || "NA"
              }
            };
          }

          if(obj.event == "Pdp Product Image Details"){
            //facebook cookies
            obj.fbp = readCookie('_fbp') || '';
            obj.fbc = readCookie('_fbc') || '';

            const gclid = readQueryParameter(window.location).gclid;
            if (gclid) {
              setCookie('gclid', gclid)
            }

            const fbclid = readQueryParameter(window.location).fbclid;
            if (fbclid) {
              setCookie('fbclid', fbclid)
            }

            const gclidFromCookie = readCookie('gclid') || '';
            const fbclidFromCookie = readCookie('fbclid') || '';

            // Build ad tracking context
            adTrackingContext = {
              ad_tracking_data: {
                gclid: gclidFromCookie || gclid || "NA",
                fbclid: fbclidFromCookie || fbclid || "NA",
                fbp_id: obj.fbp || "NA",
                fbc_id: obj.fbc || "NA"
              }
            };
          }

          if(obj.event == "Purchased"){
            //facebook cookies
            obj.fbp = readCookie('_fbp') || '';
            obj.fbc = readCookie('_fbc') || '';

            const gclid = readQueryParameter(window.location).gclid;
            if (gclid) {
              setCookie('gclid', gclid)
            }

            const fbclid = readQueryParameter(window.location).fbclid;
            if (fbclid) {
              setCookie('fbclid', fbclid)
            }

            const gclidFromCookie = readCookie('gclid') || '';
            const fbclidFromCookie = readCookie('fbclid') || '';

            // Build ad tracking context
            adTrackingContext = {
              ad_tracking_data: {
                gclid: gclidFromCookie || gclid || "NA",
                fbclid: fbclidFromCookie || fbclid || "NA",
                fbp_id: obj.fbp || "NA",
                fbc_id: obj.fbc || "NA"
              }
            };
          }

          if(obj.ecommerce && obj.ecommerce.items){
              obj.products = obj.ecommerce.items;
              delete obj.ecommerce;
          }
          enrichSearchPlpProductEvent(obj);
          enrichPlpProductViewedItems(obj);
          // Banner Viewed / Banner Clicked build their banner list as
          // `properties: { banners: [...] }`, which reaches Amplitude as
          // `properties.banners` — so every expected `banners.*` property reads as
          // absent. Hoist the array to the top level (same normalisation the
          // ecommerce.items block above does) and carry the event-level ad
          // attributes onto each banner so banners.ad_flag / banners.ad_id /
          // banners.ad_source are populated as well.
          if (obj.properties && Array.isArray(obj.properties.banners)) {
            obj.banners = obj.properties.banners.map(banner => ({
              ...banner,
              ad_flag: Boolean(banner.ad_flag ?? obj.ad_flag),
              ad_id: banner.ad_id || obj.ad_id || 'NA',
              ad_source: banner.ad_source || (banner.uclid ? 'osmos' : 'no_ads')
            }));
            delete obj.properties.banners;
            if (!Object.keys(obj.properties).length) delete obj.properties;
          }
          if (
            ITM_SOURCE_FALLBACK_EVENTS.includes(obj.event) &&
            Array.isArray(obj.products) &&
            obj.products.length
          ) {
            const isWishlistRoute =
              typeof window !== 'undefined' && window.location?.pathname?.includes('/wishlist');

            if (isWishlistRoute) {
              // Closet/wishlist: always set itm_source; do not pass itm_source_detail
                  obj.products = obj.products.map(product => ({
                ...product,
                itm_source: 'wishlist'
                  }));
            } else {
                const {
                  itm_source: _fallbackItmSource,
                  itm_source_detail: _fallbackItmSourceDetail
                } = getItmSourceParams();
                if (_fallbackItmSource || _fallbackItmSourceDetail) {
                  obj.products = obj.products.map(product => ({
                    ...product,
                    ...(_fallbackItmSource && { itm_source: _fallbackItmSource }),
                    ...(_fallbackItmSourceDetail && { itm_source_detail: _fallbackItmSourceDetail })
                  }));
                }
              }
          }

          if(!obj.store_type){
            obj.store_type = getCurrentStoreType();
          }

          if(!obj.store_type){
            obj.store_type = getCurrentStoreType();
          }

          if(!obj.sis_store){
            obj.sis_store = getSisStore();
        }

      if (!obj.store_name) {
        obj.store_name = getStoreName();
      }

          delete obj.hybris_id;
          delete obj.user_hybris_id;
        amplitudeTrack(obj.event, obj);
        if(segmentTrackingFlag === "ON"){
          analytics?.track(obj.event, obj, {
            ...(adTrackingContext && { context: adTrackingContext })
          });
        }
        
      }    
  } catch (e) {
    console.log(`${exceptionTitle} - Exception: ${e.message}`);
  }
}

export function gtmPagePush(page_name = '', page_type = '', env = '', otherOptions = {}, segmentProps={}) {
  const eventName = 'pageload';
  const { store = 'AJIO', serviceName = '', ...restOtherOptions } = otherOptions;
  const { 
    cart= {}, 
    isAssuredGifts='', 
    internalWallets = {}, 
    coachmark_impression= '', 
    search_term='', 
    search_type='', 
    quick_filter_count='',
    quick_filter_name='',
    ads_enabled_screen_flag='',
    best_coupon_name='',
    best_coupon_discount='',
    current_experience='',
  } = segmentProps;

  const enableUniversalCohortFlow = getAmplitudeVariantPayload('enableUniversalCohortFlow', window?.__PRELOADED_STATE__, false);
  
  restOtherOptions.store_type = store;
  const storeName = getStoreName();
  const linkCohortObj = {};
  const enableLinkCohort = getEnableLinkCohort(window?.__PRELOADED_STATE__?.flags) === 'ON';
  const hasAjioStore =
    String(storeName).toLowerCase() === 'ajio' || String(storeName).toLowerCase() === 'rilfnl';
  if (enableLinkCohort && hasAjioStore) {
    const linkCohort = readCookie('linkCohort');
    const cohortValue =
      readCohortValueCookie('userCohortValues', 'ajio', serviceName, enableUniversalCohortFlow) ||
      readCohortValueCookie('userCohortValues', 'rilfnl', serviceName, enableUniversalCohortFlow);
    const link_param = linkCohort || 'no parameter';
    const current_experience = String(cohortValue || linkCohort)
      .toLowerCase()
      .startsWith('premium')
      ? 'premium'
      : 'non premium';
    linkCohortObj.link_param = link_param || '';
    linkCohortObj.current_experience = current_experience;
    segmentProps.current_experience=current_experience;
  }
  gtmDatalayerPushData(
    {
      event: eventName,
      page_name,
      page_type,
      env,
      ...restOtherOptions,
      ...getPageUserInfo(),
      hybris_id: getUserUUID(),
      contains_store: storeName,
      store_type: storeName,
      pricing_cust_seg: getCustSegmentIds(),
      secondary_user_cohort: getCohortIds(store, serviceName),
      ...linkCohortObj
    },
    'gtmPagePush'
  );

  if(segmentTrackingEnabled == 'ON'){
    
    //ignore this multiple calls
    if(page_type == 'address management screen') return;
    
    const copilotMsiteEnable = getCopilotEnabledMsiteFlag(window?.__PRELOADED_STATE__?.flags);
    const copilotWebEnable = getCopilotEnabledDesktopFlag(window?.__PRELOADED_STATE__?.flags);
    const isMobileDevice = isMobile();
    const copilotEnable = (isMobileDevice ? copilotMsiteEnable : copilotWebEnable) === 'ON';
    if (copilotEnable) {
      let segmentPageType = page_type;
      if (!segmentPageType) {
        const { pageType } = getPageInfoForGTM(window?.location?.pathname, false,
          storeName.toLowerCase() === FLEEK_STORE.toLowerCase());
        segmentPageType = pageType;
      }
      const allowedPageTypeForAIParams = ['home screen', 'plp screen', 'pdp screen', 'SHOP'];
      if (allowedPageTypeForAIParams.includes(segmentPageType)) {
        otherOptions.is_ai_assistant_visible = true;
      };
    }
    segmentPageView(page_name, page_type, env , otherOptions , segmentProps);
  }

}



export function gtmPagePushOrderListing(pagename, pagetype, totalOrders, currentPage, dataRange, segmentProps) {
  try {
    const ga4AjioEnable = getGA4AJIOEnable(window?.__PRELOADED_STATE__?.flags);
    const extraFields = {};
    if (ga4AjioEnable) {
      extraFields.event = 'page_view';
      extraFields.isGA4 = true;
    }

    const { 
      cart= {}, 
      isAssuredGifts='', 
      internalWallets = {}, 
      coachmark_impression= '', 
      search_term='', 
      search_type='', 
      quick_filter_count='',
      quick_filter_name='',
      ads_enabled_screen_flag='',
      best_coupon_name='',
      best_coupon_discount=''
    } = segmentProps;

    const userHybrisId = getUserUUID();
    if (userHybrisId) extraFields.user_hybris_id = userHybrisId;

    const userTempId = getUserTempId();
    if (userTempId) extraFields.user_temp_id = userTempId;

    const { pageName } = getPageNameForGA(
      window.location.pathname || '',
      false,
      getStoreName() === FLEEK_STORE.toLowerCase()
    );

    window
      ? window.dataLayer.push({
          event: 'pageload',
          pagename: pagename || pageName,
          pagetype,
          total_number_of_orders: totalOrders,
          page_number: currentPage,
          date_range: dataRange || 'Last 6 months',
          contains_store: getStoreName(),
          store_type: getStoreName(),
          build_number: '25_10_0',
          ...extraFields
        })
      : console.log('window object not found');

      if(segmentTrackingEnabled == 'ON'){
        segmentPageView(pageName, pagetype, '' , { totalOrders, currentPage , dataRange} , segmentProps);
      }

  } catch (ex) {
    console.log(`gtmPagePushOrderListing - Exception: ${ex.message}`);
  }
}

export function gtmExtraPageInfo(obj) {
  gtmDatalayerPushData({ meta: obj }, 'gtmEventPush');
}

export function gtmExtraInfoMeta(key, value) {
  try {
    if (window) {
      let number = -1;
      window.dataLayer.forEach(function(elem, i) {
        for (const item in elem) {
          if (item == 'meta') {
            number = i;
            return;
          }
          break;
        }
      });
      number == -1
        ? window.dataLayer.push({ meta: { [key]: value } })
        : (window.dataLayer[number].meta[key] = value);
    } else {
      console.log('gtmEventPush: window undefined');
    }
  } catch (ex) {
    console.log(`gtmEventPush - Exception: ${ex.message}`);
  }
}

export function gtmEventPush(obj, customStoreAttr = '') {
  gtmDatalayerPushData(
    {
      ...obj,
      hybris_id: getUserUUID(),
      contains_store: customStoreAttr || getStoreName(),
      store_type: getStoreName(),
      ...getPageUserInfo()
    },
    'gtmEventPush'
  );
}

export function segmentEventPush(obj, customStoreAttr = '') {
  segmentDatalayerPushData(
    {
      ...obj,
      hybris_id: getUserUUID(),
      store_name: customStoreAttr || getStoreName(),
      store_type: getCurrentStoreType(),
      ...getPageUserInfoForSegment()
    },
    'gtmEventPush'
  );
}



export function gtmEventPushPLP(obj, customStoreAttr = '', checkGA4 = false) {
  
  if (checkGA4) {
    const masterGA3 = getMasterGA3Flag() === 'ON';
    const masterGA4 = getMasterGA4Flag() === 'ON';
    const releaseGA4 = getReleaseGA4Flag() === 'ON';
    const isGA4Feature = getFeatureVariant('isGA4Feature');
    const storeName = getStoreName();
    if (!(isGA4Feature === '1' && releaseGA4 && masterGA4 && !masterGA3 && storeName === 'ajio')) {
      return undefined;
    }
  }

  let user_status = readCookie('U') || 'anonymous';
  let login_type = user_status !== 'anonymous' ? 'logged in' : ' not logged In';

  gtmDatalayerPushData(
    {
      ...obj,
      user_id: getUserUUID(),
      hybris_id: getUserUUID(),
      contains_store: getStoreName(),
      store_type: getStoreName(),
      pricing_cust_seg: getCustSegmentIds(),
      login_type,
      ...getPageUserInfo()
    },
    'gtmEventPushPLP'
  );
}

export function gtmProductImpressions(ProductArray) {
  gtmDatalayerPushData(
    {
      event: 'ee-impression',
      ecommerce: {
        currencyCode: 'INR',
        impressions: ProductArray,
        contains_store: getStoreName(),
        store_type: getStoreName()
      }
    },
    'gtmProductImpressions'
  );
}

export function getTrendWidgetItmSourceProps(overrideWidgetName) {
  if (overrideWidgetName) {
    return { itm_source: 'widget', itm_source_detail: overrideWidgetName };
  }
  const urlParams = new URLSearchParams(window.location.search);
  const isTrendWidget = urlParams.get('isTrendWidget') === 'true';
  const widgetName = urlParams.get('widgetName');
  const widgetTitle = urlParams.get('widgetTitle');
  if (!isTrendWidget && !widgetName && !widgetTitle) return {};
  const itm_source_detail = widgetName || widgetTitle || 'trends_plp_widget';
  return { itm_source: 'widget', itm_source_detail };
}

export function gtmListingProductImpressions(
  ProductArray,
  showPlpExtendedBanner,
  plp_source_details,
  isPLPSource,
  searchText,
  previousSource,
  sourcePage = '',
  parentProduct,
  impression_source,
  impression_type,
  token = getValidAttributionToken(),
  itmSourceDetailValue = '',
  pageCategory = '',
  bottomCardFilterValue = []
) {
  const resolvedPageCategory = pageCategory || getStoredPageCategory();
  const resolvedToken = token || getValidAttributionToken();
  const adsDataClickImp = JSON.parse(localStorage.getItem('adsDataClickImp'));
  const getStoreTypeArray =
    ProductArray?.length >= 0
      ? ProductArray
      : ProductArray?.impressions?.length >= 0
      ? ProductArray.impressions
      : [];
  //
  const reduxState = window?.__PRELOADED_STATE__;
  const parentProdData = parentProduct ? parentProduct : ProductArray.parentProduct;
  const userRelevancyFlag = getUserRelevancy(window?.__PRELOADED_STATE__?.flags);
  const ga4AjioEnable = getGA4AJIOEnable(window?.__PRELOADED_STATE__?.flags);
  const isVertexCategoryEnabled= getAmplitudeVariantPayload('enableCategoryWidgetsVertex', reduxState, false) && getAmplitudeExperimentVariant('vertex-exp-flag',reduxState);

  // console.log("ga4AjioEnable "+ ga4AjioEnable);
  if (ga4AjioEnable) {
    let itemListName = '';
    let promotionArray = [];
    if (typeof ProductArray === 'object') {
      promotionArray =
        ProductArray.promotionArray || ProductArray.impressions || getStoreTypeArray || [];
    }
    if (promotionArray.length > 0) {
      itemListName = promotionArray[0].list || '';
    }
    let isDressTool = false;
    let dressToolSessionData;
    if (window?.location.search?.includes('dressTool')) {
      isDressTool = true;
      let tempDressToolSessionData = sessionStorage.getItem('dressToolIngressDetails') || {};
      if (tempDressToolSessionData) {
        dressToolSessionData = JSON.parse(tempDressToolSessionData);
      }
    }
    
    const previousSourceValue = window?.location.pathname?.includes('/search') || isVertexCategoryEnabled
      ? `|${previousSource}`
      : '';
    const newObjForGTM = {
      event: 'view_item_list',
      plp_page_title: adsDataClickImp?.plp_page_title ? adsDataClickImp.plp_page_title : searchText,
      contains_store: getStoreName(),
      event_category: 'Enhance E-commerce',
      event_action: 'view_item_list',
      store_type: getStoreName(),
      plp_source_details: itmSourceDetailValue
        || (isDressTool
          ? (dressToolSessionData?.BannerName || plp_source_details)
          : plp_source_details),
      plp_source: isDressTool ? 'bra_dress tool' : isPLPSource,
      isGA4: true,
      ecommerce: {
        item_list_name: sourcePage === 'fleek_curated_plp_screen' ? 'fleek_visit_ajiogram_store' : itemListName,
        items: promotionArray.map(product => {
          const productDiscount =
            (product.wasProductPrice * getDiscountValueWithoutPercentage(product.dimension170)) /
              100 || 0;
          return {
            item_id: product.id,
            item_name: product.name,
            discount: Number(productDiscount) || 0,
            index: Number(product.position) || 0,
            item_brand: product.brand,
            item_list_name: product.list || itemListName,
            item_category: product.category,
            item_variant: product.variant,
            price: Number(product.price) || 0,
            coupon: product.coupon,
            quantity: Number(product.quantity) || 1,
            product_id: product.dimension12,
            product_name: product.dimension13,
            product_category: product.dimension16,
            product_store_type: product.dimension23,
            product_segment: product.dimension36,
            product_portfolio: product.dimension37,
            product_vertical: product.dimension38,
            product_brick: product.dimension39,
            product_view_type: product.dimension41,
            ratings_shown_no_of_users: product.dimension46,
            available_quantity_deliverable_non_deliverable: product.dimension59,
            segment_id_experiment_id: product.dimension60,
            delivery_tag: product.dimension61,
            product_level_edd: product.dimension66,
            jio_ads_campaign_data: product.dimension161,
            prod_jio_ads_listing: product.dimension162,
            jio_ads_option_code: product.dimension163,
            sticker_name: product.dimension165,
            offer_name: product.dimension166,
            wishlish_count: product.dimension167,
            product_discount: product.dimension170,
            product_trade_discount: product.dimension171,
            product_size: product.dimension191,
            ...(token && {
              search_attribution: token
            }),
          };
        })
      },
      banner_theme_extended: showPlpExtendedBanner ? 'yes' : 'no',
      pricing_cust_seg: getCustSegmentIds(),
      type_of_redirection: getSearchPlpRedirectionType(),
      search_term: searchText || '',
      user_search_term: searchText || '',
      page_type: sourcePage,
      page_name: sourcePage,
      page_category: resolvedPageCategory
    };
    if (isDressTool) {
      newObjForGTM.dress_tool_ingress =
        dressToolSessionData && dressToolSessionData?.IngressPoint
          ? dressToolSessionData?.IngressPoint
          : '';
    }

    gtmDatalayerPushData(newObjForGTM, 'gtmListingProductImpressions');
    
    

  } else {
    if (Array.isArray(ProductArray)) {
      ProductArray.forEach(p => delete p.wasProductPrice);
    }
    gtmDatalayerPushData(
      {
        event: 'ee_product_impressions',
        plp_page_title: adsDataClickImp?.plp_page_title
          ? adsDataClickImp.plp_page_title
          : searchText,
        contains_store: getStoreType(getStoreTypeArray?.map(el => el.dimension23)),
        store_type: getStoreName(),
        plp_source_details,
        plp_source: isPLPSource,
        ecommerce: {
          currencyCode: 'INR',
          impressions: ProductArray
        },
        banner_theme_extended: showPlpExtendedBanner ? 'yes' : 'no',
        pricing_cust_seg: getCustSegmentIds()
      },
      'gtmListingProductImpressions'
    );
  }

  if(segmentTrackingEnabled == 'ON'){
    let itemListName = '';
  let promotionArray = [];
  if (typeof ProductArray === 'object') {
    promotionArray =
      ProductArray.promotionArray || ProductArray.impressions || getStoreTypeArray || [];
  }
  if (promotionArray.length > 0) {
    itemListName = promotionArray[0].list || '';
  }
  let isDressTool = false;
  let dressToolSessionData;
  if (window?.location.search?.includes('dressTool')) {
    isDressTool = true;
    let tempDressToolSessionData = sessionStorage.getItem('dressToolIngressDetails') || {};
    if (tempDressToolSessionData) {
      dressToolSessionData = JSON.parse(tempDressToolSessionData);
    }
  }
  
  const previousSourceValue = window?.location.pathname?.includes('/search') || isVertexCategoryEnabled
    ? `|${previousSource}`
    : '';
    const newObjForSegment = {
      event: 'Product Viewed',
      // debug_lp: true,
      ...getTrendWidgetItmSourceProps(),
      screen_title: isCurrentSearchPlpScreen()
        ? getSearchPlpScreenTitle(getSearchPlpSearchTerm(searchText))
        : adsDataClickImp?.screen_title ? adsDataClickImp.screen_title : searchText,
      store_type: getStoreType(promotionArray?.map(el => el.dimension23)),
      impression_source: impression_source,
      impression_type : impression_type,
    // store_type: getStoreName(),
      previous_screen_name : previousSourceValue,
      // plp_source_details: isDressTool
      //   ? dressToolSessionData && dressToolSessionData?.BannerName
      //     ? dressToolSessionData?.BannerName
      //     : plp_source_details
      //   : plp_source_details,
      screen_source: isDressTool ? 'bra_dress tool' : isPLPSource,
      ...getParentProduct(parentProduct),
      //isGA4: true,
      ecommerce: {
        //item_list_name: sourcePage === 'fleek_curated_plp_screen' ? 'fleek_visit_ajiogram_store' : itemListName,
        items: promotionArray.map(product => {
          const productDiscount =          
            (product.wasProductPrice * getDiscountValueWithoutPercentage(product.dimension170)) 
              100 || 0;
              //console.log("product", product);
          return getProductItem(product, resolvedToken);
        })
      },
      //banner_theme_extended: showPlpExtendedBanner ? 'yes' : 'no',
      //pricing_cust_seg: getCustSegmentIds(),
      redirection_type: getSearchPlpRedirectionType(),
      search_term: isCurrentSearchPlpScreen()
        ? getSearchPlpSearchTerm(searchText)
        : searchText || '',
      search_type : '',
      //user_search_term: searchText || '',
      //page_type: sourcePage,
      screen_name: isCurrentSearchPlpScreen() ? 'search plp screen' : sourcePage,
      ...(resolvedPageCategory && { page_category: resolvedPageCategory }),
      ...(bottomCardFilterValue?.length > 0 && { filters: bottomCardFilterValue })
    };
    // if (isDressTool) {
    //   newObjForSegment.dress_tool_ingress =
    //     dressToolSessionData && dressToolSessionData?.IngressPoint
    //       ? dressToolSessionData?.IngressPoint
    //       : '';
    // }

    //send to segment
    console.log("testing newObjForSegment", newObjForSegment);
    segmentDatalayerPushData(newObjForSegment, 'gtmListingProductImpressions');
  }

}

export function gtmFleekListingProductImpressions(
  ProductArray,
  storeName,
  page_name,
  page_type,
  dimension176,
  dimension177
) {
  const getStoreTypeArray =
    ProductArray?.length >= 0
      ? ProductArray
      : ProductArray?.impressions?.length >= 0
      ? ProductArray.impressions
      : [];

  const ga4AjioEnable = getGA4AJIOEnable(window?.__PRELOADED_STATE__?.flags);
  if (ga4AjioEnable) {
    let itemListName = '';
    let promotionArray = [];
    if (typeof ProductArray === 'object') {
      promotionArray =
        ProductArray.promotionArray || ProductArray.impressions || getStoreTypeArray || [];
    }
    if (promotionArray.length > 0) {
      itemListName = promotionArray[0].list || '';
    }
    gtmDatalayerPushData(
      {
        event: 'view_item_list',
        contains_store: getStoreName(),
        store_type: getStoreName(),
        isGA4: true,
        ecommerce: {
          item_list_name: itemListName,
          items: promotionArray.map(product => {
            const productDiscount =
              (product.price * getDiscountValueWithoutPercentage(product.dimension170)) / 100 || 0;
            return {
              item_id: product.id,
              item_name: product.name,
              discount: Number(productDiscount) || 0,
              index: Number(product.position) || 0,
              item_brand: product.brand,
              item_list_name: product.list || itemListName,
              item_category: product.category,
              item_variant: product.variant,
              price: Number(product.price) || 0,
              coupon: product.coupon,
              quantity: Number(product.quantity) || 1,
              product_id: product.dimension12,
              product_name: product.dimension13,
              product_category: product.dimension16,
              product_store_type: product.dimension23,
              product_segment: product.dimension36,
              product_portfolio: product.dimension37,
              product_vertical: product.dimension38,
              product_brick: product.dimension39,
              product_view_type: product.dimension41,
              ratings_shown_no_of_users: product.dimension46,
              available_quantity_deliverable_non_deliverable: product.dimension59,
              segment_id_experiment_id: product.dimension60,
              delivery_tag: product.dimension61,
              product_level_edd: product.dimension66,
              jio_ads_campaign_data: product.dimension161,
              prod_jio_ads_listing: product.dimension162,
              jio_ads_option_code: product.dimension163,
              sticker_name: product.dimension165,
              offer_name: product.dimension166,
              wishlish_count: product.dimension167,
              product_discount: product.dimension170,
              product_trade_discount: product.dimension171,
              product_size: product.dimension191
            };
          })
        },
        pricing_cust_seg: getCustSegmentIds(),
        page_name,
        page_type,
        post_id: dimension176,
        story_id: dimension177
      },
      'gtmListingProductImpressions'
    );

  } else {
    gtmDatalayerPushData(
      {
        event: 'ee_product_impressions',
        contains_store: getStoreType(getStoreTypeArray?.map(el => el.dimension23)),
        store_type: getStoreName(),
        ecommerce: {
          currencyCode: 'INR',
          impressions: ProductArray
        },
        pricing_cust_seg: getCustSegmentIds(),
        page_name,
        page_type,
        post_id: dimension176,
        story_id: dimension177
      },
      'gtmListingProductImpressions'
    );
  }

  if(segmentTrackingEnabled == 'ON'){
    let itemListName = '';
  let promotionArray = [];
  if (typeof ProductArray === 'object') {
    promotionArray =
      ProductArray.promotionArray || ProductArray.impressions || getStoreTypeArray || [];
  }
  if (promotionArray.length > 0) {
    itemListName = promotionArray[0].list || '';
  }
  const validToken = getValidAttributionToken();
  const resolvedPageCategory = getStoredPageCategory();
  
    //send to segment
    segmentDatalayerPushData(
      {
        event: 'Product Viewed',
        //debug_plp: true,
        //contains_store: getStoreType(promotionArray?.map(el => el.dimension23)),
        //store_type: getStoreName(),
        //isGA4: true,
        ...(resolvedPageCategory && { page_category: resolvedPageCategory }),
        ecommerce: {
        // item_list_name: itemListName,
          items: promotionArray.map(product => {
            const productDiscount =
              (product.price * getDiscountValueWithoutPercentage(product.dimension170)) / 100 || 0;
            return getProductItem(product, validToken);
          })
        },
        //pricing_cust_seg: getCustSegmentIds(),
        //page_name,
        //page_type,
        //post_id: dimension176,
        //story_id: dimension177
      },
      'gtmListingProductImpressions'
    );
  }


}

export function luxeGtmProductImpressions({
  impressions,
  list,
  store_type,
  page_name,
  page_type,
  contains_luxe,
  showPlpExtendedBanner,
  fromVideoBanner = false,
  video_name = '',
  video_load_time = 0,
  ProductArray = [],
  plp_source_details = false,
  plp_source = false,
  parentProduct,
  impression_source = '',
  impression_type = '',
  bottomCardFilterValue = [],
  pageCategory = ''
}) {
  const resolvedPageCategory = pageCategory || getStoredPageCategory();
  const validToken = getValidAttributionToken();
  const videoDetailsObject = {};
  if (fromVideoBanner) {
    (videoDetailsObject.video_name = video_name),
      (videoDetailsObject.video_load_time = video_load_time);
  }
  const getStoreTypeArray =
    ProductArray?.length >= 0
      ? ProductArray
      : ProductArray?.impressions?.length >= 0
      ? ProductArray.impressions
      : [];
  const ga4AjioEnable = getGA4AJIOEnable(window?.__PRELOADED_STATE__?.flags);
  if (ga4AjioEnable) {
    let itemListName = '';
    let promotionArray = [];
    if (typeof ProductArray === 'object') {
      promotionArray =
        ProductArray.promotionArray || ProductArray.impressions || getStoreTypeArray || [];
    }
    if (promotionArray.length > 0) {
      itemListName = promotionArray[0].list || '';
    }
    const userRelevancyFlag = getUserRelevancy(window?.__PRELOADED_STATE__?.flags);

    gtmDatalayerPushData(
      {
        event: 'view_item_list',
        event_category: 'enhance ecommerce',
        event_action: 'product impression',
        event_label: list,
        store_type: getStoreName(),
        plp_source_details,
        plp_source,
        contains_store: getStoreName(),
        isGA4: true,
        ecommerce: {
          item_list_name: itemListName,
          items: getStoreTypeArray.map(product => ({
            item_id: product.id,
            item_name: product.name,
            discount: Number(product.dimension12) || 0,
            index: Number(product.position) || 0,
            coupon: product.coupon,
            item_brand: product.brand,
            item_list_name: product.list || itemListName,
            item_category: product.category,
            item_variant: product.variant,
            price: Number(product.price) || 0,
            quantity: Number(product.quantity) || 1,
            product_id: product.dimension7,
            product_name: product.dimension6,
            product_category: product.dimension13,
            product_store_type: product.dimension23,
            product_brick: product.dimension14,
            product_tag: product.dimension15,
            product_color: product.dimension10,
            product_trade_discount: product.dimension12,
            product_price: product.dimension9,
            product_brand: product.dimension8,
            product_size: product.dimension11,
            Store_type: product.dimension16,
            segment_id_experiment_id: product.dimension60,
            offer_name: product.dimension82
          }))
        },
        page_type,
        page_name,
        contains_luxe,
        banner_theme_extended: showPlpExtendedBanner ? 'yes' : 'no',
        ...videoDetailsObject
      },
      'luxeGtmProductImpressions'
    );

  } else {
    gtmDatalayerPushData(
      {
        event: 'ee_product_impressions',
        event_category: 'enhance ecommerce',
        event_action: 'product impression',
        event_label: list,
        store_type: getStoreName(),
        plp_source_details,
        plp_source,
        contains_store: getStoreType(getStoreTypeArray.map(el => el.dimension23)),
        ecommerce: {
          currencyCode: 'INR',
          impressions
        },
        page_type,
        page_name,
        contains_luxe,
        banner_theme_extended: showPlpExtendedBanner ? 'yes' : 'no',
        ...videoDetailsObject
      },
      'luxeGtmProductImpressions'
    );
  }

  if(segmentTrackingEnabled == 'ON'){
    let itemListName = '';
  let promotionArray = [];
  if (typeof ProductArray === 'object') {
    promotionArray =
      ProductArray.promotionArray || ProductArray.impressions || getStoreTypeArray || [];
  }
  if (promotionArray.length > 0) {
    itemListName = promotionArray[0].list || '';
  }
  const userRelevancyFlag = getUserRelevancy(window?.__PRELOADED_STATE__?.flags);
    //push to segment
    segmentDatalayerPushData(
      {
        event: 'Product Viewed',
        //debug_lgp: true,
        event_category: 'enhance ecommerce',
        event_action: 'product impression',
        //event_label: list,
        //store_type: getStoreName(),
        //plp_source_details,
        screen_source : plp_source,
        //contains_store: getStoreType(promotionArray.map(el => el.dimension23)),
      // isGA4: true,
         ...getParentProduct(parentProduct),
        ecommerce: {
          item_list_name: itemListName,
          items: getStoreTypeArray.map(product => getProductItem(product, validToken))
        },
        screen_type: page_type,
        screen_name: page_name,
        impression_source,
        impression_type,
        ...(resolvedPageCategory && { page_category: resolvedPageCategory }),
        ...(bottomCardFilterValue?.length > 0 && {
          filters: bottomCardFilterValue
        })
        //contains_luxe,
        //banner_theme_extended: showPlpExtendedBanner ? 'yes' : 'no',
        //...videoDetailsObject
      },
      'luxeGtmProductImpressions'
    );
  }
}

export function gtmProductClick(id, name, price, brand, cat, variant, position, list) {
  gtmDatalayerPushData(
    {
      event: 'ee-productClicks',
      store_type: getStoreName(),
      contains_store: getStoreName(),
      ecommerce: {
        click: {
          actionField: { list },
          products: [
            {
              name,
              id,
              price,
              brand,
              category: cat,
              variant,
              position
            }
          ]
        }
      }
    },
    'gtmProductClick'
  );
}

export function luxeGtmProductClick({
  name,
  id,
  price,
  wasProductPrice,
  brand,
  position,
  category,
  product_size,
  product_color,
  product_trade_discount,
  product_brick,
  product_tag,
  list,
  impression_source = '',
  impression_type = '',
  store_type,
  pageType,
  pageName,
  sourceStore,
  containsLuxe,
  showPlpExtendedBanner,
  productTagEngineValue,
  productPriceLabel,
  video_name = '',
  video_load_time = 0,
  plpViewType,
  catalog,
  segmentId = '',
  experimentId = '',
  sourcePage = false,
  jioAdsSource = false,
  offerName = '',
  itmSourceDetailValue = '',
  skuId = '',
  adsData = {},
  sellerId = '',
  collection_position = 0,
  // Optional attributes the Amplitude Product Clicked taxonomy expects. Callers
  // that have them (widgets rendering full product objects) pass them through;
  // the rest fall back to the defaults below.
  averageRating = 0,
  productVertical = '',
  productWishlistedCount = 0,
  productOosFlag = false,
  mediaType = '',
  collectionPosition,
  crossSellType = '',
  itmSource = '',
  itmSourceDetail = '',
  discountPrice
}) {
  const userRelevancyFlag = getUserRelevancy(window?.__PRELOADED_STATE__?.flags);
  const dimension60Data =
    Number(segmentId) || Number(experimentId) ? `${segmentId}|${experimentId}` : '';
  const ga4AjioEnable = getGA4AJIOEnable(window?.__PRELOADED_STATE__?.flags);
  let validToken = getValidAttributionToken();
  if (ga4AjioEnable) {
    const productDiscount =
      (wasProductPrice * getDiscountValueWithoutPercentage(product_trade_discount)) / 100 || 0;
    gtmDatalayerPushData(
      {
        event: 'select_item',
        event_category: 'enhance ecommerce',
        event_action: 'product click',
        event_label: list,
        store_type: getStoreName(),
        plp_source_details: jioAdsSource ? jioAdsSource.replaceAll('[ccb]', Date.now()) : false,
        plp_source: sourcePage || false,
        contains_store: catalogIdToStoreName(catalog),
        isGA4: true,
        ecommerce: {
          item_list_name: list,
          items: [
            {
              item_id: id,
              item_name: name,
              index: Number(position) || 0,
              item_brand: brand,
              item_list_name: list,
              item_category: category,
              price: Number(price) || 0,
              discount: Number(productDiscount) || 0,
              quantity: 1,
              coupon: '',
              product_name: name, // pass value for product_name
              product_id: id, // pass value for product_id
              product_size: product_size, // pass value for product_size
              product_trade_discount, // pass value for product_trade_discount
              product_category: category, // pass value for product_category
              product_brick: product_brick, // pass value for product_brick
              product_store_type: catalogIdToStoreName(catalog), // pass value for Store_type
              product_view_type: plpViewType, // pass value for pl view type: grid/list,
              segment_id_experiment_id: dimension60Data,
              sticker_name: productTagEngineValue || '', // product sticker name
              product_brand: brand, // pass value for product_brand
              product_price: price, // pass value for product_price
              product_color: isNaN(product_color) ? product_color : '', // pass value for product_color
              product_tag: product_tag, // pass value for product_tag
              Store_type: sourceStore,
              ...(validToken && {
                search_attribution: validToken
              }),
              ...(offerName ? { offer_name: offerName } : {}),
              ...(itmSourceDetailValue ? { plp_source_details: itmSourceDetailValue } : {})
            }
          ]
        },
        page_type: pageType,
        page_name: pageName,
        contains_luxe: containsLuxe,
        banner_theme_extended: showPlpExtendedBanner ? 'yes' : 'no',
        video_name,
        video_load_time
      },
      'luxeGtmProductClick'
    );

    

  } else {
    gtmDatalayerPushData(
      {
        event: 'ee_product_click',
        event_category: 'enhance ecommerce',
        event_action: 'product click',
        event_label: list,
        store_type: getStoreName(),
        plp_source_details: jioAdsSource ? jioAdsSource.replaceAll('[ccb]', Date.now()) : false,
        plp_source: sourcePage || false,
        contains_store: catalogIdToStoreName(catalog),
        ecommerce: {
          click: {
            actionField: { list },
            products: [
              {
                name,
                id,
                price,
                brand,
                category,
                position,
                dimension6: name, // pass value for product_name
                dimension7: id, // pass value for product_id
                dimension8: brand, // pass value for product_brand
                dimension9: price, // pass value for product_price
                dimension10: isNaN(product_color) ? product_color : '', // pass value for product_color
                dimension11: product_size, // pass value for product_size
                dimension12: product_trade_discount, // pass value for product_trade_discount
                dimension13: category, // pass value for product_category
                dimension14: product_brick, // pass value for product_brick
                dimension15: product_tag, // pass value for product_tag
                dimension16: sourceStore, // pass value for Store_type
                dimension23: catalogIdToStoreName(catalog),
                dimension41: plpViewType, // pass value for pl view type: grid/list,
                dimension60: dimension60Data,
                dimension89: productTagEngineValue || '', // product sticker name
                dimension90: productPriceLabel || '', // pass price label
                dimension91: '',
                ...(offerName
                  ? { [getStoreName() === 'luxe' ? 'dimension82' : 'dimension166']: offerName }
                  : {})
              }
            ]
          }
        },
        page_type: pageType,
        page_name: pageName,
        contains_luxe: containsLuxe,
        banner_theme_extended: showPlpExtendedBanner ? 'yes' : 'no',
        video_name,
        video_load_time
      },
      'luxeGtmProductClick'
    );
  }

  if(segmentTrackingEnabled == 'ON'){
    const productDiscount =
    (wasProductPrice * getDiscountValueWithoutPercentage(product_trade_discount)) / 100 || 0;
    const searchExpansionType = getSearchExpansionType();
    //send to segment
    segmentDatalayerPushData(
      {
        event: 'Product Clicked',
        //event_category: 'enhance ecommerce',
        //event_action: 'product click',
        //event_label: list,
        screen_name : pageName,
        store_type: getStoreName(),
        //plp_source_details: jioAdsSource ? jioAdsSource.replaceAll('[ccb]', Date.now()) : false,
        screen_source:
          sourcePage ||
          (typeof window !== 'undefined' && window.location?.pathname?.includes('/wishlist')
            ? 'wishlist'
            : false),
        //contains_store: catalogIdToStoreName(catalog),
        //isGA4: true,
        //item_list_name: list,
        impression_source : impression_source || list,
        impression_type : impression_type || 'Horizontal',
        products: [
          {
            // item_id: id,
            // item_name: name,
            // index: Number(position) || 0,
            // item_brand: brand,
            // item_list_name: list,
            // item_category: category,
            // price: Number(price) || 0,
            // discount: Number(productDiscount) || 0,
            // quantity: 1,
            // coupon: '',
            product_name: name, // pass value for product_name
            // product_id: id + "_" + isNaN(product_color) ? product_color : '', // pass value for product_id
            product_id: id || '', // pass value for product_id
            //product_size: product_size, // pass value for product_size
            //product_trade_discount, // pass value for product_trade_discount
            product_mrp: wasProductPrice,
            product_category: category, // pass value for product_category
            product_brick: product_brick, // pass value for product_brick
            // product_store_type: userRelevancyFlag
            // ? catalogIdToStoreName(catalog) + '|' + productListType
            // : catalogIdToStoreName(catalog), // pass value for Store_type
            //product_view_type: plpViewType, // pass value for pl view type: grid/list,
            //segment_id_experiment_id: dimension60Data,
            //sticker_name: productTagEngineValue || '', // product sticker name
            product_brand: brand, // pass value for product_brand
            product_list_price: price, // pass value for product_price
            //product_color: isNaN(product_color) ? product_color : '', // pass value for product_color
            product_tag: product_tag, // pass value for product_tag
            //Store_type: sourceStore,
            product_segment: dimension60Data,
            catalogue_id: catalog,
            ...(Number(collection_position) > 0 && {
              collection_position: Number(collection_position)
            }),
            catalog_id: catalog,
            // Taxonomy names / attributes that were absent from Product Clicked.
            // catalog_id above is kept for the existing consumers of this payload.
            product_offer_price: discountPrice ?? Number(price) ?? 0,
            product_oos_flag: Boolean(productOosFlag),
            product_position: Number(position) || 0,
            product_rating: String(averageRating ?? 0),
            product_vertical: productVertical || 'NA',
            product_wishlisted_count: Number(productWishlistedCount) || 0,
            similar_product_icon_available_flag: false,
            media_type: mediaType || 'Image',
            redirection_type: getSearchPlpRedirectionType(),
            collection_position: Number(collectionPosition ?? position) || 0,
            cross_sell_type: crossSellType || 'NA',
            itm_source: itmSource || 'NA',
            itm_source_detail: itmSourceDetail || itmSourceDetailValue || 'NA',
            ...(validToken && { attribution_token: validToken }),
            ...(offerName ? { offer_name: offerName } : {}),
            ...(skuId ? { sku_id: skuId } : {}),
            ...(adsData && Object.keys(adsData).length > 0
              ? {
                  ad_flag: true,
                  ad_source: _get(adsData, 'config.ad_source', '') || 'osmos'
                }
              : {
                  ad_flag: false,
                  ad_source: 'no ads'
                }),
            ...(adsData && Object.keys(adsData).length > 0 && {
              uclid: _get(adsData, 'config.uclid', ''),
              client_id: _get(adsData, 'config.cid', ''),
              scl_id: _get(adsData, 'config.sclid', ''),
              ad_id: _get(adsData, 'ad', ''),
              ad_cost: _get(adsData, 'ad_cost', ''),
              seller_id: sellerId || '',
              impression_type: Number(position) === 0 ? 'osmos_pdp_top' : 'osmos_pdp_widget'
            }),
            ...(searchExpansionType && { search_expansion_type: searchExpansionType })
            //...(userRelevancyFlag ? { product_store_type: getStoreName() + '|' + productListType } : {})
          }
        ],
        screen_type: pageType,
        screen_name: pageName,
        //contains_luxe: containsLuxe,
        //banner_theme_extended: showPlpExtendedBanner ? 'yes' : 'no',
        //video_name,
        //video_load_time,
        ...(validToken && {
          attribution_token: validToken
        })
      },
      'luxeGtmProductClick'
    );
  }

}

export function gtmGetMeta() {
  try {
    if (window) {
      let number = -1;
      window.dataLayer.forEach(function(elem, i) {
        if (elem.meta != undefined) {
          number = i;
        }
      });
      return number !== -1 && window.dataLayer[number].meta;
    }
    console.log('gtmGetProductsInCartFromMeta: window undefined');
  } catch (ex) {
    console.log(`gtmGetProductsInCartFromMeta - Exception: ${ex.message}`);
  }
}

export function gtmProceedToShipping(ProductArray, storeName, containsLuxe) {
  gtmDatalayerPushData(
    {
      event: 'ee_checkout',
      step: 1,
      store_type: getStoreName(),
      contains_luxe: containsLuxe,
      contains_store: getStoreType(ProductArray.map(el => el.dimension23)),
      ecommerce: {
        checkout: {
          actionField: { step: 1, option: 'shipping' },
          products: ProductArray
        }
      },
      pricing_cust_seg: readCookie('cohortSegment') || ''
    },
    'gtmProceedToShipping'
  );
}

export function gtmCartPage(
  ProductArray,
  storeName,
  containsLuxe,
  cart,
  isAssuredGifts,
  subscriptionDetails,
  pincode,
  isGSTSelected = false,
  membershipSavings ={},
  membershipInfo = {},
  bestCouponData = {},
  enableNonReturnableProductRevamp = false,
  enableMultiSellerSla = false
) {
  //console.log("cart", cart);
  const ga4AjioEnable = getGA4AJIOEnable(window?.__PRELOADED_STATE__?.flags);
  let validToken = getValidAttributionToken();
  if (ga4AjioEnable) {
    if (!Array.isArray(ProductArray)) ProductArray = [];
    gtmDatalayerPushData(
      {
        event: 'begin_checkout',
        contains_store: getStoreType(ProductArray.map(el => el.dimension23)),
        step: 1,
        store_type: storeName,
        contains_luxe: containsLuxe,
        isGA4: true,
        event_category: 'Enhance E-commerce',
        event_action: 'begin_checkout',
        ...(subscriptionDetails && {
          subscription_details: subscriptionDetails
        }),
        ...(pincode && { pincode }),
        ecommerce: {
          currency: 'INR',
          value: ProductArray.reduce((acc, prod) => acc + (Number(prod.price) || 0), 0),
          items: ProductArray.map(product => ({
            item_id: product.id,
            item_name: product.name,
            discount: Number(product.discount) / (Number(product.quantity) || 1) || 0,
            index: Number(product.position) || 0,
            item_brand: product.brand,
            item_list_name: 'bag screen',
            item_category: product.category,
            item_variant: product.variant,
            price: Number(product.price) / (Number(product.quantity) || 1) || 0,
            quantity: Number(product.quantity) || 1,
            coupon: product.coupon,
            available_quantity_deliverable_non_deliverable: product.dimension59,
            segment_id_experiment_id: product.dimension60,
            delivery_tag: product.dimension61,
            product_level_edd: product.dimension66,
            product_store_type: product.dimension23,
            offer_name: containsLuxe ? product.dimension82 : product.dimension166,
            product_mrp: product?.product_mrp,
            ...(product.pincode && { pincode: product.pincode }),
            ...(product.priorityDeliveryFee && {
              product_priority_delivery_fee: Number(product.priorityDeliveryFee).toFixed(1)
            }),
            ...(product.sticker_name && { sticker_name: product.sticker_name }),
            ...(product.priorityDeliveryEDD && {
              priority_delivery_EDD: product.priorityDeliveryEDD
            }),
            ...(product.priority_delivery_sla_tag && {
              priority_delivery_sla_tag: product.priority_delivery_sla_tag
            }),
            ...(product.rushDeliveryFee && {
              product_rush_delivery_fee: Number(product.rushDeliveryFee).toFixed(1)
            }),
            ...(product.rushDeliveryEDD && {
              rush_delivery_EDD: product.rushDeliveryEDD
            }),
            ...(product.rush_delivery_sla_tag && {
              rush_delivery_sla_tag: product.rush_delivery_sla_tag
            }),
            ...(product?.ajioSuperCashCartEnable && (product.superCashData ?? {})),
            ...(product.search_attribution && {
              search_attribution: product.search_attribution
            }),
            ...(product.subscription_delivery_fee_saving && {
              subscription_delivery_fee_saving: product.subscription_delivery_fee_saving
            }),
            ...(product.subscription_platform_fee_saving && {
              subscription_platform_fee_saving: product.subscription_platform_fee_saving
            }),
            ...(product.subscription_total_savings && {
              subscription_total_savings: product.subscription_total_savings
            }),
            ...(product.subscription_membership_discount && {
              subscription_membership_discount: product.subscription_membership_discount
            }),
            ...(product.itm_source && {
              itm_source: product.itm_source
            }),
            ...(product.itm_source_detail && {
              itm_source_detail: product.itm_source_detail
            })
          }))
        },
        pricing_cust_seg: readCookie('cohortSegment') || ''
      },
      'cart'
    );
  } else {
    ProductArray.forEach(x => {
      delete x.discount;
      delete x.coupon;
      delete x.priorityDeliveryFee;
      delete x.priorityDeliveryEDD;
    });
    gtmDatalayerPushData(
      {
        event: 'ee_checkout',
        contains_store: getStoreType(ProductArray.map(el => el.dimension23)),
        step: 1,
        store_type: storeName,
        contains_luxe: containsLuxe,
        ecommerce: {
          checkout: {
            actionField: { step: 1, option: 'checkout' },
            products: ProductArray
          }
        },
        pricing_cust_seg: readCookie('cohortSegment') || ''
      },
      'cart'
    );
  }

  if(segmentTrackingEnabled == 'ON'){
    if (!Array.isArray(ProductArray)) ProductArray = [];
    // Send segment
    const enableGST = getGSTFlag(window?.__PRELOADED_STATE__);
    segmentDatalayerPushData(
      {
        event: 'Bag Details Viewed',
        store_type: getStoreType(ProductArray.map(el => el.dimension23)),
        bag_assured_gift_flag : isAssuredGifts?.cartAssuredGifts === 'ON',
        // Declared boolean — callers pass the live price-change callout state.
        bag_price_update_callout : Boolean(cart?.bagPriceUpdateCallout),
        // Order-summary fees are part of the Bag Details Viewed taxonomy; the
        // callers now pass orderSummary through on the `cart` argument.
        delivery_fee: Number(cart?.orderSummary?.deliveryCharge?.value || 0),
        platform_fee: Number(cart?.orderSummary?.convenienceFee?.total?.netAmount?.value || 0),
        is_bag_merged: cart?.restoredProducts ? true : false,
        cart_id: cart?.id,
        ...(bestCouponData?.enabled && { coupon_applicability_flag: 'bag' }),
        ...(bestCouponData?.enabled && { gap_to_unlock: bestCouponData.gap_to_unlock ?? 0 }),
        ...(pincode && { pincode }),
        ...(enableGST && { is_gst_selected: isGSTSelected }),
        products: ProductArray.map(product => ({
          ...getProductItemForCart(product),
          ...(product.itm_source && { itm_source: product?.itm_source || '' }),
          ...(product.itm_source_detail && { itm_source_detail: product?.itm_source_detail || '' }),
          ...(product.product_cross_sell_type && { product_cross_sell_type: product.product_cross_sell_type }),
          ...(enableNonReturnableProductRevamp && {
            non_returnable_reason: getNonReturnableReason(product),
            non_exchangable_reason: getNonExchangableReason(product, isGSTSelected)
          }),
          ...(enableMultiSellerSla ? getCartItemSellerOptionProps(product.prodData) : {}),
        })),
        ...membershipSavings,
        ...membershipInfo,
        subscription_first_purcase_date: 'NA',
        subscripiton_number: 'NA'
        //pricing_cust_seg: readCookie('cohortSegment') || ''
      },
      'cart'
    );
  }
}

export function gtmMassProceedToShipping(
  ProductArray,
  storeName,
  containsLuxe,
  cart,
  isAssuredGifts,
  ajioWalletData,
  cartItem,
  subscriptionDetails,
  pincode,
  isGSTSelected = false,
  membershipSavings = {},
  membershipInfo = {},
  enableNonReturnableProductRevamp = false,
  internalWallets = [],
  enableMultiSellerSla = false
) {
  //console.log("gtmMassProceedToShipping cart", cart);
  //console.log("gtmMassProceedToShipping ProductArray", ProductArray);
  //console.log("gtmMassProceedToShipping ajioWalletData", ajioWalletData);

  const ga4AjioEnable = getGA4AJIOEnable(window?.__PRELOADED_STATE__?.flags);
  if (ga4AjioEnable) {
    const isStoreLuxe = storeName === 'luxe';
    if (!Array.isArray(ProductArray)) ProductArray = [];
    gtmDatalayerPushData(
      {
        event: 'add_shipping_info',
        step: 2,
        store_type: storeName,
        contains_store: getStoreType(ProductArray.map(el => el.dimension23)),
        contains_luxe: containsLuxe,
        isGA4: true,
        ...(subscriptionDetails && {
          subscription_details: subscriptionDetails
        }),
        ecommerce: {
          currency: 'INR',
          value: ProductArray.reduce((acc, prod) => acc + (Number(prod.price) || 0), 0),
          shipping_tier: '',
          items: ProductArray.map(product => ({
            item_id: product.id,
            item_name: product.name,
            discount: Number(product.discount) / (Number(product.quantity) || 1) || 0,
            index: Number(product.position) || 0,
            item_brand: product.brand,
            item_list_name: 'bag screen',
            item_category: product.category,
            item_variant: product.variant,
            price: Number(product.price) / (Number(product.quantity) || 1) || 0,
            quantity: Number(product.quantity) || 1,
            coupon: product.coupon,
            product_id: isStoreLuxe ? product.dimension7 : product.dimension12,
            product_name: isStoreLuxe ? product.dimension6 : product.dimension13,
            product_category: isStoreLuxe ? product.dimension13 : product.dimension16,
            product_store_type: product.dimension23,
            product_discount: product.dimension170,
            available_quantity_deliverable_non_deliverable: product.dimension59,
            segment_id_experiment_id: product.dimension60,
            delivery_tag: product.dimension61,
            product_level_edd: product.dimension66,
            offer_name: product.dimension166,
            ...(product.sticker_name && { sticker_name: product.sticker_name }),
            ...(isStoreLuxe && {
              Store_type: product.dimension16,
              product_tag: product.dimension15,
              product_color: product.dimension10,
              product_trade_discount: product.dimension12,
              product_price: product.dimension9,
              product_brand: product.dimension8,
              offer_name: product.dimension82
            }),
            ...(product.priorityDeliveryFee && {
              product_priority_delivery_fee: Number(product.priorityDeliveryFee).toFixed(1)
            }),
            ...(product.priorityDeliveryEDD && {
              priority_delivery_EDD: product.priorityDeliveryEDD
            }),
            ...(product.priority_delivery_sla_tag && {
              priority_delivery_sla_tag: product.priority_delivery_sla_tag
            }),
            ...(product.subscription_delivery_fee_saving && {
              subscription_delivery_fee_saving: product.subscription_delivery_fee_saving
            }),
            ...(product.subscription_platform_fee_saving && {
              subscription_platform_fee_saving: product.subscription_platform_fee_saving
            }),
            ...(product.subscription_total_savings && {
              subscription_total_savings: product.subscription_total_savings
            }),
            ...(product.subscription_membership_discount && {
              subscription_membership_discount: product.subscription_membership_discount
            })
          }))
        },
        pricing_cust_seg: readCookie('cohortSegment') || ''
      },
      'gtmProceedToShipping'
    );
  } else {
    ProductArray.forEach(x => {
      delete x.discount;
      delete x.coupon;
      delete x.priorityDeliveryFee;
      delete x.priorityDeliveryEDD;
    });
    gtmDatalayerPushData(
      {
        event: 'ee_checkout',
        step: 2,
        store_type: storeName,
        contains_store: getStoreType(ProductArray.map(el => el.dimension23)),
        contains_luxe: containsLuxe,
        ecommerce: {
          checkout_option: {
            actionField: { step: 2, option: 'checkout_option' },
            products: ProductArray
          }
        },
        pricing_cust_seg: readCookie('cohortSegment') || ''
      },
      'gtmProceedToShipping'
    );
  }

  if (segmentTrackingEnabled == 'ON') {
    if (!Array.isArray(ProductArray)) ProductArray = [];
    const enableGST = getGSTFlag(window?.__PRELOADED_STATE__);

    const gstSelected = Boolean(cart?.gstDetails?.gstNumber);
    const gstAddressData =
      enableGST && gstSelected
        ? getGstAddressSegmentPropsFromCart(cart)
        : {};

    segmentDatalayerPushData(
      {
        event: 'Proceed to Payment Clicked',
        //step: 2,
        store_name: storeName,
        ...(pincode && { pincode }),
        ...membershipSavings,
        ...membershipInfo,
        subscription_first_purcase_date: 'NA',
        subscripiton_number: 'NA',
        store_type: getStoreType(ProductArray.map(el => el.dimension23)),
        ...getCartItemProperty(cart, isAssuredGifts, internalWallets),
        screen_name: 'shipping screen',
        ...(enableGST && {
          is_gst_selected: Boolean(gstSelected || isGSTSelected)
        }),
        ...gstAddressData,
        products: ProductArray?.map(product => ({
          ...getProductItemForCart(product),
          is_gst_selected: Boolean(gstSelected || isGSTSelected),
          ...(product.itm_source && { itm_source: product.itm_source }),
          ...(product.itm_source_detail && { itm_source_detail: product.itm_source_detail }),
          ...(product.product_cross_sell_type && { product_cross_sell_type: product.product_cross_sell_type }),
          ...(enableNonReturnableProductRevamp && {
            non_returnable_reason: getNonReturnableReason(product),
            non_exchangable_reason: getNonExchangableReason(product, isGSTSelected)
          }),
          ...(enableMultiSellerSla ? getCartItemDeliveryProps(product.prodData) : {}),
        }))
        //pricing_cust_seg: readCookie('cohortSegment') || ''
      },
      'gtmProceedToShipping'
    );
  }
}

export function gtmPaymentClick(
  ProductArray,
  storeName,
  containsLuxe,
  cart,
  isAssuredGifts,
  ajioWalletData,
  cartItem,
  pincode,
  isGSTSelected = false,
  membershipSavings = {},
  membershipInfo = {},
  internalWallets = []
) {
  const ga4AjioEnable = getGA4AJIOEnable(window?.__PRELOADED_STATE__?.flags);
  if (!ga4AjioEnable) {
    gtmDatalayerPushData(
      {
        event: 'ee_checkout',
        step: 3,
        store_type: storeName,
        contains_store: getStoreType(ProductArray.map(el => el.dimension23)),
        contains_luxe: containsLuxe,
        ecommerce: {
          checkout_option: {
            actionField: { step: 3, option: 'checkout_option' },
            products: ProductArray
          }
        },
        pricing_cust_seg: readCookie('cohortSegment') || ''
      },
      'gtmProceedToPayment'
    );
  }

  if (segmentTrackingEnabled == 'ON') {
    if (!Array.isArray(ProductArray)) ProductArray = [];
    const enableGST = getGSTFlag(window?.__PRELOADED_STATE__);

    const gstSelected = Boolean(cart?.gstDetails?.gstNumber);
    const gstAddressData =
      enableGST && gstSelected
        ? getGstAddressSegmentPropsFromCart(cart)
        : {};

    segmentDatalayerPushData(
      {
        event: 'Payment Page Product Details',
        store_type: getStoreType(ProductArray.map(el => el.dimension23)),
        ...getCartItemProperty(cart, isAssuredGifts, internalWallets),
        ...(pincode && { delivery_pincode: pincode }),
        ...(enableGST && {
          is_gst_selected: Boolean(gstSelected || isGSTSelected)
        }),
        ...gstAddressData,
        ...membershipSavings,
        ...membershipInfo,
        subscription_first_purcase_date: 'NA',
        subscripiton_number: 'NA',
        previous_screen_name: 'Bag Screen',
        screen_name: 'single page checkout',
        products: ProductArray?.map(product => ({
          ...getProductItemForCart(product)
        }))
      },
      'gtmProceedToPayment'
    );
  }
}

export function gtmProceedToPayment(ProductArray) {
  gtmDatalayerPushData(
    {
      event: 'ee_checkout',
      store_type: getStoreName(),
      contains_store: getStoreType(ProductArray.map(el => el.dimension23)),
      step: 2,
      ecommerce: {
        checkout: {
          actionField: { step: 2, option: 'payment' },
          products: ProductArray
        }
      }
    },
    'gtmProceedToPayment'
  );
}

export function getThePLPType() {
  try {
    if (window) {
      return window.location.href.includes('/search')
        ? 'search listing page'
        : window.location.href.includes('/c')
        ? 'category listing page'
        : window.location.href.includes('/b')
        ? 'brand listing page'
        : 'product listing page';
    }
    console.log('getThePLPType: window undefined');
  } catch (error) {
    console.log(`getThePLPType - Exception: ${ex.message}`);
  }
}

export function gtmAddToCart(
  id,
  name,
  price,
  brand,
  cat,
  subCat = '',
  variant,
  qty,
  discount,
  size,
  storeName = '',
  eventLabel = '',
  productData = '',
  productTagEngineValue = '',
  productPriceLabel = '',
  averageRating = 0,
  numUserRatings = '',
  catalogName = '',
  segmentId = '',
  experimentId = '',
  screenName = '',
  originalPrice,
  discountPrice,
  priorityDeliveryEDD,
  priorityDeliveryFee,
  vertexUniqueKey = '',
  copilotSource,
  pdRushData={},
  membershipMasterFlag,
  subscription_delivery_fee_saving,
  subscription_platform_fee_saving,
  subscription_total_savings,
  subscription_membership_discount,
  subscription_status,
  subscription_version,
  subscription_id,
  subscription_start_date,
  subscription_end_date,
  subscribed_orders_left,
  total_account_savings,
  platform_account_savings,
  delivery_account_savings,
  membership_discount_account_savings,
  subscription_first_purcase_date,
  subscripiton_number,
  is_subscription,
  brickCode = '',
  returnWindow = 0,
  isNonReturnable = false,
  enableNonReturnableProductRevamp = false,
  crossSellSource = false,
  slaAddToBagData = {}
) {

  const token = getValidAttributionToken();
  try {
    if (token && vertexUniqueKey) {
      const existingMap = JSON.parse(localStorage.getItem('cartAttributionMap') || '{}');
      existingMap[vertexUniqueKey] = token;
      localStorage.setItem('cartAttributionMap', JSON.stringify(existingMap));
    } else {
      localStorage.removeItem('cartAttributionMap');
    }
  } catch {
    localStorage.removeItem('cartAttributionMap');
  }

  const plaItem = getPlaItemByCode(brickCode);

  trackCopilotMap(vertexUniqueKey,copilotSource);

  storage.addItem(ENUM.STORETYPES, catalogIdToStoreName(catalogName));
  const dimension60Data =
    Number(segmentId) || Number(experimentId) ? `${segmentId}|${experimentId}` : '';
  const ga4AjioEnable = getGA4AJIOEnable(window?.__PRELOADED_STATE__?.flags);
  if (ga4AjioEnable) {
    const productDiscount =
      (Number(originalPrice) * getDiscountValueWithoutPercentage(discount)) / 100 || 0;
    const { pageName } = getPageInfo(
      window.location.pathname || '',
      false,
      storeName.toLowerCase() === FLEEK_STORE.toLowerCase()
    );
    gtmDatalayerPushData(
      {
        event: 'add_to_cart',
        event_label: eventLabel,
        event_category: 'enhance ecommerce',
        event_action: 'add to cart',
        contains_store: catalogIdToStoreName(catalogName),
        store_type: getStoreName(),
        isGA4: true,
        ecommerce: {
          currency: 'INR',
          value: Number(discountPrice) || 0,
          items: [
            {
              item_id: id,
              item_name: name,
              discount: Number(productDiscount) || 0,
              index: 0,
              item_brand: brand,
              item_list_name: screenName || pageName,
              item_category: cat,
              item_variant: variant,
              price: Number(discountPrice) || 0,
              coupon: '',
              quantity: Number(qty) || 1,
              product_id: id,
              product_name: name,
              product_size: getSkuAutoSelectSizeData(size),
              product_category: cat,
              product_store_type: catalogIdToStoreName(catalogName),
              segment_id_experiment_id: dimension60Data,
              jio_ads_option_code: productData,
              sticker_name: productTagEngineValue,
              offer_name: productPriceLabel,
              product_discount: getDiscountValue(discount),
              ...(token && {
                search_attribution: token
              }),
              ...(averageRating && {
                ratings_shown_no_of_users: `${averageRating}|${numUserRatings}`
              }),
              ...(priorityDeliveryFee && {
                product_priority_delivery_fee: Number(priorityDeliveryFee).toFixed(1)
              }),
              ...(priorityDeliveryEDD && { priority_delivery_EDD: priorityDeliveryEDD }),
              ...(copilotSource && { itm_source_detail : 'ai_assistant_widget'})
            }
          ]
        },
        pricing_cust_seg: getCustSegmentIds()
      },
      'ee_add_to_cart'
    );
   
  } else {
    gtmDatalayerPushData(
      {
        event: 'ee_add_to_cart',
        event_label: eventLabel,
        event_category: 'enhance ecommerce',
        event_action: 'add to cart',
        contains_store: catalogIdToStoreName(catalogName),
        store_type: getStoreName(),
        ecommerce: {
          currency: 'INR',
          add: {
            products: [
              {
                name,
                id,
                price,
                brand,
                category: cat,
                variant,
                quantity: qty,
                storeName,
                dimension12: id,
                dimension13: name,
                dimension14: '',
                dimension15: cat,
                dimension16: cat,
                dimension20: '',
                dimension21: brand,
                dimension22: price,
                dimension23: catalogIdToStoreName(catalogName),
                dimension24: size,
                dimension60: dimension60Data,
                dimension163: productData,
                dimension165: productTagEngineValue,
                dimension166: productPriceLabel,
                dimension170: getDiscountValue(discount),
                ...(averageRating && { dimension46: `${averageRating}|${numUserRatings}` })
              }
            ]
          }
        },
        pricing_cust_seg: getCustSegmentIds()
      },
      'ee_add_to_cart'
    );
  }

  if(segmentTrackingEnabled == 'ON'){
    const productDiscount =
    (Number(originalPrice) * getDiscountValueWithoutPercentage(discount)) / 100 || 0;
  const { pageName } = getPageInfo(
    window.location.pathname || '',
    false,
    storeName.toLowerCase() === FLEEK_STORE.toLowerCase()
  );
  let {pincode , ...restPdRushData} = pdRushData;
    const osmosConfigClientId = getPlaAdsClientID(window?.__PRELOADED_STATE__);
    segmentDatalayerPushData(
      {
        event: 'Add To Bag Clicked',
        event_label: eventLabel,
        event_category: 'enhance ecommerce',
        event_action: 'add to cart',
        //contains_store: catalogIdToStoreName(catalogName),
        //store_type: getStoreName(),
        //isGA4: true,
        add_to_bag_source : screenName || pageName,
        screen_name : screenName,
        screen_source: eventLabel,
        pincode: pincode && pincode,
        // Event-level in the taxonomy — it was only being reported inside
        // products[], where it reads as products.size_added_flag.
        size_added_flag: sessionStorage.getItem('defaultSKUSizeSelection') ? true : false,
        ...(slaAddToBagData?.enableMultiSellerSla && {
          previous_screen_name: slaAddToBagData.previousScreenName || 'pdp screen'
        }),
        //currency: 'INR',
        //value: Number(discountPrice) || 0,
        ...(membershipMasterFlag && {
          subscription_delivery_fee_saving,
          subscription_platform_fee_saving,
          subscription_total_savings,
          subscription_membership_discount,
          subscription_status,
          subscription_version,
          subscription_id,
          subscripiton_number,
          subscription_start_date,
          subscription_end_date,
          subscribed_orders_left,
          total_account_savings,
          platform_account_savings,
          delivery_account_savings,
          membership_discount_account_savings,
          subscription_first_purcase_date,
        }),
        products: [
          {
            // Taxonomy attributes that were absent from this payload. Declared
            // first so the caller-supplied values below (including the
            // `restPdRushData` spread, which the bag-screen callers use to pass
            // the real cart-entry values) always win, and defaulted so each one is
            // reported rather than dropped.
            catalogue_id: catalogName || 'NA',
            product_segment: cat || 'NA',
            product_vertical: subCat || 'NA',
            product_servicability: pdRushData?.product_servicability ?? false,
            standard_delivery_edd: 'NA',
            priority_delivery_edd: priorityDeliveryEDD || 'NA',
            priority_delivery_fees: Number(priorityDeliveryFee) || 0,
            rush_delivery_edd: 'NA',
            rush_delivery_fees: 0,
            delivery_type: pdRushData?.delivery_type || 'NA',
            itm_source: 'NA',
            itm_source_detail: 'NA',
            // Overridden by the vertex / PLA sku below when one is available.
            sku_id: id || '',
            // product_id: id,
            // product_name: name,
            // discount: Number(productDiscount) || 0,
            product_position: 0,
            product_brand: brand,
            // item_list_name: screenName || pageName,
            // item_category: cat,
            // item_variant: variant,
            product_mrp : originalPrice,
            product_list_price: Number(price) || 0,   
            product_offer_price : discountPrice,         
            // coupon: '',
            // quantity: Number(qty) || 1,
            product_id: id + "_" + variant,
            product_name: name,
            //product_size: getSkuAutoSelectSizeData(size),
            product_brick: cat,
            size_of_product: getSkuAutoSelectSizeData(size),
            size_added_flag: sessionStorage.getItem('defaultSKUSizeSelection') ? true : false,
            product_rating: averageRating,
            ...(copilotSource && { itm_source_detail : 'ai_assistant_widget'}),
            ...(crossSellSource && {
              // Falls back to NA, not '': an empty string overrode the default above
              // and read as an unfilled property whenever the URL carried no detail.
              itm_source_detail:
                new URLSearchParams(window.location.search).get('itm_source_detail') || 'NA',
              itm_source: 'widget'
            }),
            product_tag: productTagEngineValue,
            ...(pdRushData?.standard_delivery_edd && { standard_delivery_edd: pdRushData.standard_delivery_edd }),
            ...((pdRushData?.priority_delivery_edd || priorityDeliveryEDD) && {
              priority_delivery_edd: pdRushData?.priority_delivery_edd || priorityDeliveryEDD
            }),
            ...(pdRushData?.rush_delivery_edd && { rush_delivery_edd: pdRushData.rush_delivery_edd }),
            ...restPdRushData,
            //product_store_type: catalogIdToStoreName(catalogName),
            //segment_id_experiment_id: dimension60Data,
            //jio_ads_option_code: productData,
            //sticker_name: productTagEngineValue,
            //offer_name: productPriceLabel,
            // product_discount: getDiscountValue(discount),
            // ...(averageRating && {
            //   ratings_shown_no_of_users: `${averageRating}|${numUserRatings}`
            // }),
            // ...(priorityDeliveryFee && {
            //   product_priority_delivery_fee: Number(priorityDeliveryFee).toFixed(1)
            // }),
            // ...(priorityDeliveryEDD && { priority_delivery_EDD: priorityDeliveryEDD }),
            ...(membershipMasterFlag && {
              is_subscription
            }),
            product_return_window: returnWindow ?? 0,
            product_exchange_window: returnWindow ?? 0,
            ...(enableNonReturnableProductRevamp && {
              atb_non_returnable_visible: isNonReturnable ? 'yes' : 'no'
            }),
            ...((vertexUniqueKey || plaItem?.code) && {
              sku_id: vertexUniqueKey || plaItem?.productCode
            }),
            ...(plaItem?.code && {
              seller_id: plaItem?.seller_id,
              client_id: plaItem?.client_id || osmosConfigClientId || ''}),
            ...(slaAddToBagData?.enableMultiSellerSla
              ? getSelectedSellerOptionProps(
                  slaAddToBagData.slaOptions,
                  slaAddToBagData.selectedPriceType,
                  slaAddToBagData.selectedSlaOption
                )
              : {})
          }
        ],
        size_added_flag: sessionStorage.getItem('defaultSKUSizeSelection') ? true : false,
        ...(token && {
          attribution_token: token
        })
        //pricing_cust_seg: getCustSegmentIds()
      },
      'ee_add_to_cart'
    );
  }
}

export function gtmProductReturn(
  id,
  name,
  price,
  brand,
  cat,
  variant,
  qty,
  discount,
  size,
  storeName = ''
) {
  gtmDatalayerPushData(
    {
      event: 'purchase_refund',
      store_type: getStoreName(),
      contains_store: getStoreName(),
      action: 'Return',
      label: 'returnordersuccessful',
      ecommerce: {
        currency: 'INR',
        add: {
          products: [
            {
              name,
              id,
              price,
              brand,
              category: cat,
              variant,
              quantity: qty,
              storeName,
              dimension12: id,
              dimension13: name,
              dimension14: '',
              dimension15: cat,
              dimension16: cat,
              dimension20: '',
              dimension21: brand,
              dimension22: price,
              dimension23: getStoreName(),
              dimension24: size,
              dimension60: getCustSegmentIds(),
              dimension170: getDiscountValue(discount)
            }
          ]
        }
      },
      pricing_cust_seg: getCustSegmentIds()
    },
    'purchase_refund'
  );
}
export function luxeGtmAddToCart(
  name,
  id,
  price,
  brand,
  category,
  product_size,
  product_color,
  product_trade_discount,
  product_brick,
  product_tag,
  qty,
  list,
  store_type,
  pageType,
  pageName,
  eventLabel,
  sourceStore,
  containsLuxe,
  catalog,
  segmentId,
  experimentId,
  originalPrice,
  variant,
  productPriceLabel = '',
  priorityDeliveryEDD,
  priorityDeliveryFee,
  vertexUniqueKey,
  copilotSource,
  productData,
  membershipMasterFlag,
  subscription_delivery_fee_saving,
  subscription_platform_fee_saving,
  subscription_total_savings,
  subscription_membership_discount,
  subscription_status,
  subscription_version,
  subscription_id,
  subscription_start_date,
  subscription_end_date,
  subscribed_orders_left,
  total_account_savings,
  platform_account_savings,
  delivery_account_savings,
  membership_discount_account_savings,
  subscription_first_purcase_date,
  subscripiton_number,
  is_subscription,
  brickCode='',
  adsData = {},
  returnWindow = 0,
  isNonReturnable = false,
  enableNonReturnableProductRevamp = false,
  crossSellSource = false,
  slaAddToBagData = {}
) {

  const plaItem = getPlaItemByCode(brickCode);

  trackCopilotMap(vertexUniqueKey,copilotSource);  
   const token = getValidAttributionToken();
  const dimension60Data =
    Number(segmentId) || Number(experimentId) ? `${segmentId}|${experimentId}` : '';
  const ga4AjioEnable = getGA4AJIOEnable(window?.__PRELOADED_STATE__?.flags);
  if (ga4AjioEnable) {
    const productDiscount =
      (Number(displayValueWithoutSymbol(originalPrice)) *
        getDiscountValueWithoutPercentage(product_trade_discount)) /
        100 || 0;
    gtmDatalayerPushData(
      {
        event: 'add_to_cart',
        store_type: getStoreName(),
        contains_store: getStoreName(),
        event_category: 'enhance ecommerce',
        event_action: 'add to cart',
        event_label: eventLabel,
        isGA4: true,
        ecommerce: {
          currency: 'INR',
          value: Number(price) || 0,
          items: [
            {
              item_id: id,
              item_name: name,
              discount: Number(productDiscount),
              index: 0,
              item_brand: brand,
              item_list_name: list,
              item_category: category,
              item_variant: variant,
              price: Number(price) || 0,
              coupon: '',
              quantity: Number(qty) || 1,
              product_name: name, // pass value for product_name
              product_id: id, // pass value for product_id
              product_size: getSkuAutoSelectSizeData(product_size), // pass value for product_size
              product_trade_discount, // pass value for product_trade_discount
              product_category: category, // pass value for product_category
              product_brick: product_brick, // pass value for product_brick
              product_store_type: sourceStore, // pass value for Store_type
              segment_id_experiment_id: dimension60Data,
              product_brand: brand, // pass value for product_brand
              product_price: price, // pass value for product_price
              product_color: isNaN(product_color) ? product_color : '', // pass value for product_color
              product_tag, // pass value for product_tag
              ...(productPriceLabel ? { offer_name: productPriceLabel } : {}),
              Store_type: sourceStore, // pass value for Store_type
              ...(priorityDeliveryFee && {
                product_priority_delivery_fee: Number(priorityDeliveryFee).toFixed(1)
              }),
              ...(priorityDeliveryEDD && { priority_delivery_EDD: priorityDeliveryEDD }),
              ...(copilotSource && { itm_source_detail : 'ai_assistant_widget'}),
              ...(crossSellSource && { product_cross_sell_type: 'cross_sell', itm_source_detail: 'cross_sell_widget' })
            }
          ]
        },
        page_type: pageType,
        page_name: pageName,
        contains_luxe: containsLuxe
      },
      'luxeGtmAddToCart'
    );

  } else {
    gtmDatalayerPushData(
      {
        event: 'ee_add_to_cart',
        store_type: getStoreName(),
        contains_store: getStoreName(),
        event_category: 'enhance ecommerce',
        event_action: 'add to cart',
        event_label: eventLabel,
        ecommerce: {
          add: {
            actionField: { list },
            products: [
              {
                name,
                id,
                price,
                brand,
                category,
                position: 1,
                quantity: qty,
                dimension6: name, // pass value for product_name
                dimension7: id, // pass value for product_id
                dimension8: brand, // pass value for product_brand
                dimension9: price, // pass value for product_price
                dimension10: isNaN(product_color) ? product_color : '', // pass value for product_color
                dimension11: product_size, // pass value for product_size
                dimension12: product_trade_discount, // pass value for product_trade_discount
                dimension13: category, // pass value for product_category
                dimension14: product_brick, // pass value for product_brick
                dimension15: product_tag, // pass value for product_tag
                dimension16: sourceStore, // pass value for Store_type
                dimension23: catalogIdToStoreName(catalog),
                dimension60: dimension60Data,
                ...(productPriceLabel ? { dimension82: productPriceLabel } : {})
              }
            ]
          }
        },
        page_type: pageType,
        page_name: pageName,
        contains_luxe: containsLuxe
      },
      'luxeGtmAddToCart'
    );
  }

  if(segmentTrackingEnabled == 'ON'){
    const productDiscount =
    (Number(displayValueWithoutSymbol(originalPrice)) *
      getDiscountValueWithoutPercentage(product_trade_discount)) /
      100 || 0;
    const osmosConfigClientId = getPlaAdsClientID(window?.__PRELOADED_STATE__) || '';
    // sent to segment
    segmentDatalayerPushData(
      {
        event: 'Add To Bag Clicked',
        //store_type: getStoreName(),
        //contains_store: getStoreName(),
        event_category: 'enhance ecommerce',
        event_action: 'add to cart',
        source_screen: eventLabel,
        ...(slaAddToBagData?.enableMultiSellerSla && {
          previous_screen_name: slaAddToBagData.previousScreenName || 'pdp screen'
        }),
        ...(crossSellSource && {
          itm_source: 'widget',
          itm_source_detail: new URLSearchParams(window.location.search).get('itm_source_detail') || ''
        }),
        //event_label: eventLabel,
        //isGA4: true,
        //currency: 'INR',
        //value: Number(price) || 0,
        ...(membershipMasterFlag && {
          subscription_delivery_fee_saving: subscription_delivery_fee_saving || "NA",
          subscription_platform_fee_saving: subscription_platform_fee_saving || "NA",
          subscription_total_savings: subscription_total_savings || "NA",
          subscription_membership_discount: subscription_membership_discount || "NA",
          subscription_status,
          subscription_version,
          subscription_id,
          subscripiton_number,
          subscription_start_date,
          subscription_end_date,
          subscribed_orders_left,
          total_account_savings,
          platform_account_savings,
          delivery_account_savings,
          membership_discount_account_savings,
          subscription_first_purcase_date,
        }),
        products: [
          {
            //product_id: id,
            //product_name: name,
          // discount: Number(productDiscount),
            product_position: 0,
            product_brand: brand,
          // item_list_name: list,
          // item_category: category,
          // item_variant: variant,
            product_mrp : originalPrice,
            product_list_price: Number(price) || 0,  
            //coupon: '',
            //quantity: Number(qty) || 1,
            product_name: name, // pass value for product_name
            product_id: id + "_" + variant, // pass value for product_id
            size_of_product: getSkuAutoSelectSizeData(product_size), // pass value for product_size
            size_added_flag: sessionStorage.getItem('defaultSKUSizeSelection') ? true : false,
            //product_trade_discount, // pass value for product_trade_discount
            //product_category: category, // pass value for product_category
            product_brick: product_brick, // pass value for product_brick
            //product_store_type: sourceStore, // pass value for Store_type
            product_segment: dimension60Data || "",
            product_vertical: category,
            catalogue_id: catalog || '',
            product_servicability: true,
            //product_brand: brand, // pass value for product_brand
            //product_price: price, // pass value for product_price
            //product_color: isNaN(product_color) ? product_color : '', // pass value for product_color
            product_tag: product_tag, // pass value for product_tag
            //...(productPriceLabel ? { offer_name: productPriceLabel } : {}),
            //Store_type: sourceStore, // pass value for Store_type

            ...(priorityDeliveryEDD && { standard_delivery_edd: priorityDeliveryEDD }),
            ...(priorityDeliveryEDD && { priority_delivery_edd: priorityDeliveryEDD }),
            delivery_type: '',
            ...(membershipMasterFlag && {
              is_subscription
            }),

            ...(Object.keys(plaItem || {}).length > 0 && {
              client_id: plaItem?.client_id || osmosConfigClientId || ''
            }),
            ...(Object.keys(plaItem || {}).length > 0 && {
              seller_id: plaItem?.seller_id || ''
            }),
            ...((vertexUniqueKey || plaItem?.productCode) && {
              sku_id: vertexUniqueKey || plaItem?.productCode
            }),
            product_return_window: returnWindow ?? 0,
            product_exchange_window: returnWindow ?? 0,
            ...(enableNonReturnableProductRevamp && {
              atb_non_returnable_visible: isNonReturnable ? 'yes' : 'no'
            }),
            ...(slaAddToBagData?.enableMultiSellerSla
              ? getSelectedSellerOptionProps(
                  slaAddToBagData.slaOptions,
                  slaAddToBagData.selectedPriceType,
                  slaAddToBagData.selectedSlaOption
                )
              : {})
          }
        ],
        size_added_flag: sessionStorage.getItem('defaultSKUSizeSelection') ? true : false,
         ...(token && {
          attribution_token: token
        }),
        screen_type: pageType,
        screen_name: pageName
        //contains_luxe: containsLuxe
      },
      'luxeGtmAddToCart'
    );
  }
}

export function gtmRemoveFromCart(id, name, price, brand, cat, variant, qty) {
  gtmDatalayerPushData(
    {
      event: 'ee-removeFromCart',
      store_type: getStoreName(),
      contains_store: getStoreName(),
      ecommerce: {
        currency: 'INR',
        remove: {
          products: [
            {
              name,
              id,
              price,
              brand,
              category: cat,
              variant,
              quantity: qty
            }
          ]
        }
      }
    },
    'gtmRemoveFromCart'
  );
}

export function gtmProductDetailView(
  name,
  id,
  list,
  price,
  brand,
  cat,
  cat2,
  variant,
  tradeDiscount,
  size,
  productTag,
  priceLabel,
  averageRating,
  ratingCount,
  productCatalog,
  segmentId,
  experimentId,
  dimension59Data,
  discountPrice,
  earnRuleId,
  superCashEarning,
  validToken,
  itmSourceDetailValue = '',
  product_details_text,
  visual_format,
  product_offer_price
) {

  const dimension60Data =
    Number(segmentId) || Number(experimentId) ? `${segmentId}|${experimentId}` : '';
  gtmDatalayerPushData(
    {
      productName: name,
      productID: id,
      productPrice: price,
      productBrand: brand,
      productCategory: cat,
      productVariant: variant,
      productSubCategory: cat2,
      productDiscount: tradeDiscount,
      productSize: size,
      productQty: 1,
      store_type: getStoreName(),
      contains_store: catalogIdToStoreName(productCatalog)
    },
    'gtmProductDetailView'
  );

  const ga4AjioEnable = getGA4AJIOEnable(window?.__PRELOADED_STATE__?.flags);
  if (ga4AjioEnable) {
    const discount = (price * getDiscountValueWithoutPercentage(tradeDiscount)) / 100 || 0;
    gtmDatalayerPushData(
      {
        event: 'view_item',
        store_type: getStoreName(),
        contains_store: catalogIdToStoreName(productCatalog),
        pricing_cust_seg: getCustSegmentIds(),
        isGA4: true,
        event_category: 'Enhance E-commerce',
        event_action: 'view_item',
        ecommerce: {
          currency: 'INR',
          value: Number(discountPrice) || 0,
          items: [
            {
              item_id: id,
              item_name: name,
              discount: Number(discount) || 0,
              index: 0,
              item_brand: brand,
              item_list_name: list,
              item_category: cat,
              item_variant: variant,
              coupon: '',
              quantity: 1,
              price: Number(discountPrice) || 0,
              item_category2: cat2,
              product_id: id,
              product_name: name,
              product_category: cat2,
              product_store_type: catalogIdToStoreName(productCatalog),
              available_quantity_deliverable_non_deliverable: dimension59Data,
              segment_id_experiment_id: dimension60Data,
              sticker_name: productTag,
              offer_name: priceLabel,
              wishlish_count: '',
              ...(validToken && {
                search_attribution: validToken
              }),
              ...(earnRuleId && { earn_rule_id: earnRuleId }),
              ...(superCashEarning && { product_supercash_earn: superCashEarning }),
              product_discount: getDiscountValue(tradeDiscount),
              ...(averageRating && { ratings_shown_no_of_users: `${averageRating}|${ratingCount}` }),
              ...(itmSourceDetailValue ? { itm_source_detail: itmSourceDetailValue } : {})
            }
          ]
        }
      },
      'gtmProductDetailView'
    );
   
  } else {
    gtmDatalayerPushData(
      {
        event: 'ee_product_detail',
        store_type: getStoreName(),
        contains_store: catalogIdToStoreName(productCatalog),
        ecommerce: {
          currencyCode: 'INR',
          detail: {
            actionField: { list }, // 'detail' actions have an optional list property.
            products: [
              {
                name, // Name or ID is required.
                id,
                price,
                brand,
                category: cat,
                variant,
                size,
                category2: cat2,
                tradeDiscount,
                dimension12: id,
                dimension13: name,
                dimension14: '',
                dimension15: cat,
                dimension16: cat2,
                dimension20: '',
                dimension21: brand,
                dimension22: price,
                dimension23: catalogIdToStoreName(productCatalog),
                dimension24: '',
                dimension59: dimension59Data,
                dimension60: dimension60Data,
                dimension165: productTag,
                dimension166: priceLabel,
                dimension167: '',
                dimension170: getDiscountValue(tradeDiscount),
                ...(averageRating && { dimension46: `${averageRating}|${ratingCount}` })
              }
            ]
          }
        },
        pricing_cust_seg: getCustSegmentIds()
      },
      'gtmProductDetailView'
    );
  }

  if(segmentTrackingEnabled == 'ON'){
    const discount = (price * getDiscountValueWithoutPercentage(tradeDiscount)) / 100 || 0;
    segmentDatalayerPushData(
      {
        event: 'Product Details Viewed',
        //store_type: getStoreName(),
      // contains_store: catalogIdToStoreName(productCatalog),
      //  pricing_cust_seg: getCustSegmentIds(),
      // isGA4: true,
        screen_name: list,
        screen_source : list,
        ecommerce: {
          //currency: 'INR',
          //value: Number(discountPrice) || 0,
          items: [
            {
              // item_id: id,
              // item_name: name,
              // discount: Number(discount) || 0,
              product_position : 0,
              product_brand: brand,
              // item_list_name: list,
              // item_category: cat,
              // item_variant: variant,
              // coupon: '',
              // quantity: 1,
              product_list_price: Number(discountPrice) || 0,
              // item_category2: cat2,
              product_id: id + "_" + variant,
              product_name: name,
              product_brick: cat2,             
              
              product_segment: cat,
              product_details_text: product_details_text,
              product_offer_price: product_offer_price,
              product_mrp: price,
              visual_format: visual_format?.isBetterPdpEnabled ? 'Tabular' : 'Descriptor',
              ...(averageRating && { product_rating: `${averageRating}` }),
              ...(ratingCount && { number_of_ratings_collected : `${ratingCount}`}),
              product_tag: productTag
              // product_store_type: catalogIdToStoreName(productCatalog),
              // available_quantity_deliverable_non_deliverable: dimension59Data,
              // segment_id_experiment_id: dimension60Data,
              // sticker_name: productTag,
              // offer_name: priceLabel,
              // wishlish_count: '',
              // product_discount: getDiscountValue(tradeDiscount),
              // ...(averageRating && { ratings_shown_no_of_users: `${averageRating}|${ratingCount}` })
            }
          ]
        },
        ...(validToken && { attribution_token: validToken })
      },
      'gtmProductDetailView'
    );
  }
}

export function luxeGtmProductDetailView(
  name,
  id,
  price,
  brand,
  category,
  product_size,
  product_color,
  product_trade_discount,
  product_brick,
  product_tag,
  price_label,
  list,
  store_type,
  pageType,
  pageName,
  productCatalog,
  segmentId,
  experimentId,
  dimension59Data,
  originalPrice,
  offerName,
  itmSourceDetailValue = ''
) {
  const dimension60Data =
    Number(segmentId) || Number(experimentId) ? `${segmentId}|${experimentId}` : '';
  const ga4AjioEnable = getGA4AJIOEnable(window?.__PRELOADED_STATE__?.flags);
  if (ga4AjioEnable) {
    const discount =
      (originalPrice * getDiscountValueWithoutPercentage(product_trade_discount)) / 100 || 0;
    gtmDatalayerPushData(
      {
        event: 'view_item',
        store_type: getStoreName(),
        contains_store: catalogIdToStoreName(productCatalog),
        event_category: 'enhance ecommerce',
        event_action: 'details view',
        event_label: list,
        isGA4: true,
        ecommerce: {
          currency: 'INR',
          value: Number(price) || 0,
          items: [
            {
              item_id: id,
              item_name: name,
              discount: Number(discount) || 0,
              index: 0,
              item_brand: brand,
              item_list_name: list,
              item_category: category,
              price: Number(price) || 0,
              quantity: 1,
              product_name: name, // pass value for product_name
              product_id: id, // pass value for product_id
              product_size, // pass value for product_size
              product_category: category, // pass value for product_category
              product_brick, // pass value for product_brick
              product_store_type: catalogIdToStoreName(productCatalog), // pass value for Store_typ
              product_brand: brand, // pass value for product_brand
              product_price: price, // pass value for product_price
              product_color, // pass value for product_color
              product_trade_discount, // pass value for product_trade_discount
              product_tag, // pass value for product_tag
              Store_type: store_type, // pass value for Store_typ
              available_quantity_deliverable_non_deliverable: dimension59Data,
              segment_id_experiment_id: dimension60Data,
              ...(offerName ? { offer_name: offerName } : {}),
              ...(itmSourceDetailValue ? { itm_source_detail: itmSourceDetailValue } : {})
            }
          ]
        },
        page_type: pageType,
        page_name: pageName,
        contains_luxe: true
      },
      'luxeGtmProductDetailView'
    );
  } else {
    gtmDatalayerPushData(
      {
        event: 'ee_product_details',
        store_type: getStoreName(),
        contains_store: catalogIdToStoreName(productCatalog),
        event_category: 'enhance ecommerce',
        event_action: 'details view',
        event_label: list,
        ecommerce: {
          detail: {
            actionField: { list },
            products: [
              {
                name,
                id,
                price,
                brand,
                category,
                dimension6: name, // pass value for product_name
                dimension7: id, // pass value for product_id
                dimension8: brand, // pass value for product_brand
                dimension9: price, // pass value for product_price
                dimension10: product_color, // pass value for product_color
                dimension11: product_size, // pass value for product_size
                dimension12: product_trade_discount, // pass value for product_trade_discount
                dimension13: category, // pass value for product_category
                dimension14: product_brick, // pass value for product_brick
                dimension15: product_tag, // pass value for product_tag
                dimension16: store_type, // pass value for Store_typ
                dimension23: catalogIdToStoreName(productCatalog),
                dimension59: dimension59Data,
                dimension60: dimension60Data,
                dimension89: product_tag || '',
                dimension90: price_label || '',
                dimension91: '',
                ...(offerName ? { dimension82: offerName } : {})
              }
            ]
          }
        },
        page_type: pageType,
        page_name: pageName,
        contains_luxe: true
      },
      'luxeGtmProductDetailView'
    );
  }
}

export function gtmPurchaseComplete(
  tnxid,
  revenue,
  tax,
  shippingCharges,
  coupon,
  ProductArray,
  appliedCode,
  order,
  subscriptionDetails,
  pincode,
  membershipSavings = {},
  membershipInfo = {},
  urlPaymentMode = '',
  isBestCoupon = false,
  couponAppliedScreen = '',
  enableNonReturnableProductRevamp = false
) {
  const ga4AjioEnable = getGA4AJIOEnable(window?.__PRELOADED_STATE__?.flags);
  const validToken = getValidAttributionToken();
  if (ga4AjioEnable) {
    
    if (!Array.isArray(ProductArray)) ProductArray = [];
    gtmDatalayerPushData(
      {
        event: 'purchase',
        store_type: getStoreName(),
        contains_store: getStoreType(ProductArray.map(el => el.dimension23)),
        hybris_id: getUserUUID(),
        pricing_cust_seg: getCustSegmentIds(),
        event_category: 'Enhance E-commerce',
        event_action: 'purchase',
        isGA4: true,
        ...(pincode && { pincode }),
        ...(subscriptionDetails && {
          subscription_details: subscriptionDetails
        }),
        ecommerce: {
          transaction_id: tnxid,
          tax: Number(tax) || 0,
          shipping: Number(shippingCharges) || 0,
          currency: 'INR',
          coupon: appliedCode,
          value: ProductArray.reduce((acc, prod) => acc + (Number(prod.finalPrice) || 0), 0),
          items: ProductArray.map(product => ({
            item_id: product.id,
            item_name: product.name,
            discount: Number(product.discount) / (Number(product.quantity) || 1) || 0,
            index: Number(product.position) || 0,
            item_brand: product.brand,
            item_list_name: 'Thankyou',
            item_category: product.category,
            item_variant: product.variant,
            price: Number(product.finalPrice) / (Number(product.quantity) || 1) || 0,
            quantity: Number(product.quantity) || 1,
            coupon: appliedCode,
            product_id: product.dimension12,
            product_name: product.dimension13,
            product_category: product.dimension16,
            product_store_type: product.dimension23,
            product_segment: product.dimension36,
            product_portfolio: product.dimension37,
            product_vertical: product.dimension38,
            product_brick: product.dimension39,
            product_view_type: product.dimension41,
            ratings_shown_no_of_users: product.dimension46,
            available_quantity_deliverable_non_deliverable: product.dimension59,
            segment_id_experiment_id: product.dimension60,
            delivery_tag: product.dimension61,
            product_level_edd: product.dimension66,
            jio_ads_campaign_data: product.dimension161,
            prod_jio_ads_listing: product.dimension162,
            jio_ads_option_code: product.dimension163,
            sticker_name: product.dimension165,
            offer_name: product.dimension166,
            wishlish_count: product.dimension167,
            product_discount: product.dimension170,
            product_trade_discount: product.dimension171,
            product_size: product.dimension191,
            ...(product.pincode && { pincode: product.pincode }),
            ...(product.priorityDeliveryFee && {
              product_priority_delivery_fee: Number(product.priorityDeliveryFee).toFixed(1)
            }),
            ...(product.priorityDeliveryEDD && {
              priority_delivery_EDD: product.priorityDeliveryEDD
            }),
            ...(product.priority_delivery_sla_tag && {
              priority_delivery_sla_tag: product.priority_delivery_sla_tag
            }),
            ...(product.rushDeliveryFee && {
              product_rush_delivery_fee: Number(product.rushDeliveryFee).toFixed(1)
            }),
            ...(product.rushDeliveryEDD && {
              rush_delivery_EDD: product.rushDeliveryEDD
            }),
            ...(product.rush_delivery_sla_tag && {
              rush_delivery_sla_tag: product.rush_delivery_sla_tag
            }),
            ...(product.search_attribution && {
              search_attribution: product.search_attribution
            }),
            ...(product.subscription_delivery_fee_saving && {
              subscription_delivery_fee_saving: product.subscription_delivery_fee_saving
            }),
            ...(product.subscription_platform_fee_saving && {
              subscription_platform_fee_saving: product.subscription_platform_fee_saving
            }),
            ...(product.subscription_total_savings && {
              subscription_total_savings: product.subscription_total_savings
            }),
            ...(product.subscription_membership_discount && {
              subscription_membership_discount: product.subscription_membership_discount
            }),
            ...(product.itm_source && {
              itm_source: product.itm_source
            }),
            ...(product.itm_source_detail && {
              itm_source_detail: product.itm_source_detail
            }),
            ...(product.giftCount && {
              product_gift_with_purchase_flag: product.giftCount > 0
            })
          }))
        }
      },
      'gtmPurchaseComplete'
    );

  } else {
    ProductArray.forEach(x => {
      delete x.wasPrice;
      delete x.finalPrice;
      delete x.discount;
      delete x.priorityDeliveryFee;
      delete x.priorityDeliveryEDD;
    });
    gtmDatalayerPushData(
      {
        event: 'ee_purchase',
        store_type: getStoreName(),
        contains_store: getStoreType(ProductArray.map(el => el.dimension23)),
        hybris_id: getUserUUID(),
        pricing_cust_seg: getCustSegmentIds(),
        ecommerce: {
          purchase: {
            actionField: {
              id: tnxid,
              revenue,
              tax,
              shipping: shippingCharges,
              coupon
            },
            products: ProductArray
          }
        }
      },
      'gtmPurchaseComplete'
    );
  }

  if(segmentTrackingEnabled == 'ON'){
    if (!Array.isArray(ProductArray)) ProductArray = [];
    const enableGST = getGSTFlag(window?.__PRELOADED_STATE__, 'cart'); // TODO-GST check pagename for thankyou page
    let gstInfo = sessionStorage.getItem('gstInfo');
    let gstOrder = false;
    let cartItemGST = [];
    try {
      if (gstInfo) {
        gstInfo = JSON.parse(gstInfo);
        gstOrder =
          Boolean(gstInfo?.gstDetails?.gstNumber) ||
          (gstInfo?.gstDetails && Object.keys(gstInfo?.gstDetails).length > 0);
        cartItemGST = Array.isArray(gstInfo?.cartItemGST) ? gstInfo?.cartItemGST : [];
      }
    } catch (error) {
      console.error(error);
    }

    const isItemGSTEligible = skuId => {
      const gstItem = cartItemGST.find(x => x.code === skuId);
      if (gstItem) return gstItem?.isGstEligible || false;
      return false;
    };

    const productDataSeg = (order?.item || [])?.map(item => {
      const matchedEntry = order?.entries?.find(
        entry => entry?.product?.code === item?.code
      );

      return {
        ...item,
        deliveryMethod: matchedEntry?.deliveryMethod || null,
        deliverySLA: matchedEntry?.deliverySLA || null,
        convenienceFee: matchedEntry?.convenienceFee || item.convenienceFee,
        isPriorityDeliveryApplied: matchedEntry?.isPriorityDeliveryApplied,
        priceGroupId: matchedEntry?.priceGroupId ?? item?.priceGroupId ?? null,
        selectedPriceType: matchedEntry?.selectedPriceType ?? item?.selectedPriceType ?? null
      };
    });

    const { noCostEmiDiscount = 0, cashbackAmount = 0 } = order?.emiOfferDetails || {};
    const emiOfferTypeParts = [
      noCostEmiDiscount > 0 && 'Instant Discount',
      cashbackAmount > 0 && 'Cashback'
    ].filter(Boolean);

    let selectedEmiPlan = {};
    try {
      const emiPlanData = readCookie('selectedEmiPlan');
      if (emiPlanData) {
        selectedEmiPlan = JSON.parse(decodeURIComponent(emiPlanData));
        const isLocalhost = location.hostname === 'localhost' || location.hostname === '127.0.0.1';
        const domainAttr = isLocalhost ? '' : '; domain=.services.ajio.com';
        document.cookie = `selectedEmiPlan=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/${domainAttr};`;
      }
    } catch (e) {}

    segmentDatalayerPushData(
      {
        event: 'Purchased',
        store_name: getStoreName(),
        // store_type: getStoreType(ProductArray.map(el => el.dimension23)),
        store_type: getCurrentStoreType(),
        order_id : order.id,
        order_total: revenue,
        bag_total:  order?.orderSummary?.bagTotal?.value,
       // bag_assured_gift_flag : isAssuredGifts?.cartAssuredGifts === 'ON',
        //bag_price_update_callout: '',
        bag_item_count : order?.totalItems,
        //is_bag_merged: cart?.restoredProducts ? true : false,
        bag_savings: order?.orderSummary?.bagDiscount?.value,
        coupon_savings: order?.orderSummary?.couponSaving?.value,
        order_total: order?.orderSummary?.orderTotal?.value,
        platform_fee: order?.orderSummary?.convenienceFee?.total?.netAmount?.value,
        total_savings: order?.orderSummary?.totalSaving?.value,
        rone_balance: order?.relianceOneEarnedAmount?.value,
        rone_balance_used:'',
        rone_points_applicable:'',
        //wallet_cash_balance: ajioWalletData?.ajioWalletBalanceDet?.totalWalletBalance?.totalCash,
        wallet_points_applicable:'',
        //wallet_points_balance: ajioWalletData?.ajioWalletBalanceDet?.totalWalletBalance?.totalPoints,
        wallet_points_used:'',
       // delivery_type_selected: cart?.deliveryInfo?.deliveryModeValue,
        delivery_fee:  order?.orderSummary?.deliveryCharge?.value,
        coupon_name: appliedCode,
        ...(couponAppliedScreen && { is_best_coupon: isBestCoupon ? 'yes' : 'no' }),
        ...(couponAppliedScreen && { coupon_applied_screen: couponAppliedScreen }),
        payment_type: order?.paymentCode,
        emi_type: selectedEmiPlan.emi_type || (noCostEmiDiscount > 0 ? 'No Cost' : ''),
        interest_rate: selectedEmiPlan.interest_rate !== undefined ? selectedEmiPlan.interest_rate : '',
        tenure_months: selectedEmiPlan.tenure_months || '',
        emi_amount: selectedEmiPlan.emi_amount || '',
        emi_processing_fees:
          selectedEmiPlan.emi_processing_fees !== undefined
            ? selectedEmiPlan.emi_processing_fees
            : '',
        emi_offer_type: selectedEmiPlan.emi_offer_type || emiOfferTypeParts.join(', '),
        bank_discount: order?.orderSummary?.bankDiscount?.value || 0,
        return_processing_fee: '',
        rone_balance_used:'',       
        upi_id_saved: '',
        cards_saved: getCardsSavedCountFromState(getAppStateForAnalytics()),
        wallet_points_used: '',
        // delivery_type_selected: '',
        delivery_edd:'',
        delivery_fee:  order?.orderSummary?.deliveryCharge?.value,
        ...membershipSavings,
        ...membershipInfo,
        subscription_first_purcase_date: 'NA',
        subscripiton_number: 'NA',
        ...(pincode && { pincode }),
        ...(enableGST && { is_gst_selected: gstOrder }),
        ...(gstInfo && {
          ...(() => {
            const billingPin =
              gstInfo?.selectedBillingAddress?.pinCode ||
              gstInfo?.gstDetails?.address?.postalCode ||
              gstInfo?.gstDetails?.address?.pinCode;
            const shippingPin =
              gstInfo?.selectedShippingAddress?.pinCode ||
              gstInfo?.gstDetails?.address?.postalCode ||
              gstInfo?.gstDetails?.address?.pinCode;
            return {
              ...(billingPin && { billing_address_pincode: String(billingPin) }),
              ...(shippingPin && { shipping_address_pincode: String(shippingPin) }),
              ...(billingPin &&
                shippingPin && {
                  same_billing_shipping_address: String(billingPin) === String(shippingPin)
                })
            };
          })(),
          ...((gstInfo?.gstDetails?.gstNumber || gstInfo?.gstDetails) && {
            billing_address_gst_applicable: true,
            shipping_address_gst_applicable: isShippingGstApplicableFromGstInfo(gstInfo)
          })
        }),
        ecommerce: {
          //transaction_id: tnxid,
          //tax: Number(tax) || 0,
          shipping: Number(shippingCharges) || 0,
          currency: 'INR',
               
          items: (() => {
            let crossSellCodes = [];
            try {
              crossSellCodes = JSON.parse(sessionStorage.getItem('cross_sell_added_codes') || '[]');
            } catch {}
            const items = productDataSeg?.map(product => {
              const crossSellEntry = crossSellCodes.find(e => e.code === product.code);
              const crossSellWidgetTitle = getCrossSellWidgetSource(product?.code);
              const purchaseProductMatch =
                (ProductArray || []).find(el => el?.skuId === product?.code) || {};
              return {
                ...getProductItemForCart(product, ProductArray, validToken),
                price_group_id: product.priceGroupId ?? null,
                seller_option_selected: product.selectedPriceType ?? null,
                ...(enableGST && { is_gst_eligible: isItemGSTEligible(product.code) }),
                ...(enableNonReturnableProductRevamp && {
                  non_returnable_reason: product.isReturnable ? 'NA' : 'Product Policy',
                  non_exchangable_reason:
                    (!product.isExchangeable &&
                      (gstInfo?.gstDetails && Object.keys(gstInfo.gstDetails).length > 0
                        ? 'GST'
                        : 'Product Policy')) ||
                    'NA'
                }),
                ...(crossSellWidgetTitle && {
                  itm_source_detail: crossSellWidgetTitle
                }),
                ...(crossSellEntry && {
                  product_cross_sell_type: crossSellEntry.tabName || '',
                  itm_source_detail: 'cross sell widget'
                }),
                ...getPurchasedProductExtraProps({
                  product,
                  purchaseProductMatch,
                  appliedCode,
                  crossSellEntry,
                  crossSellWidgetTitle
                })
              };
            });
            if (crossSellCodes.length) sessionStorage.removeItem('cross_sell_added_codes');
            return items;
          })()
        }
      },
      'gtmPurchaseComplete'
    );
  }

}

export function luxeGtmPurchaseComplete(
  tnxId,
  affiliation = '',
  revenue,
  tax,
  shipping,
  coupon,
  purchaseProduct = [],
  payment_mode,
  storeType,
  subscriptionDetails,
  pincode,
  membershipSavings = {},
  membershipInfo,
  enableNonReturnableProductRevamp = false,
  emiOfferDetails = {},
  paymentCode = ''
) {
  const ga4AjioEnable = getGA4AJIOEnable(window?.__PRELOADED_STATE__?.flags);
  const validToken = getValidAttributionToken();
  if (ga4AjioEnable) {
    gtmDatalayerPushData({
      event: 'purchase',
      hybris_id: getUserUUID(),
      event_category: 'enhance ecommerce',
      event_action: 'checkout-payment',
      event_label: payment_mode,
      store_type: storeType,
      contains_store: getStoreType(purchaseProduct.map(el => el.dimension23)),
      contains_luxe: true,
      isGA4: true,
      ...(pincode && { pincode }),
      ...(subscriptionDetails && {
        subscription_details: subscriptionDetails
      }),
      ecommerce: {
        transaction_id: tnxId,
        value: purchaseProduct.reduce((acc, prod) => acc + (Number(prod.finalPrice) || 0), 0),
        tax: Number(tax) || 0,
        shipping: Number(shipping) || 0,
        currency: 'INR',
        coupon,
        items: purchaseProduct.map(product => ({
          item_id: product.id,
          item_name: product.name,
          discount: Number(product.discount) / (Number(product.quantity) || 1) || 0,
          index: Number(product.position) || 0,
          item_brand: product.brand,
          item_list_name: 'Thankyou',
          item_category: product.category,
          item_variant: product.variant,
          price: Number(product.finalPrice) / (Number(product.quantity) || 1) || 0,
          coupon,
          quantity: Number(product.quantity) || 1,
          product_id: product.dimension7,
          product_name: product.dimension6,
          product_category: product.dimension13,
          product_store_type: product.dimension16,
          product_brick: product.dimension14,
          product_size: product.dimension11,
          Store_type: product.dimension16,
          product_tag: product.dimension15,
          product_color: product.dimension10,
          product_trade_discount: product.dimension12,
          product_price: product.dimension9,
          product_brand: product.dimension8,
          segment_id_experiment_id: product.dimension60,
          ...(product.pincode && { pincode: product.pincode }),
          ...(product.priorityDeliveryFee && {
            product_priority_delivery_fee: Number(product.priorityDeliveryFee).toFixed(1)
          }),
          ...(product.priorityDeliveryEDD && {
            priority_delivery_EDD: product.priorityDeliveryEDD
          }),
          ...(product.priority_delivery_sla_tag && {
            priority_delivery_sla_tag: product.priority_delivery_sla_tag
          }),
          ...(product.rushDeliveryFee && {
            product_rush_delivery_fee: Number(product.rushDeliveryFee).toFixed(1)
          }),
          ...(product.rushDeliveryEDD && {
            rush_delivery_EDD: product.rushDeliveryEDD
          }),
          ...(product.rush_delivery_sla_tag && {
            rush_delivery_sla_tag: product.rush_delivery_sla_tag
          }),
          ...(product.subscription_delivery_fee_saving && {
            subscription_delivery_fee_saving: product.subscription_delivery_fee_saving
          }),
          ...(product.subscription_platform_fee_saving && {
            subscription_platform_fee_saving: product.subscription_platform_fee_saving
          }),
          ...(product.subscription_total_savings && {
            subscription_total_savings: product.subscription_total_savings
          }),
          ...(product.subscription_membership_discount && {
            subscription_membership_discount: product.subscription_membership_discount
          }),
          ...(product.itm_source && { itm_source: product.itm_source }),
          ...(product.itm_source_detail && { itm_source_detail: product.itm_source_detail }),
        }))
      }
    });

  } else {
    purchaseProduct.forEach(x => {
      delete x.wasPrice;
      delete x.finalPrice;
      delete x.discount;
      delete x.priorityDeliveryFee;
      delete x.priorityDeliveryEDD;
    });
    gtmDatalayerPushData({
      event: 'ee_purchase',
      hybris_id: getUserUUID(),
      event_category: 'enhance ecommerce',
      event_action: 'checkout-payment',
      event_label: payment_mode,
      store_type: storeType,
      contains_store: getStoreType(purchaseProduct.map(el => el.dimension23)),
      contains_luxe: true,
      ecommerce: {
        purchase: {
          actionField: {
            id: tnxId,
            affiliation,
            revenue,
            tax,
            shipping,
            coupon
          },
          products: purchaseProduct
        }
      }
    });
  }

  if(segmentTrackingEnabled == 'ON'){
    const enableGST = getGSTFlag(window?.__PRELOADED_STATE__, 'cart'); // TODO-GST check pagename for thankyou page
    let gstInfo = sessionStorage.getItem('gstInfo');
    let gstOrder = false;
    let cartItemGST = [];
    try {
      if (gstInfo) {
        gstInfo = JSON.parse(gstInfo);
        gstOrder =
          Boolean(gstInfo?.gstDetails?.gstNumber) ||
          (gstInfo?.gstDetails && Object.keys(gstInfo?.gstDetails).length > 0);
        cartItemGST = Array.isArray(gstInfo?.cartItemGST) ? gstInfo?.cartItemGST : [];
      }
    } catch (error) {
      console.error(error);
    }

    const isItemGSTEligible = skuId => {
      const gstItem = cartItemGST.find(x => x.code === skuId);
      if (gstItem) return gstItem?.isGstEligible || false;
      return false;
    };

    const { noCostEmiDiscount: luxeNoCostEmiDiscount = 0, cashbackAmount: luxeCashbackAmount = 0 } = emiOfferDetails || {};
    const luxeEmiOfferTypeParts = [
      luxeNoCostEmiDiscount > 0 && 'Instant Discount',
      luxeCashbackAmount > 0 && 'Cashback'
    ].filter(Boolean);

    let luxeSelectedEmiPlan = {};
    try {
      const emiPlanData = readCookie('selectedEmiPlan');
      if (emiPlanData) {
        luxeSelectedEmiPlan = JSON.parse(decodeURIComponent(emiPlanData));
        const isLocalhost = location.hostname === 'localhost' || location.hostname === '127.0.0.1';
        const domainAttr = isLocalhost ? '' : '; domain=.services.ajio.com';
        document.cookie = `selectedEmiPlan=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/${domainAttr};`;
      }
    } catch (e) {}

    segmentDatalayerPushData({
      event: 'Purchased',
      //hybris_id: getUserUUID(),
      event_category: 'enhance ecommerce',
      event_action: 'checkout-payment',
      event_label: payment_mode,
      store_type: storeType,
      //contains_store: getStoreType(purchaseProduct.map(el => el.dimension23)),
      //contains_luxe: true,
      //isGA4: true,
      order_id:tnxId,
      order_total: purchaseProduct.reduce((acc, prod) => acc + (Number(prod.finalPrice) || 0), 0),
      payment_type:
        payment_mode && paymentCode?.toLowerCase() !== 'cod'
          ? `${payment_mode} - ${paymentCode}`
          : paymentCode,
      emi_type: luxeSelectedEmiPlan.emi_type || (luxeNoCostEmiDiscount > 0 ? 'No Cost' : ''),
      interest_rate: luxeSelectedEmiPlan.interest_rate !== undefined ? luxeSelectedEmiPlan.interest_rate : '',
      tenure_months: luxeSelectedEmiPlan.tenure_months || '',
      emi_amount: luxeSelectedEmiPlan.emi_amount || '',
      emi_processing_fees: luxeSelectedEmiPlan.emi_processing_fees !== undefined ? luxeSelectedEmiPlan.emi_processing_fees : '',
      emi_offer_type: luxeSelectedEmiPlan.emi_offer_type || luxeEmiOfferTypeParts.join(', '),
      cards_saved: getCardsSavedCountFromState(getAppStateForAnalytics()),
      ...(pincode && { pincode }),
      ...(enableGST && { is_gst_selected: gstOrder }),
      ...(gstInfo && {
        ...(() => {
          const billingPin =
            gstInfo?.selectedBillingAddress?.pinCode ||
            gstInfo?.gstDetails?.address?.postalCode ||
            gstInfo?.gstDetails?.address?.pinCode;
          const shippingPin =
            gstInfo?.selectedShippingAddress?.pinCode ||
            gstInfo?.gstDetails?.address?.postalCode ||
            gstInfo?.gstDetails?.address?.pinCode;
          return {
            ...(billingPin && { billing_address_pincode: String(billingPin) }),
            ...(shippingPin && { shipping_address_pincode: String(shippingPin) }),
            ...(billingPin &&
              shippingPin && {
                same_billing_shipping_address: String(billingPin) === String(shippingPin)
              })
          };
        })(),
        ...((gstInfo?.gstDetails?.gstNumber || gstInfo?.gstDetails) && {
          billing_address_gst_applicable: true,
          shipping_address_gst_applicable: isShippingGstApplicableFromGstInfo(gstInfo)
        })
      }),
      ...membershipSavings,
      subscription_first_purcase_date: 'NA',
      subscripiton_number: 'NA',
      ...membershipInfo,
      ecommerce: {
        //transaction_id: tnxId,
        //value: purchaseProduct.reduce((acc, prod) => acc + (Number(prod.finalPrice) || 0), 0),
        //tax: Number(tax) || 0,
        //shipping: Number(shipping) || 0,
        //currency: 'INR',
        //coupon,
        items: (() => {
          let crossSellCodes = [];
          try {
            crossSellCodes = JSON.parse(sessionStorage.getItem('cross_sell_added_codes') || '[]');
          } catch {}
          const items = purchaseProduct.map(product => {
            const crossSellEntry = crossSellCodes.find(e => e.code === product.skuId);
             const crossSellWidgetTitle = getCrossSellWidgetSource(product?.code);
            const plaItem = getPlaItemByProductCode(product?.skuId);
            return {
          product_id: product.id + "_" + product.variant,
          product_name: product.name,
          sku_id: plaItem?.productCode || product?.skuId,
          ...(plaItem?.productCode && {
            seller_id: plaItem?.seller_id,
            client_id: plaItem?.client_id || getPlaAdsClientID(window?.__PRELOADED_STATE__) || ''
          }),
          //discount: Number(product.discount) / (Number(product.quantity) || 1) || 0,
          product_position: Number(product.position) || 0,
          product_brand: product.brand,
          is_subscription: product?.isMembershipArticle,
          //item_list_name: 'Thankyou',
          //item_category: product.category,
          //item_variant: product.variant,
          product_list_price : Number(product?.price) / (Number(product.quantity) || 1) || 0,
          coupon_name: coupon,
          product_quantity: Number(product.quantity) || 1,
          // product_id: product.dimension7,
          // product_name: product.dimension6,
          // product_category: product.dimension13,
          // product_store_type: product.dimension16,
          product_brick: product.dimension14,
          product_size: product.dimension11,
          product_source_store: product.dimension16,
          product_tag: product.dimension15,
          product_gift_with_purchase_flag:
            Number(product?.giftQuantity ?? product?.giftCount ?? 0) > 0,
          //product_color: product.dimension10,
          //product_trade_discount: product.dimension12,
          //product_price: product.dimension9,
          //product_brand: product.dimension8,
          // segment_id_experiment_id: product.dimension60,
          // ...(product.priorityDeliveryFee && {
          //   product_priority_delivery_fee: Number(product.priorityDeliveryFee).toFixed(1)
          // }),
          // ...(product.priorityDeliveryEDD && {
          //   delivery_edd: product.priorityDeliveryEDD
          // }),
          // ...(product.priority_delivery_sla_tag && {
          //   priority_delivery_sla_tag: product.priority_delivery_sla_tag
          // })
          ...(product.pincode && { pincode: product.pincode }),
          ...(product.priorityDeliveryFee && {
            product_priority_delivery_fee: Number(product.priorityDeliveryFee).toFixed(1)
          }),
          ...(product.priorityDeliveryEDD && {
            priority_delivery_EDD: product.priorityDeliveryEDD
          }),
          ...(product.priority_delivery_sla_tag && {
            priority_delivery_sla_tag: product.priority_delivery_sla_tag
          }),
          ...(product.rushDeliveryFee && {
            product_rush_delivery_fee: Number(product.rushDeliveryFee).toFixed(1)
          }),
          ...(product.rushDeliveryEDD && {
            rush_delivery_EDD: product.rushDeliveryEDD
          }),
          ...(product.rush_delivery_sla_tag && {
            rush_delivery_sla_tag: product.rush_delivery_sla_tag
          }),
          ...(enableGST && {
            is_gst_eligible: isItemGSTEligible(product.skuId)
          }),
          product_return_window: product.returnWindow ?? 0,
          ...(enableNonReturnableProductRevamp && {
            non_returnable_reason: product.isReturnable ? 'NA' : 'Product Policy',
            non_exchangable_reason:
              (!product.isExchangeable &&
                (gstInfo?.gstDetails && Object.keys(gstInfo.gstDetails).length > 0
                  ? 'GST'
                  : 'Product Policy')) ||
              'NA'
          }),
         ...(crossSellWidgetTitle && {
                itm_source_detail: crossSellWidgetTitle
              }),
          ...(validToken && { attribution_token: validToken }),
              ...(crossSellEntry && {
                product_cross_sell_type: crossSellEntry.tabName || '',
                itm_source_detail: 'cross sell widget'
              }),
          ...getPurchasedProductExtraProps({
            product,
            purchaseProductMatch: product,
            appliedCode: coupon,
            crossSellEntry,
            crossSellWidgetTitle
          }),
              price_group_id: product.priceGroupId ?? null,
              seller_option_selected: product.selectedPriceType ?? null
            };
          });
          if (crossSellCodes.length) sessionStorage.removeItem('cross_sell_added_codes');
          return items;
        })()
      }
    });
  }

}

export function gtmPromotionImpressions(promotionArray = []) {
  const jioadsData = JSON.parse(localStorage.getItem('jioadsData'));
  const plpPageTitle = localStorage.getItem('plpPageTitle');
  const osmosConfigClientId = getPlaAdsClientID(window?.__PRELOADED_STATE__) || '';
  const ga4AjioEnable = getGA4AJIOEnable(window?.__PRELOADED_STATE__?.flags);
  if (ga4AjioEnable) {
    gtmDatalayerPushData(
      {
        event: 'view_promotion',
        plp_page_title: plpPageTitle,
        hybris_id: getUserUUID(),
        isGA4: true,
        ecommerce: {
          items: promotionArray.map(promotion => ({
            creative_name: promotion.creative,
            creative_slot: promotion.position,
            promotion_id: promotion.id,
            promotion_name: promotion.name,
            ...(promotion.subscriptionDetails && {
              subscription_details: promotion.subscriptionDetails
            })
          }))
        }
      },
      'gtmPromotionImpressions'
    );  
    
  } else {
    gtmDatalayerPushData(
      {
        event: 'ee_Internal_Promotions_views',
        plp_page_title: plpPageTitle,
        hybris_id: getUserUUID(),
        ecommerce: {
          promoView: {
            promotions: promotionArray
          }
        }
      },
      'gtmPromotionImpressions'
    );
  }

   segmentEventPush({
      event:'Banner Viewed',
      ad_budget: '',
      ad_cost:'',
      ad_flag: jioadsData? true: false,
      ad_id: jioadsData?.id || promotionArray?.[0]?.ad_id || promotionArray?.[0]?.adsData?.ad || '',
      ad_type:'',
        banners: promotionArray.map(promotion => ({
          // creative_name: promotion.creative,
          banner_position: getSearchPlpBannerPosition(promotion.position) || '',
          banner_id: promotion.id || '',
          banner_name: promotion.name || '',
          banner_type: promotion.type || 'IMAGE',
          clickable : promotion.clickable ?? true,
          ...(promotion?.uclid && { uclid: promotion?.uclid || "" }),
          client_id: promotion?.client_id || osmosConfigClientId || "",
          ...(promotion?.ad_source && { ad_source: promotion?.ad_source || "" }),
          ...(promotion?.sclid && { scl_id: promotion?.sclid || "" }),
          ad_id: promotion?.ad_id || promotion?.adsData?.ad || '',
          ...(promotion?.adsData && { ad_flag: true }),
        })),
      screen_name: isCurrentSearchPlpScreen() ? 'search plp screen' : plpPageTitle
    });


}

export function gtmPlpBannerView(promotionArray = []){
   const jioadsData = JSON.parse(localStorage.getItem('jioadsData'));
   const plpPageTitle = localStorage.getItem('plpPageTitle');
   const osmosConfigClientId = getPlaAdsClientID(window?.__PRELOADED_STATE__) || '';
   segmentEventPush({
    event:'Banner Viewed',
    ad_budget: '',
    ad_cost: promotionArray?.[0]?.ad_cost || '',
    ad_flag: jioadsData? true: false,
    ad_id: jioadsData?.id || promotionArray?.[0]?.ad_id || '',
    ad_type:'',
      banners: promotionArray.map(promotion => ({
        // creative_name: promotion.creative,
        banner_position: getSearchPlpBannerPosition(promotion.position) || '',
        banner_id: promotion.id || '',
        banner_name: promotion.name || '',
        banner_type: promotion.type || 'IMAGE',
        clickable : promotion.clickable ?? true,
        ...(promotion?.uclid && { uclid: promotion?.uclid || "" }),
        client_id: promotion?.client_id || osmosConfigClientId || "",
        ...(promotion?.ad_source && { ad_source: promotion?.ad_source || "" }),
        ...(promotion?.sclid && { scl_id: promotion?.sclid || "" }),
        ...(promotion?.ad_cost && { ad_cost: promotion?.ad_cost || "" }),
        ad_id: promotion?.ad_id || promotion?.adsData?.ad || '',
        ...(promotion?.adsData && { ad_flag: true }),
      })),
    screen_name: isCurrentSearchPlpScreen() ? 'search plp screen' : plpPageTitle
  });
}

export function gtmPromotionCmsImpressions(
  promotionArray = [],
  storeType = 'ajio',
  pageName = '',
  pageType = ''
) {
  const ga4AjioEnable = getGA4AJIOEnable(window?.__PRELOADED_STATE__?.flags);
  if (ga4AjioEnable) {
    gtmDatalayerPushData(
      {
        event: 'view_promotion',
        hybris_id: getUserUUID(),
        store_type: storeType,
        contains_store: getStoreName(),
        page_name: pageName,
        page_type: pageType,
        isGA4: true,
        ecommerce: {
          items: promotionArray.map(promotion => ({
            creative_name: promotion.creative,
            creative_slot: promotion.position,
            promotion_id: promotion.id,
            promotion_name: promotion.name,
            ...(promotion.subscriptionDetails && {
              subscription_details: promotion.subscriptionDetails
            })
          }))
        }
      },
      'gtmPromotionImpressions'
    );   
    
  } else {
    gtmDatalayerPushData(
      {
        event: 'ee_Internal_Promotions_views',
        hybris_id: getUserUUID(),
        store_type: storeType,
        contains_store: getStoreName(),
        page_name: pageName,
        page_type: pageType,
        ecommerce: {
          promoView: {
            promotions: promotionArray
          }
        }
      },
      'gtmPromotionCmsImpressions'
    );
  }
  let page = pageType;
  if(pageType.search("fleek") !== -1){
    page = pageType?.split("_")?.[0] || '';
  }
  const pageTypeOsmos = page === 'orderListing' ? 'orderList' : page === "thankyou" ? 'thankYou' : page === 'home screen' ? 'home' : page;
  const osmosConfig = getBannerOsmosAdsConfig(window?.__PRELOADED_STATE__) || '';
  const isOsmosEnable = pageTypeOsmos ? osmosConfig?.[pageTypeOsmos]?.isEnabled : '';
   segmentEventPush({
     event: 'Banner Viewed',
     ad_budget: '',
     ad_cost: '',
     ad_flag: isOsmosEnable,
     ad_id: '',
     ad_type: '',
       banners: promotionArray.map(promotion => ({
         // creative_name: promotion.creative,
         banner_position: getSearchPlpBannerPosition(promotion.position) || '',
         banner_id: promotion.id || '',
         banner_name: promotion.name || '',
         banner_type: promotion.type || 'IMAGE',
         clickable: promotion.clickable ?? true,
         ad_source: promotion.adSource || '',
         ad_flag: isOsmosEnable,
         ad_id: promotion.ad_id || promotion.cid || promotion.id || '',
         scl_id: promotion.sclid || '',
         uclid: promotion.uclid || '',
         client_id: promotion.clientId || ''
       })),
     screen_name: isCurrentSearchPlpScreen() ? 'search plp screen' : pageName
   });

}

export function gtmPromotionPdpImpressions(
  promotionArray = [],
  storeType = 'ajio',
  productData,
  pageName = ''
) {
  const ga4AjioEnable = getGA4AJIOEnable(window?.__PRELOADED_STATE__?.flags);
  if (ga4AjioEnable) {
    gtmDatalayerPushData(
      {
        event: 'view_promotion',
        hybris_id: getUserUUID(),
        store_type: storeType,
        contains_store: getStoreName(),
        ...productData,
        page_name: pageName,
        isGA4: true,
        ecommerce: {
          items: promotionArray.map(promotion => ({
            creative_name: promotion.creative,
            creative_slot: promotion.position,
            promotion_id: promotion.id,
            promotion_name: promotion.name,
            ...(promotion.subscriptionDetails && {
              subscription_details: promotion.subscriptionDetails
            })
          }))
        }
      },
      'gtmPromotionImpressions'
    );   
   
  } else {
    gtmDatalayerPushData(
      {
        event: 'ee_Internal_Promotions_views',
        hybris_id: getUserUUID(),
        store_type: storeType,
        contains_store: getStoreName(),
        ...productData,
        page_name: pageName,
        ecommerce: {
          promoView: {
            promotions: promotionArray
          }
        }
      },
      'gtmPromotionCmsImpressions'
    );
  }

  segmentEventPush({
      event:'Banner Viewed',
      ad_budget: '',
      ad_cost:'',
      ad_flag: '',
      ad_id: '',
      ad_type:'',
        banners: promotionArray.map(promotion => ({
          // creative_name: promotion.creative,
          banner_position: promotion.position || '',
          banner_id: promotion.id || '',
          banner_name: promotion.name || '',
          banner_type: promotion.type || 'IMAGE',
          clickable : promotion.clickable ?? true,
        })),   
      screen_name: pageName
    });
}

export function gtmLuxePromotionImpressions(promotionArray = []) {
  const ga4AjioEnable = getGA4AJIOEnable(window?.__PRELOADED_STATE__?.flags);
  if (ga4AjioEnable) {
    gtmDatalayerPushData(
      {
        event: 'view_promotion',
        hybris_id: getUserUUID(),
        event_category: 'enhance ecommerce',
        event_action: 'banner impressions',
        store_type: 'luxe',
        contains_store: getStoreName(),
        isGA4: true,
        ecommerce: {
          items: promotionArray.map(promotion => ({
            creative_name: promotion.creative,
            creative_slot: promotion.position,
            promotion_id: promotion.id,
            promotion_name: promotion.name,
            ...(promotion.subscriptionDetails && {
              subscription_details: promotion.subscriptionDetails
            })
          }))
        }
      },
      'gtmPromotionImpressions'
    );
   
  } else {
    gtmDatalayerPushData(
      {
        event: 'ee_Internal_Promotions_views',
        hybris_id: getUserUUID(),
        event_category: 'enhance ecommerce',
        event_action: 'banner impressions',
        store_type: 'luxe',
        contains_store: getStoreName(),
        ecommerce: {
          promoView: {
            promotions: promotionArray
          }
        }
      },
      'gtmLuxePromotionImpressions'
    );
  }

  segmentEventPush({
      event:'Banner Viewed',
      ad_budget: '',
      ad_cost:'',
      ad_flag: '',
      ad_id: '',
      ad_type:'',
        banners: promotionArray.map(promotion => ({
          // creative_name: promotion.creative,
          banner_position: promotion.position || '',
          banner_id: promotion.id || '',
          banner_name: promotion.name || '',
          banner_type: promotion.type || 'IMAGE',
          clickable : promotion.clickable ?? true,
          ad_flag: false,
          ad_id: ''
        })),
      screen_name: pageName
    });
}

export function gtmLuxePromotionCmsImpressions(
  promotionArray = [],
  storeType = 'luxe',
  pageName = '',
  pageType = ''
) {
  const ga4AjioEnable = getGA4AJIOEnable(window?.__PRELOADED_STATE__?.flags);
  if (ga4AjioEnable) {
    if (!Array.isArray(promotionArray)) promotionArray = [];
    gtmDatalayerPushData(
      {
        event: 'view_promotion',
        hybris_id: getUserUUID(),
        store_type: storeType,
        contains_store: getStoreName(),
        page_name: pageName,
        page_type: pageType,
        isGA4: true,
        ecommerce: {
          items: promotionArray.map(promotion => ({
            creative_name: promotion.creative,
            creative_slot: promotion.position,
            promotion_id: promotion.id,
            promotion_name: promotion.name,
            ...(promotion.subscriptionDetails && {
              subscription_details: promotion.subscriptionDetails
            })
          }))
        }
      },
      'gtmPromotionImpressions'
    );    
   
  } else {
    gtmDatalayerPushData(
      {
        event: 'ee_Internal_Promotions_views',
        hybris_id: getUserUUID(),
        store_type: storeType,
        contains_store: getStoreName(),
        page_name: pageName,
        page_type: pageType,
        ecommerce: {
          promoView: {
            promotions: promotionArray
          }
        }
      },
      'gtmLuxePromotionCmsImpressions'
    );
  }

  const pageTypeOsmos = pageType === 'orderListing' ? 'orderList' : pageType === "thankyou" ? 'thankYou' : pageType === 'home screen' ? 'home' : pageType;
  const osmosConfig = getBannerOsmosAdsConfig(window?.__PRELOADED_STATE__) || '';
  const isOsmosEnable = pageType ? osmosConfig?.[pageTypeOsmos]?.isEnabled : '';

  segmentEventPush({
    event:'Banner Viewed',
    ad_budget: '',
    ad_cost:'',
    ad_flag: isOsmosEnable || '',
    ad_id: '',
    ad_type:'',
      banners: promotionArray.map(promotion => ({
        // creative_name: promotion.creative,
        banner_position: promotion.position,
        banner_id: promotion.id,
        banner_name: promotion.name,
        banner_type: promotion.type || 'IMAGE',
        clickable : promotion.clickable ?? true,
        ad_source: promotion.adSource || '',
        ad_flag: isOsmosEnable || false,
        ad_id: '',
        scl_id: promotion.sclid || '',
        uclid: promotion.uclid || '',
        client_id: promotion.clientId || ''
      })),
    screen_name: pageName
  });
}

export function gtmPromotionClick(promoID, promoName, promoCreativeName, promoPosition, isJioAd = false, osmosSegmentPayload = {}, isOsmos = false) {
  const adsDataClickImp = JSON.parse(localStorage.getItem('adsDataClickImp'));
  const plpPageTitle = localStorage.getItem('plp_page_title');
  const ga4AjioEnable = getGA4AJIOEnable(window?.__PRELOADED_STATE__?.flags);
  if (ga4AjioEnable) {
    gtmDatalayerPushData(
      {
        event: 'select_promotion',
        ...(!isJioAd && {
          plp_page_title: adsDataClickImp?.plp_page_title || plpPageTitle
        }),
        hybris_id: getUserUUID(),
        store_type: getStoreName(),
        contains_store: getStoreName(),
        isGA4: true,
        ecommerce: {
          items: [
            {
              creative_name: promoCreativeName || '',
              ...(promoPosition && {
                creative_slot: promoPosition === undefined ? '' : promoPosition
              }),
              promotion_id: promoID || '',
              promotion_name: promoName || ''
            }
          ]
        }
      },
      'gtmPromotionClick'
    );   
  } else {
    gtmDatalayerPushData(
      {
        event: 'ee_Internal_Promotions_Clicks',
        plp_page_title: adsDataClickImp?.plp_page_title
          ? adsDataClickImp.plp_page_title
          : plpPageTitle,
        hybris_id: getUserUUID(),
        store_type: getStoreName(),
        contains_store: getStoreName(),
        ecommerce: {
          promoClick: {
            promotions: [
              {
                id: promoID || '',
                name: promoName || '',
                creative: promoCreativeName || '',
                position: promoPosition === undefined ? '' : promoPosition
              }
            ]
          }
        }
      },
      'gtmPromotionClick'
    );
  }

  const jioadsData = typeof localStorage !== 'undefined'
    ? JSON.parse(localStorage.getItem('jioadsData') || 'null')
    : null;
  const resolvedBannerAdId =
    osmosSegmentPayload?.ad_id || jioadsData?.id || promoID || '';

    segmentEventPush({
      event:'Banner Clicked',
      ad_budget: '',
      ad_cost:'',
      ad_flag: isJioAd || isOsmos || '',
      ad_id: resolvedBannerAdId,
      ad_type:'',
        banners: [
          {
            banner_id: promoID || '',
            banner_name : promoName || '',
            ...(getSearchPlpBannerPosition(promoPosition) !== undefined &&
              getSearchPlpBannerPosition(promoPosition) !== '' && {
                banner_position: getSearchPlpBannerPosition(promoPosition)
              }),
            clickable : true,
            ...osmosSegmentPayload,
            banner_type : adsDataClickImp || 'IMAGE',
            ad_flag: isJioAd || isOsmos || '',
            ad_id: resolvedBannerAdId,
          }
        ],
      screen_name: plpPageTitle
    });


}

export function gtmPlpBannerClick (promoID, promoName, promoCreativeName, promoPosition, isJioAd = false, uclid = '', client_id = '', ad_source = '', scl_id = ' ', ad_cost = '', ad_id = '') {
  const adsDataClickImp = JSON.parse(localStorage.getItem('adsDataClickImp'));
  const plpPageTitle = localStorage.getItem('plp_page_title');
  const resolvedBannerAdId = ad_id || promoID || '';
   segmentEventPush({
      event:'Banner Clicked',
      ad_budget: '',
      ad_cost: ad_cost || '',
      ad_flag: isJioAd,
      ad_id: resolvedBannerAdId,
      ad_type:'',
        banners: [
          {
            banner_id: promoID || '',
            banner_name : promoName || '',
            ...(getSearchPlpBannerPosition(promoPosition) !== undefined &&
              getSearchPlpBannerPosition(promoPosition) !== '' && {
                banner_position: getSearchPlpBannerPosition(promoPosition)
              }),
            banner_type : adsDataClickImp || 'IMAGE',
            clickable : true,
            ...(ad_source && { ad_source: ad_source }) ,
            ...(client_id && { client_id: client_id }) ,
            ...(uclid && { uclid: uclid }),
            ...(scl_id && { scl_id: scl_id }),
            ...(ad_cost && { ad_cost: ad_cost }),
            ad_id: resolvedBannerAdId,
            ad_flag: isJioAd,
          }
        ],
      screen_name: plpPageTitle
    });
}

export function gtmPromotionCmsClick(
  promoID,
  promoName,
  promoCreativeName,
  promoPosition,
  storeType,
  pageName,
  pageType,
  isJioAds = 'false',
  cid = '',
  jioAdsBannerName = '',
  isPDBanner = 'false',
  subscriptionDetails = null,
  banner_type = '',
  sclid = '',
  uclid = '',
  client_id = ''
) {
  const ccb = UTILS.getCCB(jioAdsBannerName);
  const adSource = banner_type === 'EXTERNAL_AD' ? 'osmos' : isJioAds ? 'jioads' : 'no_ads';
  const ga4AjioEnable = getGA4AJIOEnable(window?.__PRELOADED_STATE__?.flags);
  if (ga4AjioEnable) {
    gtmDatalayerPushData(
      {
        event: 'select_promotion',
        hybris_id: getUserUUID(),
        store_type: storeType,
        contains_store: getStoreName(),
        page_name: pageName,
        page_type: pageType,
        isGA4: true,
        ...(subscriptionDetails && {
          subscription_details: subscriptionDetails
        }),
        ecommerce: {
          items: [
            {
              creative_name: promoCreativeName || '',
              ...(promoPosition && { creative_slot: promoPosition || '' }),
              ...((cid || promoID) && {
                promotion_id: isJioAds === 'true' ? cid.replace('[ccb]', ccb) : promoID || ''
              }),
              promotion_name:
                isJioAds === 'true'
                  ? `jioads - ${jioAdsBannerName}`
                  : isPDBanner === 'true'
                  ? `priority - ${promoName}`
                  : promoName || ''
            }
          ]
        }
      },
      'gtmPromotionCmsClick'
    );

  } else {
    gtmDatalayerPushData(
      {
        event: 'ee_Internal_Promotions_Clicks',
        hybris_id: getUserUUID(),
        store_type: storeType,
        contains_store: getStoreName(),
        page_name: pageName,
        page_type: pageType,
        ecommerce: {
          promoClick: {
            promotions: [
              {
                id: isJioAds === 'true' ? cid.replace('[ccb]', ccb) : promoID || '',
                name: isJioAds === 'true' ? `jioads - ${jioAdsBannerName}` : isPDBanner === 'true' ? `priority - ${promoName}` : promoName || '',
                creative: promoCreativeName || '',
                position: promoPosition || ''
              }
            ]
          }
        }
      },
      'gtmPromotionCmsClick'
    );
  }

   segmentEventPush({
      event:'Banner Clicked',
      ad_budget: '',
      ad_cost:'',
      ad_flag: isJioAds || banner_type === "EXTERNAL_AD" ? true : '',
      ad_id: cid,
      ad_type:'',
        banners: [
          {
            banner_id: promoID || '',
            banner_name :  banner_type === "EXTERNAL_AD" ? `osmos - ${promoName}` :  isJioAds === 'true'
                        ? `jioads - ${jioAdsBannerName}`
                        : isPDBanner === 'true'
                        ? `priority - ${promoName}`
                        : promoName || '',
            ...(getSearchPlpBannerPosition(promoPosition) !== undefined &&
              getSearchPlpBannerPosition(promoPosition) !== '' && {
                banner_position: getSearchPlpBannerPosition(promoPosition)
              }),     
            banner_type : banner_type || 'IMAGE',
            clickable : true,
            scl_id: sclid || '',
            uclid: uclid || '',
            client_id: client_id || '',
            ad_source: adSource,
            ad_flag: isJioAds || banner_type === "EXTERNAL_AD" ? true : false,
            ad_id: cid || ''
          }
        ],
      screen_name: pageName
    });
}

export function gtmLuxePromotionClick(
  promoID,
  promoName,
  promoCreativeName,
  promoPosition,
  storeType
) {
  const ga4AjioEnable = getGA4AJIOEnable(window?.__PRELOADED_STATE__?.flags);
  if (ga4AjioEnable) {
    gtmDatalayerPushData(
      {
        event: 'select_promotion',
        hybris_id: getUserUUID(),
        event_category: 'enhance ecommerce',
        event_action: 'banner click',
        store_type: storeType,
        contains_store: getStoreName(),
        isGA4: true,
        ecommerce: {
          items: [
            {
              creative_name: promoCreativeName || '',
              creative_slot: promoPosition || '',
              promotion_id: promoID || '',
              promotion_name: promoName || ''
            }
          ]
        }
      },
      'gtmLuxePromotionClick'
    );
    
  } else {
    gtmDatalayerPushData(
      {
        event: 'ee_Internal_Promotions_Clicks',
        hybris_id: getUserUUID(),
        event_category: 'enhance ecommerce',
        event_action: 'banner click',
        store_type: storeType,
        contains_store: getStoreName(),
        ecommerce: {
          promoClick: {
            promotions: [
              {
                id: promoID || '',
                name: promoName || '',
                creative: promoCreativeName || '',
                position: promoPosition || ''
              }
            ]
          }
        }
      },
      'gtmLuxePromotionClick'
    );
  }

  segmentEventPush({
      event:'Banner Clicked',
      ad_budget: '',
      ad_cost:'',
      ad_flag: '',
      ad_id: promoID || '',
      ad_type:'',
        banners: [
          {
            banner_id: promoID || '',
            banner_name : promoName || '',
            ...(getSearchPlpBannerPosition(promoPosition) !== undefined &&
              getSearchPlpBannerPosition(promoPosition) !== '' && {
                banner_position: getSearchPlpBannerPosition(promoPosition)
              }),     
            banner_type : adsDataClickImp || 'IMAGE',
            clickable : true,
            ad_id: promoID || '',
          }
        ],
      screen_name: '',
    });
}

export function gtmLuxePromotionCmsClick(
  promoID,
  promoName,
  promoCreativeName,
  promoPosition,
  storeType,
  pageName,
  pageType,
  isJioAds = 'false',
  cid = '',
  jioAdsBannerName = '',
  subscriptionDetails,
  banner_type = '',
  sclid = '',
  uclid = '',
  client_id = ''
) {
  const ccb = UTILS.getCCB(jioAdsBannerName);
  const adSource = banner_type === 'EXTERNAL_AD' ? 'osmos' : isJioAds ? 'jioads' : 'no_ads';
  const ga4AjioEnable = getGA4AJIOEnable(window?.__PRELOADED_STATE__?.flags);
  if (ga4AjioEnable) {
    gtmDatalayerPushData(
      {
        event: 'select_promotion',
        hybris_id: getUserUUID(),
        store_type: storeType,
        contains_store: getStoreName(),
        page_name: pageName,
        page_type: pageType,
        isGA4: true,
        ...(subscriptionDetails && {
          subscription_details: subscriptionDetails
        }),
        ecommerce: {
          items: [
            {
              creative_name: promoCreativeName || '',
              ...(promoPosition && {
                creative_slot: promoPosition || ''
              }),
              ...((cid || promoID) && {
                promotion_id: isJioAds === 'true' ? cid.replace('[ccb]', ccb) : promoID || ''
              }),
              promotion_name:
                isJioAds === 'true' ? `jioads - ${jioAdsBannerName}` : promoName || ''
            }
          ]
        }
      },
      'gtmLuxePromotionCmsClick'
    );       
    
  } else {
    gtmDatalayerPushData(
      {
        event: 'ee_Internal_Promotions_Clicks',
        hybris_id: getUserUUID(),
        store_type: storeType,
        contains_store: getStoreName(),
        page_name: pageName,
        page_type: pageType,
        ecommerce: {
          promoClick: {
            promotions: [
              {
                id: isJioAds === 'true' ? cid.replace('[ccb]', ccb) : promoID || '',
                name: isJioAds === 'true' ? `jioads - ${jioAdsBannerName}` : promoName || '',
                creative: promoCreativeName || '',
                position: promoPosition || ''
              }
            ]
          }
        }
      },
      'gtmLuxePromotionCmsClick'
    );
  }

  segmentEventPush({
      event:'Banner Clicked',
      ad_budget: '',
      ad_cost:'',
      ad_flag: isJioAds || banner_type === 'EXTERNAL_AD' ? true : false,
      ad_id: cid,
      ad_type:'',
        banners: [
          {
            banner_id: isJioAds === 'true' ? cid.replace('[ccb]', ccb) : promoID || '',
            banner_name : banner_type === 'EXTERNAL_AD' ? `osmos - ${promoName}` : isJioAds === 'true' ? `jioads - ${jioAdsBannerName}` : promoName || '',
            ...(getSearchPlpBannerPosition(promoPosition) !== undefined &&
              getSearchPlpBannerPosition(promoPosition) !== '' && {
                banner_position: getSearchPlpBannerPosition(promoPosition)
              }),     
            banner_type : banner_type || 'IMAGE',
            clickable : true,
            scl_id: sclid || '',
            uclid: uclid || '',
            client_id: client_id || '',
            ad_source: adSource,
            ad_flag: isJioAds || banner_type === 'EXTERNAL_AD' ? true : false,
            ad_id: cid || ''
          }
        ],
      screen_name: pageName
  });
}

export function gtmRefundEvent(id, products = {}, pageType = '', pageName = '') {
  gtmDatalayerPushData(
    {
      event: 'ee_return',
      store_type: getStoreName(),
      contains_store: getStoreName(),
      hybris_id: getUserUUID(),
      ecommerce: {
        refund: {
          actionField: { id },
          ...products
        }
      },
      page_type: pageType,
      page_name: pageName
    },
    'gtmRefundEvent'
  );
}

export function gtmGA4RefundEvent(id, product, pageType = '', pageName = '', returnId) {
  gtmDatalayerPushData(
    {
      event: 'refund',
      store_type: getStoreName(),
      contains_store: getStoreName(),
      hybris_id: getUserUUID(),
      isGA4: true,
      ecommerce: {
        transaction_id: id,
        order_status: 'DELIVERED',
        return_id: returnId,
        value: product.value * product.quantity,
        currency: 'INR',
        tax: Number(product.tax) || 0,
        shipping: Number(product.shipping) || 0,
        coupon: product.coupon,
        items: [
          {
            item_id: product.id,
            item_name: product.name,
            discount: product.discount,
            index: product.index,
            item_brand: product.brand,
            item_list_name: product.list,
            item_category: product.category,
            item_variant: product.variant,
            price: product.price,
            quantity: product.quantity || 1,
            segment_id_experiment_id: product.segmentId,
            ...(product.offer_name && { offer_name: product.offer_name })
          }
        ]
      },
      page_type: pageType,
      page_name: pageName
    },
    'gtmRefundEvent'
  );
}

export function gtmFeedPostClick(
  itemId,
  itemName,
  creativeName,
  creativeSlot,
  screenName = '',
  storeType = FLEEK_STORE.toLowerCase(),
  banner_type = ''
) {
  const ga4AjioEnable = getGA4AJIOEnable(window?.__PRELOADED_STATE__?.flags);
  if (ga4AjioEnable) {
    gtmDatalayerPushData(
      {
        event: 'select_promotion',
        hybris_id: getUserUUID(),
        store_type: storeType,
        contains_store: getStoreName(),
        page_name: screenName,
        isGA4: true,
        ecommerce: {
          items: [
            {
              creative_name: creativeName,
              creative_slot: creativeSlot,
              promotion_id: itemId,
              promotion_name: itemName
            }
          ]
        }
      },
      'gtmPromotionCmsClick'
    );

  } else {
    gtmDatalayerPushData(
      {
        event: 'ee_Internal_Promotions_Clicks',
        hybris_id: getUserUUID(),
        store_type: storeType,
        contains_store: getStoreName(),
        page_name: screenName,
        ecommerce: {
          promoClick: {
            promotions: [
              {
                id: itemId,
                name: itemName,
                creative: creativeName,
                position: creativeSlot
              }
            ]
          }
        }
      },
      'gtmPromotionCmsClick'
    );
  }

  segmentEventPush({
      event:'Banner Clicked',
      ad_budget: '',
      ad_cost:'',
      ad_flag: '',
      ad_id: '',
      ad_type:'',
      
        banners:[
          {
            banner_id: itemId,
            banner_name : creativeName,
            ...(creativeSlot && { banner_position: creativeSlot || '' }),     
            banner_type : banner_type || 'IMAGE',
            clickable : true,
          }
        ],
      screen_name: screenName
  });

}

export function gtmScratchCardEventPush(obj) {
  gtmDatalayerPushData(
    {
      ...obj
    },
    'gtmScratchCardEventPush'
  );
}

export function gtmTopShoppersEventPush(obj) {
  gtmDatalayerPushData(
    {
      ...obj
    },
    'gtmTopShoppersEventPush'
  );
}

export function gtmDynamicRemarketingSetup(prodIds, pageType, totalValue = 0) {
  const tagsParams = {
    ecomm_prodid: prodIds,
    ecomm_pagetype: pageType
  };
  if (pageType === 'cart') {
    tagsParams.ecomm_totalvalue = totalValue || 0;
  }
  gtmDatalayerPushData(
    {
      event: 'dynRmktParamsReady',
      google_tag_params: tagsParams
    },
    'gtmDynamicRemarketingSetup'
  );
}

export function gtmPrimeAICTA(gaType = null, displayValue = null) {
  console.log('🚀 ~ file: GTMdatalayer.js ~ line 803 ~ gtmPrimeAICTA ~ gaType', gaType);
  const gaParams = {
    event: 'find_recomonded_size',
    event_category: 'product detail interactions',
    event_action: 'find recommended size click'
  };
  if (gaType === 'EDIT') {
    gaParams.event = 'recomonded_size_click';
    gaParams.event_action = 'recommended size click';
  } else if (gaType === 'CONTINUE') {
    gaParams.event = 'continue_cta';
    gaParams.event_category = 'recommended size interactions';
    gaParams.event_action = 'continue';
  } else if (gaType === 'DISPLAY') {
    gaParams.event = 'recomonded_size';
    gaParams.event_category = 'product detail interactions';
    gaParams.event_action = 'recommended size display';
    gaParams.event_label = displayValue;
  } else if (gaType === 'WIDGET_VIEW') {
    gaParams.event = 'size_chart_interaction';
    gaParams.event_action = 'prime AI recommonded widget view';
  }
  console.log('🚀 ~ file: GTMdatalayer.js ~ line 809 ~ gtmPrimeAICTA ~ gaParams', gaParams);
  gtmDatalayerPushData(gaParams, 'gtmPrimeAICTA');
}

export function gtmEventPushAccountInteraction(pageName, action, label = '', customStoreAttr = '') {
  gtmDatalayerPushData(
    {
      action,
      label,
      page_name: pageName,
      page_type: 'my account screen',
      event: 'user_account_interactions',
      category: 'user account interactions',
      hybris_id: getUserUUID(),
      contains_store: customStoreAttr || getStoreName(),
      store_type: getStoreName(),
      ...getPageUserInfo()
    },
    'gtmEventPush'
  );

  // Segment push event
  segmentDatalayerPushData(
    {
      action,
      label,
      screen_name: pageName,
      // page_type: 'my account screen',
      event: 'Menu Option Clicked',
      clicked_menu_name: label,
      component_name: label,
      // hybris_id: getUserUUID(),
      // contains_store: customStoreAttr || getStoreName(),
      store_type: getStoreName(),
      // ...getPageUserInfo()
    },
    'gtmEventPush'
  );
}

export function gtmEventPushProductDetailsInteractions(
  action,
  label,
  productCode,
  productName,
  brandName,
  productCategory,
  productSubCategory
) {
  const { pageType, pageName } = getPageInfo(window.location.pathname || '', false, false);
  gtmEventPush({
    event: 'product_details_interactions',
    event_category: 'product details interactions',
    event_action: action,
    label: label,
    product_id: productCode,
    product_name: productName,
    product_brand: brandName,
    product_brick: productCategory,
    product_category: productSubCategory,
    page_name: pageName,
    page_type: pageType
  });
}

export function gtmDressToolViewEvent(type, dressToolDataObject) {
  const ga4AjioEnable = getGA4AJIOEnable(window?.__PRELOADED_STATE__?.flags);
  if (ga4AjioEnable) {
    gtmEventPush({
      event: type,
      dress_tool_ingress: dressToolDataObject?.dressToolTriggerPoint,
      screenname: dressToolDataObject?.screenname
    });
  }
}

export function gtmDressToolInteractEvent(type, dressToolDataObject) {
  const ga4AjioEnable = getGA4AJIOEnable(window?.__PRELOADED_STATE__?.flags);
  if (ga4AjioEnable) {
    gtmEventPush({
      event: type,
      dress_tool_ingress: dressToolDataObject?.dressToolTriggerPoint,
      screenname: dressToolDataObject?.screenname,
      category: dressToolDataObject?.category,
      action: dressToolDataObject?.action,
      label: dressToolDataObject?.label
    });
  }
}


export function rushAddressBarEvents(eventNameType, payload) {
  const ga4AjioEnable = getGA4AJIOEnable(window?.__PRELOADED_STATE__?.flags);
  if (ga4AjioEnable) {
    switch (eventNameType) {
      case "closeButtonClick":
        gtmEventPush({
          event: "back_button_clicked",
          event_category: "address_selection_screen_interactions",
          screen_name :"address selection"
        });
        segmentEventPush({
          event: "Back Button Clicked",
          page_name :"address selection"
        });
        break;

      case "locationNotAllowed":
        gtmEventPush({
          event: "location_disabled",
          event_category: "address_selection_screen_interactions",
          screen_name :"address selection"
        });
        segmentEventPush({
          event: "Location Disabled",
          page_name :"address selection"
        });
        break;

      case "locationEnableClick":
        gtmEventPush({
          event: "buttonTap",
          event_category: "buttonTap",
          event_action: "enable_location_clicked",
          screen_name :"address selection"
        });
        segmentEventPush({
          event: "Enable Location Clicked",
          page_name :"address selection"
        });
        break;

      case "searchNoResult":
        gtmEventPush({
          event: "search_no_result",
          event_category: "address_selection_screen_interactions",
          event_label: payload,
          screen_name :"address selection"
        });
        segmentEventPush({
          event: "Address Zsr Triggered",
          search_term:payload,
          page_name :"address selection"
        });
        break;

      case "searchBarCrossIconClicked":
        gtmEventPush({
          event: "search_cross_clicked",
          event_category: "address_selection_screen_interactions",
          screen_name :"address selection"
        });
        segmentEventPush({
          event: "Address Search Cross Clicked",
          page_name :"address selection"
        });
        break;

      case "selectRushAddress":
        gtmEventPush({
          event: "select_address",
          event_category: "address_selection_screen_interactions",
          event_action: payload?.source,
          event_label: payload?.pincode,
          screen_name :"address selection"
        });
        segmentEventPush({
          event: "Address Selected",
          address_selected_source:payload?.source,
          address_details:payload?.pincode,
          page_name :"address selection"
        });
        break;

      case "pinInteraction":
        gtmEventPush({
          event: "pin_interaction",
          event_category: "address_selection_screen_interactions",
          event_label: `${payload?.pincode} | ${payload?.lat} | ${payload?.long}`,
          screen_name :"address selection"
        });
        segmentEventPush({
          event: "pin interacted",
          address_details:payload?.pincode,
          lat_long:`${payload?.lat} | ${payload?.long}`,
          page_name :"address selection map"
        });
        break;

      case "confirmLocationButtonClick":
        gtmEventPush({
          event: "buttonTap",
          event_category: "buttonTap",
          event_action: "confirm_location_clicked",
          event_label: payload?.pincode,
          screen_name :"address selection"
        });
        segmentEventPush({
          event: "location confirmed",
          address_details:payload?.pincode,
          lat_long:`${payload?.lat} | ${payload?.long}`,
          page_name :"address selection map"
        });
        break;

      case "rushPdpRedirectImpression":
        gtmEventPush({
          event: "rush_pdp_redirect_impression",
          event_category: "rush_pdp_redirect_impression",
          event_action: "rush_pdp_redirect_impression",
          product_id:payload?.product_id,
          brick:payload?.brick,
          vertical:payload?.vertical
        });
        break;

      case "rushPdpRedirectClick":
        gtmEventPush({
          event: "rush_pdp_redirect_click",
          event_category: "rush_pdp_redirect_click",
          event_action: "rush_pdp_redirect_click",
          product_id:payload?.product_id,
          brick:payload?.brick,
          vertical:payload?.vertical
        });
        break;
        
        default:{}
    }
  }
}
export function getParentProduct(parentProduct){
  //console.log("Parent Product", parentProduct);
  return (parentProduct && parentProduct.productDetails ? {
          media_type : 'image',
          parent_product_id : parentProduct.productDetails?.code,
          parent_product_name: parentProduct.productDetails?.name,
          parent_product_mrp: parentProduct.productDetails?.wasPriceData?.value,
          parent_product_list_price: parentProduct.productDetails?.price?.value,
          //parent_product_offer_price: parentProduct.productDetails.id,
          //parent_product_position: parentProduct.id,
         // parent_product_rating: parentProduct.productDetails.id,
          parent_product_brick: parentProduct.productDetails?.brickCategory,
         // parent_product_vertical: parentProduct.productDetails.id,
        // parent_product_segment: parentProduct.id,
          parent_product_brand: parentProduct.productDetails?.brandName,
          catalogue_id: parentProduct.productDetails?.catalogName,
        } : {});
}

export function getProductItem(product, token){
  const searchExpansionType = getSearchExpansionType();
  const isSearchPlpProduct = isCurrentSearchPlpScreen();
  const isPlpProduct = isCurrentPlpListingScreen();
  const listingEntity = isPlpProduct ? getListingProductEntity(product) : product;
  const itmSourceProps = isPlpProduct ? getPlpProductItmSource(product) : {};
  console.log('getProductItem :: adsFlag ::', product?.adsFlag, ':: position ::', product?.position);
  return {
            //item_id: product.id,
            //item_name: product.name,
            //discount: Number(productDiscount) || 0,
            ...(isSearchPlpProduct && {
              media_type: getListingProductMediaType(listingEntity),
              product_oos_flag: getListingProductOosFlag(listingEntity),
              redirection_type: getSearchPlpRedirectionType()
            }),
            product_position: Number(product.position) || 0,
            product_brand: product.brandName || product.brandTypeName || product.fnlColorVariantData?.brandName || product.brand,
            //item_list_name: product.list || itemListName,
            //item_category: product.category,
            //item_variant: product.variant,            
            //coupon: product.coupon,
            //quantity: Number(product.quantity) || 1,
            // Closet entities do not always carry fnlColorVariantData.colorGroup, so fall
            // back to code the same way getProductItemPDP does on the click side.
            product_id:
              product?.fnlColorVariantData?.colorGroup || product?.colorGroup || product?.code,
            product_name: product.name,
            product_mrp : isPlpProduct
              ? getListingProductMrp(listingEntity) || product.wasProductPrice || ''
              : product.wasProductPrice,
            product_list_price: Number(product.price) || 0,
            product_offer_price: isPlpProduct
              ? getListingOfferPrice(listingEntity) || product.offerPrice || ''
              : product.offerPrice,
            //product_category: product.category,
            //product_store_type: product.dimension23,
            product_segment: isPlpProduct
              ? getListingProductSegment(listingEntity) || product.dimension36 || ''
              : product.dimension36,
            //product_portfolio: product.dimension37,
            product_vertical: isPlpProduct
              ? getListingProductVertical(listingEntity) || product.dimension38 || ''
              : product.dimension38,
            product_brick: isPlpProduct
              ? getListingProductBrick(listingEntity) || product.dimension39 || ''
              : product.dimension39,
            product_tag: (() => {
              const baseTag = product.sticker_name || product.dimension89 || '';
              const urlParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
              const trendTagName = urlParams?.get('isTrendWidget') === 'true' ? (urlParams?.get('tagName') || '') : '';
              return trendTagName ? `${baseTag}|||${trendTagName}` : baseTag;
            })(),
            //product_view_type: product.dimension41,
            product_rating: isPlpProduct
              ? getListingProductRating(listingEntity) || (product.dimension46 ? String(product.dimension46).split('|')[0] : '') || ''
              : product.dimension46?.split('|')[0] || String(product.averageRating ?? 0),            
            //available_quantity_deliverable_non_deliverable: product.dimension59,
            //segment_id_experiment_id: product.dimension60,
            //delivery_tag: product.dimension61,
            //product_level_edd: product.dimension66,
            //jio_ads_campaign_data: product.dimension161,
            //prod_jio_ads_listing: product.dimension162,
            //jio_ads_option_code: product.dimension163,
            //sticker_name: product.dimension165,
            //offer_name: product.dimension166,
            product_wishlisted_count: product.dimension167 || 0,
            //product_discount: product.dimension170,
            //product_trade_discount: product.dimension171,
            //product_size: product.dimension191,
            //...(userRelevancyFlag ? { product_store_type: getStoreName() + '|' + productListType } : {}),
            catalogue_id: product.catalogName,
            similar_product_icon_available_flag: isPlpProduct
              ? getSimilarProductIconAvailableFlag()
              : false,
            redirection_type:
              product.redirection_type ||
              getSearchPlpRedirectionType(),
            ...(product.media_type && { media_type: product.media_type }),
            ...(typeof product.product_oos_flag === 'boolean' && {
              product_oos_flag: product.product_oos_flag
            }),
            ...(Number(product.collection_position) > 0 && {
              collection_position: Number(product.collection_position)
            }),
            catalog_id: product.catalogName,
            // The taxonomy name is catalogue_id; catalog_id is kept for the
            // existing consumers of this payload.
            // sku_id was only reported for ad-sourced products below, so it read as
            // missing for every organic impression.
            sku_id: product.code || product.skuId || '',
            product_oos_flag: Number(product.dimension59Count ?? product.availableQuantity ?? 1) === 0,
            media_type: product.media_type || (product.videoUrl || product.dimension164 ? 'video' : 'Image'),
            redirection_type: product.redirection_type || getSearchPlpRedirectionType(),
            cross_sell_type: product.product_cross_sell_type || product.cross_sell_type || 'NA',
            itm_source: product.itm_source || 'NA',
            itm_source_detail: product.itm_source_detail || 'NA',
            similar_product_icon_available_flag: false,
            ...(product.dimension162 == 'yes') ? {
              ad_flag: true,
              ad_id: product.dimension161,
              ad_source: 'jioads'
            } : {
              ad_flag:false,
              ad_source: 'no ads'
            },
            ...(product.dimension166 && {
              rush_product_tag: product.dimension166
            }),
            priority_delivery_edd: product.dimension166,
            ...(token && {
              attribution_token: token
            }),
            ...(product?.adsFlag && Object.keys(product.adsFlag).length > 0 && { uclid: _get(product, 'adsFlag.config.uclid','')}),
            ...(product?.adsFlag && Object.keys(product.adsFlag).length > 0 && { client_id: _get(product, 'adsFlag.config.cid','')}),
            ...(product?.adsFlag && Object.keys(product.adsFlag).length > 0 && { seller_id: _get(product, 'seller_id','') || _get(product, 'sellerId','')}),
            sku_id:
              product?.adsFlag && Object.keys(product.adsFlag).length > 0
                ? product?.code
                : product?.variantOptions?.[0]?.code || product?.code || '',
            ...(product?.adsFlag && Object.keys(product.adsFlag).length > 0 && {
              ad_source: _get(product, 'adsFlag.config.ad_source', '') || (_get(product, 'adsFlag.config.uclid', '') ? 'osmos' : 'jioads')
            }),
            ...(product?.adsFlag && Object.keys(product.adsFlag).length > 0 && { scl_id: _get(product, 'adsFlag.config.sclid','')}),
            ...(product?.adsFlag && Object.keys(product.adsFlag).length > 0 && { ad_id: _get(product, 'adsFlag.ad','')}),
            ...(product?.adsFlag && Object.keys(product.adsFlag).length > 0 && { ad_cost: _get(product, 'adsFlag.ad_cost','')}),
            ...(product?.adsFlag && _get(product, 'adsFlag.config.uclid', '') && {
              impression_type: Number(product.position) === 0 ? 'osmos_pdp_top' : 'osmos_pdp_widget'
            }),
            ...(product?.adsFlag && Object.keys(product.adsFlag).length > 0 && { ad_flag: true }),
            ...(searchExpansionType && { search_expansion_type: searchExpansionType }),
            ...(product?.media_type && { media_type: product.media_type }),
            ...(product?.product_oos_flag && { product_oos_flag: product.product_oos_flag }),
            ...(isPlpProduct && {
              ad_id: getListingProductAdId(listingEntity) || product.dimension161 || '',
              itm_source: itmSourceProps.itm_source,
              itm_source_detail: itmSourceProps.itm_source_detail
            })
          };
}

export function getProductItemPDP(product, idx, campaignId, priority_delivery_EDD, isCheckUncheck){
  console.log("getProductItemPDP ::", product);
  if(!product) return {}

  const searchExpansionType = getSearchExpansionType();
  const offerPrice = product.offerPrice ? product.offerPrice?.price : 
                      (product.potentialPromotions && product.potentialPromotions.length > 0 ? product.potentialPromotions[0].maxSavingPrice : '');
  let productListPrice;
  let productOfferPrice;
  let pdEdd;
  if(isCheckUncheck){
    productListPrice = Number(product && product?.priceInfo && product?.priceInfo?.netPrice && product?.priceInfo?.netPrice?.value);
    productOfferPrice = 'NA';
    pdEdd = priority_delivery_EDD && priority_delivery_EDD?.priorityDelivery && priority_delivery_EDD?.priorityDelivery?.deliveryDateTime;
  }

  const productImageOosFlag = (() => {
    const status =
      String(product?.stock?.stockLevelStatus ?? '').toLowerCase() ||
      String(product?.stockLevelStatus ?? '').toLowerCase();
        if (status) return status !== 'outofstock';
        return false;
  })();
  const isSearchPlpProduct = isCurrentSearchPlpScreen();
  const isPlpProduct = isCurrentPlpListingScreen();
  const validToken = getValidAttributionToken();
  const itmSourceProps = isPlpProduct ? getPlpProductItmSource(product) : {};
  
  // Shared with the impression path so a product cannot report in stock on view and
  // out of stock on click. Also covers variantOptions[].stock, which is where a PLP
  // entity actually carries its stock level.
  const productOosFlag = getImpressionOosFlag(product);

  return {
                
                media_type: isSearchPlpProduct ? getListingProductMediaType(product) : 'image',
                product_position: Number(idx) || 0,
                ...(Number(product.collection_position) > 0 && {
                  collection_position: Number(product.collection_position)
                }),
    product_brand:
      product.brandName || product.brandTypeName || product.fnlColorVariantData?.brandName,
                product_mrp: product.wasPriceData?.price ? product.wasPriceData?.price : product.wasPriceData?.value,
                product_list_price: Number(product.price?.value) || (isCheckUncheck && productListPrice && productListPrice) || 0,
                product_offer_price: Number(offerPrice) || (isCheckUncheck && productOfferPrice && productOfferPrice) ||0,            
                // product_id: product.code?.includes('_') ? product.code : (product.code.slice(0, -3) + "_" + (product.fnlColorVariantData?.colorGroup ? product.fnlColorVariantData?.colorGroup?.split('_')[1] : product.code?.split('_')[1])),
                product_id: product?.fnlColorVariantData?.colorGroup || product?.colorGroup || product?.code,
                product_name: product.name,
                product_brick: product.brickNameText ? product.brickNameText : product.brickSubCategory,
                product_vertical: product.verticalNameText ? product.verticalNameText : product.brickName,
                product_segment: product.segmentNameText ? product.segmentNameText : product.brickCategory,
                similar_product_icon_available_flag: Boolean(product.variantOptions?.length || 0),
                redirection_type: getSearchPlpRedirectionType(),                
                ...(product.campaignId && {
                  ad_id : product.campaignId,
                }),
                ...(product.priority_delivery_edd && {
                  rush_product_tag: product.priority_delivery_edd
                }),
                priority_delivery_edd: product.priority_delivery_edd,
                ...(isCheckUncheck && pdEdd && {
                  event_priority_delivery_edd: pdEdd
                }),
                product_tag: product.product_tag,
                product_rating: product.averageRating ? product.averageRating : product.ratingsResponse?.aggregateRating?.averageRating,
                number_of_ratings_collected : product.ratingCount,
                catalog_id :product.catalogName,
                product_oos_flag: isSearchPlpProduct
                  ? getListingProductOosFlag(product)
                  : !productImageOosFlag,
                catalogue_id: product.catalogName || product.catalog,
                product_oos_flag: productOosFlag,
                ...(product?.adsData || product.campaignId
                  ? {
                      ad_flag: true,
                      ad_source: product?.adsData ? (product?.adsData?.config?.adSource || "osmos") : "jioads"
                    }
                  : {
                      ad_flag: false,
                      ad_source: 'no ads'
                    }),
                ...(product?.adsData && { uclid: product?.adsData?.config?.uclid || ""}),
                ...(product?.adsData && { client_id: product?.adsData?.config?.cid || product?.osmosClientId || ""}) ,
                ...(product?.adsData && { scl_id: product?.adsData?.config?.sclid || ""}) ,
                sku_id: product?.adsData
                      ? product?.code
                      : product?.variantOptions?.[0]?.code || product?.code || '',
                ...(product?.adsData && { seller_id: product?.seller_id || product?.sellerId || "" }) ,
                ...(product?.adsData && { ad_id: product?.adsData?.ad || "" }) ,
                ...(product?.adsData && { ad_cost: product?.adsData?.ad_cost || "" }),
                ...(product?.adsData && {
                  impression_type: Number(idx) === 0 ? 'osmos_pdp_top' : 'osmos_pdp_widget'
                }),
                product_gift_with_purchase_flag: product?.offerPrice?.giftAvailable,
                ...(searchExpansionType && { search_expansion_type: searchExpansionType }),
                ...(isPlpProduct && {
                  ad_id: getListingProductAdId(product) || campaignId || '',
                  attribution_token: validToken || '',
                  collection_position: getPlpCollectionPosition(idx),
                  itm_source: itmSourceProps.itm_source,
                  itm_source_detail: itmSourceProps.itm_source_detail,
                  product_wishlisted_count: getListingWishlistedCount(product),
                  similar_product_icon_available_flag: getSimilarProductIconAvailableFlag(),
                  redirection_type: getSearchPlpRedirectionType()
                })
      };
}

/**
 * Size label from a variant's qualifiers — prefer standardSize (S/L/XXL),
 * fall back to size (brand/numeric).
 */
function getVariantSizeLabel(variant) {
  const quals = Array.isArray(variant?.variantOptionQualifiers)
    ? variant.variantOptionQualifiers
    : [];
  let size = '';
  let standardSize = '';
  quals.forEach(({ qualifier, value }) => {
    if (!value) return;
    const key = String(qualifier || '').toLowerCase();
    if (key === 'standardsize') standardSize = value;
    if (key === 'size') size = value;
  });
  return standardSize || size || '';
}

/**
 * Multiseller-only enrichment for the "Pdp Product Image Details" event.
 * Returns the extra `products[]` props (available_sizes / oos_sizes /
 * price_group_id) only when the multiseller flag is on, otherwise an empty
 * object so the event shape is unchanged for the control cohort. `product` is
 * the raw PDP aggregator object (carries `variantOptions` + `priceData`).
 * available_sizes / oos_sizes are comma-separated size labels (e.g. "S,L,XXL").
 * price_group_id comes from API `priceData.priceGroupId`.
 */
export function getMultiSellerImageDetailsProps(product, enabled) {
  if (!enabled || !product) return {};
  const variants = Array.isArray(product.variantOptions) ? product.variantOptions : [];
  const available = [];
  const oos = [];
  variants.forEach(variant => {
    const label = getVariantSizeLabel(variant);
    if (!label) return;
    const status = String(variant?.stock?.stockLevelStatus ?? '').toLowerCase();
    const level = variant?.stock?.stockLevel;
    const isOos = status === 'outofstock' || level === 0;
    if (isOos) oos.push(label);
    else available.push(label);
  });
  const priceGroupId =
    product?.priceData?.priceGroupId ??
    variants[0]?.priceData?.priceGroupId ??
    product?.baseOptions?.[0]?.options?.[0]?.priceData?.priceGroupId ??
    '';
  return {
    available_sizes: available.join(','),
    oos_sizes: oos.join(','),
    price_group_id: priceGroupId
  };
}

/**
 * Comma-separated list of size labels that offer a RUSH delivery option, for
 * the multiseller "Size Widget Viewed" event. `sizes` is the size list
 * ([{ value }]), `sizeDataMap` maps sizeName -> { code } (the SKU code), and
 * `deliveryTypeData` is the rush-availability response ([{ skuId, rushAvailable }])
 * from getRushPDPData. A size is rush-eligible when a deliveryTypeData entry's
 * skuId matches that size's code and carries rushAvailable.
 */
export function getRushOptionSizesList(sizes, sizeDataMap, deliveryTypeData) {
  if (!Array.isArray(sizes) || !Array.isArray(deliveryTypeData)) return '';
  return sizes
    .filter(({ value }) => {
      const sizeCode = Number(sizeDataMap?.[value]?.code);
      return deliveryTypeData.some(
        item => Number(item?.skuId) === sizeCode && item?.rushAvailable
      );
    })
    .map(({ value }) => value)
    .join(',');
}

const SLA_METHOD_LABEL = {
  RUSH: 'Rush',
  PRIORITY: 'Priority',
  STANDARD: 'Standard'
};

/**
 * Map raw scopedView SLA options → seller_options array (one object per card).
 * Shape matches Amplitude Object schema:
 * { price_group_id, seller_option_type, product_list_price, delivery_type,
 *   delivery_edd, product_td }
 */
function mapSlaOptionsToSellerOptions(slaOptions) {
  const options = Array.isArray(slaOptions) ? slaOptions : [];
  const firstDetail = opt => opt?.serviceabilityDetails?.productDetails?.[0] || {};
  const methodOf = opt => String(firstDetail(opt)?.deliveryMethod || '').toUpperCase();
  const deliveryTypeOf = opt => {
    const detail = firstDetail(opt);
    const method = SLA_METHOD_LABEL[methodOf(opt)] || detail?.deliveryMethod || '';
    const sla = detail?.deliverySLA;
    return method && sla && methodOf(opt) !== 'STANDARD' ? `${method}|${sla}` : method;
  };
  const toInt = value => {
    if (value == null || value === '') return null;
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
  };
  // "50% off" → 50 when discountValue is missing.
  const toTd = priceData => {
    const fromValue = toInt(priceData?.discountValue);
    if (fromValue != null) return fromValue;
    const percent = String(priceData?.discountPercent || '').match(/(\d+)/);
    return percent ? toInt(percent[1]) : null;
  };

  return options.map(opt => {
    const priceData = opt?.price?.priceData || {};
    return {
      price_group_id: opt?.priceGroupId ?? null,
      seller_option_type: opt?.priceType ?? null,
      product_list_price: toInt(priceData.value),
      delivery_type: deliveryTypeOf(opt) || null,
      delivery_edd: firstDetail(opt)?.eddUpper ?? null,
      product_td: toTd(priceData)
    };
  });
}

/**
 * True when any size under the option has multi-seller (2+ SLA cards).
 * Prefers all SKUs cached for the pincode; falls back to the current size's
 * slaOptions when that cache isn't available yet.
 */
function hasMultiSellerEnabledSize(slaOptions, slaDetailsBySku) {
  const skuEntries =
    slaDetailsBySku && typeof slaDetailsBySku === 'object'
      ? Object.values(slaDetailsBySku)
      : [];
  if (skuEntries.length) {
    return skuEntries.some(
      entry => Array.isArray(entry?.options) && entry.options.length > 1
    );
  }
  return Array.isArray(slaOptions) && slaOptions.length > 1;
}

/**
 * Multiseller enrichment for the "Delivery And Return Details Viewed" event
 * (PDP delivery & return section). Returns the extra props to merge into the
 * existing event when the flag is on:
 *  - top level: pin_code, delivery_information_text, previous_screen_name, seller_options
 *  - products[0]: is_multi_seller_enabled_size (boolean — true if any size of
 *    the option has multi-seller), size_selected
 * seller_options is an array of objects (one per delivery SLA card):
 * { price_group_id, seller_option_type, product_list_price, delivery_type,
 *   delivery_edd, product_td }.
 */
export function getDeliveryReturnDetailsViewedProps({
  slaOptions,
  pincode,
  sizeSelected,
  deliveryInfoText,
  previousScreenName = 'pdp screen',
  slaDetailsBySku
}) {
  const sellerOptions = mapSlaOptionsToSellerOptions(slaOptions);
  return {
    top: {
      pin_code: pincode ?? null,
      delivery_information_text: deliveryInfoText ?? null,
      previous_screen_name: previousScreenName,
      seller_options: sellerOptions
    },
    product: {
      is_multi_seller_enabled_size: hasMultiSellerEnabledSize(
        slaOptions,
        slaDetailsBySku
      ),
      size_selected: sizeSelected ?? null
    }
  };
}

/**
 * Multiseller enrichment for the "Select Size Clicked" event. Derives the
 * product-level flags and `seller_options` (one object per delivery SLA card)
 * from the scopedView (v2) SLA option array for the just-selected SKU.
 *
 * Each option is a RawSlaOption: { priceType, priceGroupId, isSelected,
 * price.priceData.{ value, discountValue }, serviceabilityDetails.productDetails[0].{
 * deliveryMethod, deliverySLA, eddUpper } }. delivery_type is "Rush|1HR" /
 * "Priority|2DD" for express modes and just "Standard" for standard delivery.
 * product_td is the integer discountValue (not the "50% off" string).
 */
export function getSelectSizeSlaEventProps(slaOptions) {
  const options = Array.isArray(slaOptions) ? slaOptions : [];
  const sellerOptions = mapSlaOptionsToSellerOptions(options);
  // scopedView failed / returned nothing: still emit the params so the event
  // shape is stable — booleans false, seller_options empty array.
  if (!options.length) {
    return {
      productProps: {
        rush_option_enabled_size: false,
        is_priority_option_enabled_size: false,
        is_multi_enabled_size_flag: false,
        price_group_id: null
      },
      sellerOptions: []
    };
  }

  const firstDetail = opt => opt?.serviceabilityDetails?.productDetails?.[0] || {};
  const methodOf = opt => String(firstDetail(opt)?.deliveryMethod || '').toUpperCase();
  const hasRush = options.some(opt => methodOf(opt) === 'RUSH');
  const hasPriority = options.some(opt => methodOf(opt) === 'PRIORITY');
  const selected = options.find(opt => opt?.isSelected) || options[0] || {};

  return {
    productProps: {
      rush_option_enabled_size: hasRush,
      is_priority_option_enabled_size: hasPriority,
      is_multi_enabled_size_flag: options.length > 1,
      price_group_id: selected?.priceGroupId ?? ''
    },
    sellerOptions
  };
}

/** "Rush|1HR" / "Priority|2DD" for express modes; plain "Standard" for standard. */
function getSlaDeliveryType(option) {
  const detail = option?.serviceabilityDetails?.productDetails?.[0] || {};
  const raw = String(detail?.deliveryMethod || '').toUpperCase();
  const method = SLA_METHOD_LABEL[raw] || detail?.deliveryMethod || '';
  const sla = detail?.deliverySLA;
  return method && sla && raw !== 'STANDARD' ? `${method}|${sla}` : method;
}

/**
 * Amplitude / UI seller option key: BEST_PRICE → best, BEST_SPEED → fastest.
 */
function normalizeSellerPriceType(priceType) {
  const key = String(priceType || '')
    .trim()
    .toUpperCase()
    .replace(/-/g, '_');
  if (key === 'BEST_SPEED' || key === 'FASTEST') return 'fastest';
  if (key === 'BEST_PRICE' || key === 'BEST') return 'best';
  const lower = String(priceType || '')
    .trim()
    .toLowerCase();
  return lower || null;
}

/**
 * Selected-option props for the "Add To Bag Clicked" event. Picks the option
 * matching `selectedPriceType` (falling back to the API-preselected / first
 * option / `selectedOption`) and returns its seller_option_selected /
 * delivery_type / price_group_id. Values are null when there is no SLA option
 * for the size. seller_option_selected is always the Amplitude key
 * (`best` / `fastest`).
 */
export function getSelectedSellerOptionProps(
  slaOptions,
  selectedPriceType,
  selectedOption = null
) {
  const options = Array.isArray(slaOptions) ? slaOptions : [];
  const wanted = normalizeSellerPriceType(
    selectedPriceType || selectedOption?.priceType
  );
  const selected =
    (selectedOption &&
      (!wanted || normalizeSellerPriceType(selectedOption.priceType) === wanted) &&
      selectedOption) ||
    (wanted &&
      options.find(opt => normalizeSellerPriceType(opt?.priceType) === wanted)) ||
    options.find(opt => opt?.isSelected) ||
    options[0] ||
    selectedOption ||
    null;
  return {
    seller_option_selected: selected
      ? normalizeSellerPriceType(selected.priceType) || wanted
      : wanted || null,
    delivery_type: selected ? getSlaDeliveryType(selected) || null : null,
    price_group_id: selected?.priceGroupId ?? null
  };
}

/**
 * Multiseller enrichment for the "Bag Details Viewed" event — per cart item.
 * The cart response carries the SELECTED option flat on the item
 * (selectedPriceType + servicabilityInfo.{shippingAction,deliverySLA}) and the
 * alternatives in `unSelectedPrice[]`. seller_options_shown is the alternate
 * option CTA: "Get it in 90 Mins" when best is selected, else "Get in cheapest price".
 */
/**
 * "Rush|1HR" / "Priority|2DD" / "Standard" from a cart item's SELECTED-option
 * servicabilityInfo (flat shippingAction + deliverySLA).
 */
function getCartDeliveryType(item = {}) {
  const svc = item?.servicabilityInfo || {};
  const rawMethod = String(svc?.shippingAction || '').toUpperCase();
  const method = SLA_METHOD_LABEL[rawMethod] || svc?.shippingAction || '';
  const sla = svc?.deliverySLA;
  return method && sla && rawMethod !== 'STANDARD' ? `${method}|${sla}` : method || null;
}

/**
 * seller_options_shown for cart analytics — CTA of the alternate option:
 *  - selected 'best'    → fastDeliveryTimeMessage[unselected.deliverySLA] e.g. "Get it in 90 Mins"
 *  - otherwise          → "Get in cheapest price"
 * Returns null when no alternate option is available.
 */
function getCartSellerOptionsShownFromUi(item = {}, config = {}) {
  const selectedPriceType = String(item?.selectedPriceType || '')
    .trim()
    .toLowerCase();
  const unselected = Array.isArray(item?.unSelectedPrice) ? item.unSelectedPrice : [];
  if (!unselected.length) return null;

  if (selectedPriceType === 'best') {
    const { fastDeliveryTimeMessage = {} } = config || {};
    const { deliverySLA = '' } = unselected[0] || {};
    return fastDeliveryTimeMessage[deliverySLA] || null;
  }

  return 'Get in cheapest price';
}

export function getCartItemSellerOptionProps(item = {}) {
  const config = getMultiSellerSlaConfig(window?.__PRELOADED_STATE__) || {};
  const selectedPriceType = item?.selectedPriceType || null;

  return {
    seller_option_selected: selectedPriceType,
    // Cart response may carry priceGroupId on the entry.
    price_group_id: item?.priceGroupId ?? null,
    delivery_type: getCartDeliveryType(item),
    seller_options_shown: getCartSellerOptionsShownFromUi(item, config)
  };
}

/**
 * Multiseller props for the "Proceed to Payment Clicked" event — per cart item:
 * seller_option_selected (= selectedPriceType), delivery_type (selected option),
 * price_group_id (the entry's, once `setCartItem` carries it through).
 */
export function getCartItemDeliveryProps(item = {}) {
  return {
    seller_option_selected: item?.selectedPriceType || null,
    delivery_type: getCartDeliveryType(item),
    price_group_id: item?.priceGroupId ?? null
  };
}

/**
 * Fires "Seller Option Clicked" — the cart "Faster delivery / Better price"
 * strip (cart-pricetype-strip) tap. The clicked option is the promoted
 * alternative (unSelectedPrice[0]); delivery_type/seller_option_clicked come
 * from it, seller_options_shown lists all option types shown. Product fields
 * mirror getProductItemForCart's cart-item mapping. hit_number / screen_name /
 * previous_screen_name are auto-added by segmentDatalayerPushData.
 */
export function gtmSellerOptionClicked({
  item = {},
  size = '',
  storeType = '',
  cartId = '',
  screenName = ''
}) {
  if (segmentTrackingEnabled !== 'ON') return;
  const selectedPriceType = item?.selectedPriceType || null;
  const unselected = Array.isArray(item?.unSelectedPrice) ? item.unSelectedPrice : [];
  const clicked = unselected[0] || {};
  const rawMethod = String(clicked?.shippingAction || '').toUpperCase();
  const method = SLA_METHOD_LABEL[rawMethod] || clicked?.shippingAction || '';
  const sla = clicked?.deliverySLA;
  const deliveryType =
    method && sla && rawMethod !== 'STANDARD' ? `${method}|${sla}` : method || null;
  const config = getMultiSellerSlaConfig(window?.__PRELOADED_STATE__) || {};
  const quantity = Number(item?.quantity) || 1;
  const listPrice = item?.priceInfo?.netPrice?.value ?? item?.price;
  const offerPrice =
    item?.priceInfo?.specialPrice?.value ?? item?.priceInfo?.netPrice?.value ?? item?.price;
  segmentDatalayerPushData(
    {
      event: 'Seller Option Clicked',
      store_type: storeType,
      ...(screenName && { screen_name: screenName }),
      cart_id: cartId || readCookie('C') || '',
      products: [
        {
          seller_option_clicked: clicked?.selectedPriceType || null,
          delivery_type: deliveryType,
          seller_options_shown: getCartSellerOptionsShownFromUi(item, config),
          price_group_id: item?.priceGroupId ?? null,
          product_id: item?.baseProduct || item?.code,
          product_name: item?.name,
          product_brick: item?.brickSubCategory,
          product_segment: item?.brickCategory,
          product_vertical: item?.brickName,
          product_mrp: Number(item?.wasPriceData?.value) || 0,
          product_list_price: Number(listPrice) / quantity || 0,
          product_offer_price: Number(offerPrice) / quantity || 0,
          size_of_product: size || item?.size
        }
      ]
    },
    'cart'
  );
}

/**
 * Fires "Add To Bag Clicked" for the wishlist add-to-bag flow (msite
 * SizeSelectionPopup / desktop addToBagFromWishlist). The non-luxe wishlist path
 * never called gtmAddToCart, so this event was missing there; luxe already fires
 * it via luxeGtmAddToCart. Product identity comes from the wishlist `entity`
 * (via getProductItemPDP); multiseller seller-option props are added when the
 * flag is on. Uses the same segmentDatalayerPushData pipeline as gtmAddToCart so
 * the "Add To Bag Clicked" enrichment (fbp/fbc/gclid) still applies.
 */
export function gtmWishlistAddToBagClicked({
  entity = {},
  size = '',
  quantity = 1,
  screenName = '',
  previousScreenName = 'wishlist screen',
  enableMultiSellerSla = false,
  slaOptions = null,
  selectedPriceType = '',
  selectedSlaOption = null
}) {
  if (segmentTrackingEnabled !== 'ON') return;
  segmentDatalayerPushData(
    {
      event: 'Add To Bag Clicked',
      event_category: 'enhance ecommerce',
      event_action: 'add to cart',
      add_to_bag_source: screenName || previousScreenName,
      screen_name: screenName || previousScreenName,
      screen_source: previousScreenName,
      ...(enableMultiSellerSla && { previous_screen_name: previousScreenName }),
      products: [
        {
          ...getProductItemPDP(entity),
          size_of_product: size,
          ...(quantity ? { quantity } : {}),
          ...(enableMultiSellerSla
            ? getSelectedSellerOptionProps(slaOptions, selectedPriceType, selectedSlaOption)
            : {})
        }
      ]
    },
    'ee_add_to_cart'
  );
}

function getNonReturnableReason(product) {
  const isReturnable = product.prodData?.isReturnable ?? product.isReturnable;
  const isCatalogFraudReturnable =
    product.prodData?.isCatalogFraudReturnable ?? product.isCatalogFraudReturnable;
  if (isReturnable) return 'NA';
  return isCatalogFraudReturnable ? 'Coupon Applied' : 'Product Policy';
}

function getNonExchangableReason(product, isGSTSelected) {
  const isExchangeable = product.prodData?.isExchangeable ?? product.isExchangeable;
  const isCatalogFraudExchangeable = product.prodData?.isCatalogFraudExchangeable ?? product.isCatalogFraudExchangeable;
  const returnWindow = product.prodData?.returnWindow ?? product.returnWindow ?? 0;
  const gstBlocksExchange = isGSTSelected && isExchangeable && returnWindow > 0;
  if (gstBlocksExchange) return 'GST';
  if (!isExchangeable) return isCatalogFraudExchangeable ? 'Coupon Applied' : 'Product Policy';
  return 'NA';
}

function toPurchaseFee(value) {
  if (value && typeof value === 'object' && value.value != null) {
    const nested = Number(value.value);
    return Number.isFinite(nested) ? nested : 0;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function toPurchaseEddDate(value) {
  if (value == null || value === '') return '';
  const str = String(value).trim();
  if (!str) return '';
  const iso = str.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2}:\d{2})/);
  if (iso) return `${iso[1]}T${iso[2]}`;
  const ymd = str.match(/^(\d{4})(\d{2})(\d{2})$/);
  if (ymd) return `${ymd[1]}-${ymd[2]}-${ymd[3]}T00:00:00`;
  const d = new Date(str);
  if (Number.isNaN(d.getTime())) return '';
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T00:00:00`;
}

function getPurchasedProductExtraProps({
  product = {},
  purchaseProductMatch = {},
  appliedCode = '',
  crossSellEntry,
  crossSellWidgetTitle
}) {
  const sku = product.code || product.skuId || purchaseProductMatch.skuId;
  const orderState = getAppStateForAnalytics()?.order || {};
  const orderItem = (orderState.item || []).find(i => i?.code === sku) || {};
  const orderEntry = (orderState.entries || []).find(e => e?.product?.code === sku) || {};
  const servicabilityInfo =
    product.prodData?.servicabilityInfo ||
    product.servicabilityInfo ||
    orderItem.servicabilityInfo ||
    {};
  const deliveryMethod = String(
    product.deliveryMethod ||
      product.prodData?.deliveryMethod ||
      orderEntry.deliveryMethod ||
      ''
  ).toUpperCase();
  const priorityDelivery = servicabilityInfo.priorityDelivery || {};
  const rushDelivery = servicabilityInfo.rushDelivery || {};
  const eddUpper = product.eddUpper || orderItem.eddUpper;
  const eddLower = product.eddLower || orderItem.eddLower;
  const couponFromMatch =
    typeof purchaseProductMatch.coupon === 'string' ? purchaseProductMatch.coupon : '';
  const couponFromProduct =
    typeof product.coupon === 'string' ? product.coupon : '';
  const itmSource =
    crossSellEntry || crossSellWidgetTitle
      ? 'widget'
      : purchaseProductMatch.itm_source || getItmSourceParams().itm_source || '';
  const supercash = Number(
    product.superCashData?.product_supercash_earn ??
      product.prodData?.superCashData?.product_supercash_earn ??
      orderItem.superCashData?.product_supercash_earn ??
      0
  );
  const priorityFee = toPurchaseFee(
    purchaseProductMatch.priorityDeliveryFee ??
      priorityDelivery.fee?.value ??
      product.convenienceFee?.priorityDelivery?.netAmount ??
      orderEntry.convenienceFee?.priorityDelivery?.netAmount
  );
  const rushFee = toPurchaseFee(
    purchaseProductMatch.rushDeliveryFee ??
      rushDelivery.fee?.value ??
      product.convenienceFee?.rushDelivery?.netAmount ??
      orderEntry.convenienceFee?.rushDelivery?.netAmount
  );
  const priorityApplied =
    !!priorityDelivery.isApplied || deliveryMethod === 'PRIORITY' || priorityFee > 0;
  const rushApplied = !!rushDelivery.isApplied || deliveryMethod === 'RUSH' || rushFee > 0;

  return {
    coupon_name:
      couponFromMatch ||
      product.offers?.appliedVouchers?.[0]?.code ||
      orderItem.offers?.appliedVouchers?.[0]?.code ||
      couponFromProduct ||
      appliedCode ||
      '',
    standard_delivery_edd: toPurchaseEddDate(
      servicabilityInfo.promiseShippingDate || servicabilityInfo.eddUpper || eddUpper || eddLower
    ),
    priority_delivery_edd: priorityApplied
      ? toPurchaseEddDate(priorityDelivery.deliveryDate || eddUpper || eddLower)
      : '',
    priority_delivery_fees: priorityFee,
    rush_delivery_edd: rushApplied
      ? toPurchaseEddDate(rushDelivery.deliveryDate || eddUpper || eddLower)
      : '',
    rush_delivery_fees: rushFee,
    product_supercash_earned: Number.isFinite(supercash) ? supercash : 0,
    product_tag:
      product.sticker_name ||
      product.prodData?.productTag ||
      product.productTag ||
      orderItem.productTag ||
      (product.exclusiveTill || orderItem.exclusiveTill ? 'Exclusive' : '') ||
      product.dimension15 ||
      '',
    itm_source: itmSource || '',
    cross_sell_type:
      crossSellEntry?.tabName ||
      purchaseProductMatch.product_cross_sell_type ||
      crossSellWidgetTitle ||
      ''
  };
}

export function getProductItemForCart(product, ProductArray, token){
  // console.log("cart product", product);

  //some scenarios has productData
  if(!product.prodData && product.productData){
     product.prodData = product.productData;
     delete product.productData;
  }

  const servicabilityInfo = product.prodData ? product.prodData?.servicabilityInfo : product.servicabilityInfo;
  const couponSavings = product.prodData ? product.prodData?.priceInfo?.couponSaving?.value : product.priceInfo?.couponSaving?.value;
  const productMrp = product.prodData ? product.prodData?.wasPriceData?.value : product.wasPriceData?.value;
  const productWithAttributeKey = (ProductArray || [])?.find(el => el?.skuId === product?.code) || {};
  let rushPdData = ProductArray && Array.isArray(ProductArray) && ProductArray.length>0 && ProductArray.filter((item) => item?.skuId === product?.code);  
  const plaItem = getPlaItemByProductCode(product?.code);
  const plaConfigClientId = getPlaAdsClientID(window?.__PRELOADED_STATE__)
  const product_list_price = product.prodData?.priceInfo?.netPrice?.value ?? product?.priceInfo?.netPrice?.value ?? product?.price;
  const baseProduct = product.prodData?.baseProduct || product.baseProduct;
  const productId = baseProduct ? baseProduct : product.id && product.variant ? `${product.id}_${product.variant}` : product.code && product.color ? `${product.code}_${product.color}` : product.id;
  // Both halves are defaulted: cart entries frequently carry no deliveryMethod, which
  // was producing the literal string "undefined | STANDARD".
  const deliveryType = `${product.prodData?.deliveryMethod || product.deliveryMethod || 'STANDARD'} | ${servicabilityInfo?.shippingAction || product.prodData?.deliverySLA || product.deliverySLA || 'NA'}`
  const isCompanion = isCompanionProduct(product?.prodData?.code || product?.code)
  // Priority / rush delivery live on the enriched GA product for some callers and
  // only on the cart entry's servicabilityInfo for others (the desktop cart and the
  // mSite bag-update payload build their product arrays without them).
  const rushPdEntry = (Array.isArray(rushPdData) && rushPdData[0]) || {};
  const priorityDeliveryEDD =
    product.priorityDeliveryEDD ||
    rushPdEntry.priorityDeliveryEDD ||
    servicabilityInfo?.priorityDelivery?.deliveryDate ||
    '';
  const priorityDeliveryFee =
    product.priorityDeliveryFee ??
    rushPdEntry.priorityDeliveryFee ??
    servicabilityInfo?.priorityDelivery?.fee?.value ??
    0;
  const rushDeliveryEDD =
    product.rushDeliveryEDD ||
    rushPdEntry.rushDeliveryEDD ||
    servicabilityInfo?.rushDelivery?.deliveryDate ||
    '';
  const rushDeliveryFee =
    product.rushDeliveryFee ??
    rushPdEntry.rushDeliveryFee ??
    servicabilityInfo?.rushDelivery?.fee?.value ??
    0;
  return {
          product_id: productId,
          product_name: product.name,
          //discount: Number(product.discount) / (Number(product.quantity) || 1) || 0,
          product_poistion: Number(product.position) || 0,
          //product_brand: product.brand,
          //item_list_name: 'bag screen',
          //product_quantity: (Number(product.quantity) || 1),
          product_brick: product.prodData ? product.prodData?.brickSubCategory : product.brickSubCategory,
          product_segment: product.prodData ? product.prodData?.brickCategory : product.brickCategory,
          product_vertical: product.prodData? product.prodData?.brickName : product.brickName,
          //item_variant: product.variant,
          product_list_price: Number(product_list_price) / (Number(product.quantity) || 1) || 0,        
          product_offer_price: Number(product.prodData?.priceInfo?.specialPrice?.value ?? product.prodData?.priceInfo?.netPrice?.value ?? product.price) / (Number(product.quantity) || 1) || 0,
          product_brand : product.brand,  
          product_size: product.size,          
          product_quantity: Number(product.quantity) || 1,
          coupon_name: product.coupon || product.prodData?.offers?.appliedVouchers?.[0]?.code || '',
          product_rating: product.averageRating ?? product.prodData?.averageRating ?? '',
        // available_quantity_deliverable_non_deliverable: product.dimension59,
          //segment_id_experiment_id: product.dimension60,
          //delivery_tag: product.dimension61,
          //delivery_edd: product.dimension66,
          product_source_store: isCompanion ? 'Companion App' : product.dimension23,
          store_name: isCompanion?.tenant_id ? isCompanion?.tenant_id : null,
          //offer_name: containsLuxe ? product.dimension82 : product.dimension166,
          product_mrp: Number(productMrp) || 0,
          ...(product.priorityDeliveryFee && {
            prioirty_delivery_fee : Number(product.priorityDeliveryFee).toFixed(1)
          }),
          //...(product.sticker_name && { sticker_name: product.sticker_name }),
          ...(product.priorityDeliveryEDD && {
            prioirty_delivery_edd : product.priorityDeliveryEDD
          }),
          product_gift_with_purchase_flag:
            servicabilityInfo?.isGiftProduct ||
            Number(product?.prodData?.giftQuantity || product?.giftQuantity || product?.giftProductsCount || 0) > 0,
          product_oos_flag: (product.dimension59 == 0 ),
          product_return_exhange_window: product.prodData?.returnWindow ?? product.returnWindow ?? 0,
          product_return_window: product.prodData?.returnWindow ?? product.returnWindow ?? 0,
          product_exchange_window: product.prodData?.returnWindow ?? product.returnWindow ?? 0,
          product_servicability:servicabilityInfo?.servicability,
          product_supercash_earned: Number(product.superCashData?.product_supercash_earn || 0),
          delivery_type: deliveryType,
          delivery_type_selected: ['PRIORITY', 'RUSH'].includes(product.prodData?.deliveryMethod || product.deliveryMethod),
          standard_delivery_edd: servicabilityInfo?.promiseShippingDate,
          catalogue_id: product.prodData?.catalogName || product.catalogName,
          product_coupon_savings: Number(couponSavings) || 0,
          product_tag: product.sticker_name || product.prodData?.productTag || product.productTag,  
          is_subscription: product?.isMembershipArticle,
          // ...(product.priority_delivery_sla_tag && {
          //   priority_delivery_sla_tag: product.priority_delivery_sla_tag
          // })
          ...((product.pincode || ProductArray && ProductArray[0]?.pincode) && { pincode: product.pincode || ProductArray && ProductArray[0]?.pincode }),
          ...((product.priorityDeliveryFee || rushPdData && Array.isArray(rushPdData) && rushPdData?.[0]?.priorityDeliveryFee) && {
            product_priority_delivery_fee: Number(product.priorityDeliveryFee || rushPdData && Array.isArray(rushPdData) && rushPdData?.[0]?.priorityDeliveryFee).toFixed(1)
          }),
          ...((product.priorityDeliveryEDD || rushPdData && Array.isArray(rushPdData) && rushPdData?.[0]?.priorityDeliveryEDD) && {
            priority_delivery_EDD: product.priorityDeliveryEDD || rushPdData && Array.isArray(rushPdData) && rushPdData?.[0]?.priorityDeliveryEDD
          }),
          ...((product.priority_delivery_sla_tag || rushPdData && Array.isArray(rushPdData) && rushPdData?.[0]?.priority_delivery_sla_tag) && {
            priority_delivery_sla_tag: product.priority_delivery_sla_tag || rushPdData && Array.isArray(rushPdData) && rushPdData?.[0]?.priority_delivery_sla_tag
          }),
          ...((product.rushDeliveryFee || rushPdData && Array.isArray(rushPdData) && rushPdData?.[0]?.rushDeliveryFee) && {
            product_rush_delivery_fee: Number(product.rushDeliveryFee || rushPdData && Array.isArray(rushPdData) && rushPdData?.[0]?.rushDeliveryFee).toFixed(1)
          }),
          ...((product.rushDeliveryEDD || rushPdData && Array.isArray(rushPdData) && rushPdData?.[0]?.rushDeliveryEDD) && {
            rush_delivery_EDD: product.rushDeliveryEDD || rushPdData && Array.isArray(rushPdData) && rushPdData?.[0]?.rushDeliveryEDD
          }),
          ...((product.rush_delivery_sla_tag || rushPdData && Array.isArray(rushPdData) && rushPdData?.[0]?.rush_delivery_sla_tag) && {
            rush_delivery_sla_tag: product.rush_delivery_sla_tag || rushPdData && Array.isArray(rushPdData) && rushPdData?.[0]?.rush_delivery_sla_tag
          }),
          ...((productWithAttributeKey.search_attribution || token) && {
            attribution_token: productWithAttributeKey.search_attribution || token
          }),
          ...(product.hasOwnProperty('is_gst_eligible') && {
            is_gst_eligible: product.is_gst_eligible
          }),
          // The cart product arrays carry the sku only on the nested cart entry,
          // so `product.code` alone left sku_id empty on the bag events.
          sku_id: product?.code || product?.prodData?.code || productWithAttributeKey?.skuId || '',
          // Spec-named priority / rush delivery attributes. The keys above
          // (priority_delivery_EDD, product_priority_delivery_fee, ...) are kept for
          // the existing GA consumers; these are the names the Amplitude taxonomy
          // expects, and they are always present so the fee reads as 0 rather than
          // missing when the option does not apply to the item.
          // NA rather than '' when the item has no priority / rush option: an empty
          // string reports as an unfilled property, and NA is the sentinel this
          // codebase already uses for a genuinely absent value.
          priority_delivery_edd: priorityDeliveryEDD || 'NA',
          priority_delivery_fees: Number(priorityDeliveryFee) || 0,
          rush_delivery_edd: rushDeliveryEDD || 'NA',
          rush_delivery_fees: Number(rushDeliveryFee) || 0,
          // Attribution source of the item. Falls back to NA so the property is
          // reported even for items added without a tracked source.
          itm_source: product?.itm_source || productWithAttributeKey?.itm_source || 'NA',
          itm_source_detail:
            product?.itm_source_detail || productWithAttributeKey?.itm_source_detail || 'NA',
          ...(plaItem?.productCode && {
                sku_id: plaItem?.productCode,
                seller_id: plaItem?.seller_id,
                client_id: plaItem?.client_id || plaConfigClientId || ""
          })
        };
}

export function getCartItemProperty(cart, isAssuredGifts, internalWallets){
  //console.log("Wallet ", internalWallets);

  let wallet_cash_balance = 0;
  let wallet_points_applicable = 0;
  let wallet_points_balance = 0;
  let wallet_points_used = 0;

  let rone_balance=0;
  let rone_balance_used = 0;
  let rone_points_applicable = 0;

  // Callers pass different shapes here: the mSite CartPage passes its own props
  // (cart fields spread at the top level, cart id under `cartId`), the desktop
  // cart passes either `this.props` or `this.props.cart`. Resolve the id from all
  // of them, falling back to the cart cookie, so cart_id is never dropped.
  const resolvedCartId = cart?.id || cart?.cartId || cart?.cart?.id || readCookie('C') || '';

  // Priority delivery is charged per item, so the bag-level fee is the sum over
  // the cart entries. Different callers expose the entries as `cartItem`,
  // `entries` or `item`.
  const cartEntries =
    (Array.isArray(cart?.cartItem) && cart.cartItem) ||
    (Array.isArray(cart?.entries) && cart.entries) ||
    (Array.isArray(cart?.item) && cart.item) ||
    [];
  const priority_delivery_fee = cartEntries.reduce((total, entry) => {
    const fee = Number(entry?.servicabilityInfo?.priorityDelivery?.fee?.value);
    return total + (Number.isNaN(fee) ? 0 : fee);
  }, 0);

  // RVP (reverse pickup) is the return processing fee shown in the order summary.
  const return_processing_fee = Number(
    cart?.orderSummary?.convenienceFee?.RVP?.netAmount?.value || 0
  );

  if(internalWallets && internalWallets.length > 0){
    //get wallet information
    let roneWallet = internalWallets.find(item => item.type == 'RELIANCE_ONE_WALLET');
    let cashWallet =
      internalWallets.find(item => item.name === 'AJIO_CASH_WALLET') ||
      internalWallets.find(item => item.type === 'AJIO_CASH_WALLET');
    let subWallets = cashWallet?.subWallets?.length > 0 ? cashWallet?.subWallets[0] : null;
    const pointsWallet =
      cashWallet?.subWallets?.find(w => w.type === 'AJIO_CASH_POINTS_WALLET') || null;

    rone_balance= roneWallet?.availableAmount;
    rone_balance_used = '';
    rone_points_applicable = roneWallet?.amount;      

    wallet_cash_balance = cashWallet?.availableAmount; 
    wallet_points_applicable = subWallets?.amount;
    wallet_points_balance = pointsWallet?.availableAmount ?? pointsWallet?.points ?? 0;
    wallet_points_used = pointsWallet?.amount ?? 0;
  }
 
  return {
        ...cart && cart?.totalUnitCount >0 && {
          cart_id : resolvedCartId,
          bag_total:  Number(cart?.orderSummary?.bagTotal?.value || 0),
          bag_assured_gift_flag : isAssuredGifts?.cartAssuredGifts === 'ON',
          // Declared as a boolean — an empty string reads as an unfilled property
          // in Amplitude. Callers that render the price-change callout pass the
          // real state through `bagPriceUpdateCallout`.
          bag_price_update_callout: Boolean(cart?.bagPriceUpdateCallout),
          bag_item_count : cart?.totalUnitCount || 0,
          is_bag_merged: cart?.restoredProducts ? true : false,
          bag_savings: Number(cart?.orderSummary?.bagDiscount?.value || 0),
          coupon_savings: Number(cart?.orderSummary?.couponSaving?.value || 0),
          order_total: Number(cart?.orderSummary?.orderTotal?.value || 0),
          platform_fee: Number(cart?.orderSummary?.convenienceFee?.total?.netAmount?.value || 0),
          total_savings: Number(cart?.orderSummary?.totalSaving?.value || 0),
          delivery_fee:  Number(cart?.orderSummary?.deliveryCharge?.value),
          priority_delivery_fee,
          return_processing_fee,
        },
        // Always reported: a shopper with no wallet still has 0 points
        // applicable, and omitting the property makes the field read as missing.
        wallet_points_applicable: Number(wallet_points_applicable) || 0,
        ...internalWallets && internalWallets.length >0 && {
          rone_balance: Number(rone_balance),
          //rone_balance_used: Number(rone_balance_used),
          rone_points_applicable: Number(rone_points_applicable),
          wallet_cash_balance: Number(wallet_cash_balance),
          wallet_points_applicable: Number(wallet_points_applicable),
          wallet_points_balance: Number(wallet_points_balance),
          wallet_points_used: Number(wallet_points_used)
        // delivery_type_selected: cart?.deliveryInfo?.deliveryModeValue,
        }
  }
}

function checkEntranceFlag(){
   if (document.referrer) {
      // Referrer exists
      if (!(document.referrer.includes("ajio.com") || document.referrer.includes("localhost"))) {
        return true;
      }else 
        return false;

    } else {
      // Referrer does not exist
      return true;
    }
}

function getPreviousScreenName(){

  if(document.referrer){
    const pageInfo = getPageInfo(document.referrer);
    return pageInfo?.pageName || pageInfo?.pageType;
  }
  return "";
}

export function getProductItemAjioGram(product, position){
  //console.log("getProductItemPDP ::", product);
  if(!product) return {}

  return {      
           
         //media_type: 'image',
          product_position: Number(position) || 0,
          product_brand: product.fnlColorVariantData?.brandName,              
          product_mrp: product.wasPriceData?.price ? product.wasPriceData?.price : product.wasPriceData?.value,
          product_list_price: Number(product.price?.value) || 0,
          product_offer_price: product.offerPrice?.price,               
          product_id: product.id,
          product_name: product.name,    
                           
      };
}

const INLINE_QUICK_FILTER_CODES = new Set(['tags', 'sla']);

function getAppStateForAnalytics() {
  if (typeof window === 'undefined') return {};
  try {
    return window.store?.getState?.() || window.__PRELOADED_STATE__ || {};
  } catch (e) {
    return window.__PRELOADED_STATE__ || {};
  }
}

function isBrandPlpScreen(pathname = '') {
  const path = (pathname || '').toLowerCase();
  if (path.includes('/b/') || path.includes('/fb/')) return false;
  if (typeof window === 'undefined') return false;
  try {
    return !!new URLSearchParams(window.location.search || '').get('brandPageCode');
  } catch (e) {
    return false;
  }
}

function isSearchPlpScreen(page_name, pathname = '', gaPageName = '') {
  if (isBrandPlpScreen(pathname)) return false;
  const path = (pathname || '').toLowerCase();
  if (page_name === 'search plp screen' || gaPageName === 'search plp screen') return true;
  return path.includes('/search/') || path.includes('/find/');
}

function isCurrentSearchPlpScreen() {
  if (typeof window === 'undefined') return false;
  const pathname = window.location.pathname || '';
  let gaPageName = '';
  try {
    gaPageName = getPageNameForGA(pathname, false, false)?.pageName || '';
  } catch (e) {
    gaPageName = '';
  }
  return isSearchPlpScreen('', pathname, gaPageName);
}

export function getSearchPlpRedirectionType() {
  if (typeof window === 'undefined') return 'search';
  const path = (window.location.pathname || '').toLowerCase();
  const brandPageCode = new URLSearchParams(window.location.search || '').get('brandPageCode');
  if (brandPageCode || path.includes('/b/') || path.includes('/fb/')) return 'brand';
  if (path.includes('/c/') || path.includes('/fc/')) return 'category';
  return 'search';
}

function getSearchPlpSearchTerm(fallback = '') {
  if (fallback) return fallback;
  if (typeof window === 'undefined') return '';
  const params = new URLSearchParams(window.location.search || '');
  const fromQuery = params.get('text') || params.get('query') || '';
  if (fromQuery) {
    try {
      return decodeURIComponent(fromQuery.replace(/\+/g, ' '));
    } catch (e) {
      return fromQuery;
    }
  }
  const match = (window.location.pathname || '').match(/\/(?:search|find)\/([^/?]+)/i);
  if (match) {
    try {
      return decodeURIComponent(match[1].replace(/\+/g, ' '));
    } catch (e) {
      return match[1];
    }
  }
  const state = getAppStateForAnalytics();
  return state?.request?.freeTextSearch || state?.products?.request?.freeTextSearch || '';
}

function getSearchPlpScreenTitle(searchTerm = '') {
  const state = getAppStateForAnalytics();
  const request = state?.request || state?.products?.request || {};
  const path = (typeof window !== 'undefined' ? window.location.pathname : '') || '';
  const lowerPath = path.toLowerCase();
  if (lowerPath.includes('/search/') || lowerPath.includes('/find/')) {
    return searchTerm || request.freeTextSearch || request.pageTitle || '';
  }
  const docTitle =
    typeof document !== 'undefined' ? String(document.title || '').split('|')[0].trim() : '';
  return request.pageTitle || request.categoryName || state?.products?.categoryName || searchTerm || docTitle || '';
}

function getListingProductMediaType(product = {}) {
  const videoValues = [
    product.videoURL,
    product.videoUrl,
    product.productVideoUrl,
    product.fnlColorVariantData?.videoURL,
    product.fnlColorVariantData?.videoUrl,
    product.media_type
  ];
  if (videoValues.some(value => String(value || '').toLowerCase().includes('video'))) {
    return 'video';
  }
  const images = product.images || product.fnlColorVariantData?.images || [];
  if (
    Array.isArray(images) &&
    images.some(img => {
      const blob = `${img?.format || ''} ${img?.type || ''} ${img?.mediaType || ''} ${img?.url || ''}`.toLowerCase();
      return blob.includes('video') || blob.includes('.mp4') || blob.includes('.webm');
    })
  ) {
    return 'video';
  }
  return 'Image';
}

function getListingProductOosFlag(product = {}) {
  const variants = product.variantOptions;
  if (Array.isArray(variants) && variants.length) {
    return !variants.some(variant => {
      const level = Number(variant?.stock?.stockLevel);
      const status = String(variant?.stock?.stockLevelStatus || '').toLowerCase();
      return level > 0 || status === 'instock' || status === 'lowstock';
    });
  }
  const status = String(
    product?.stock?.stockLevelStatus || product?.stockLevelStatus || ''
  ).toLowerCase();
  return status === 'outofstock';
}

function getListingProductAdId(product = {}) {
  return (
    product?.adsData?.ad ||
    product?.adsFlag?.ad ||
    product?.ad_id ||
    product?.campaignId ||
    product?.dimension161 ||
    product?.adsData?.config?.c ||
    product?.adsFlag?.config?.c ||
    ''
  );
}

function isCurrentPlpListingScreen() {
  if (typeof window === 'undefined') return false;
  if (isCurrentSearchPlpScreen() || isBrandPlpScreen()) return true;
  const path = (window.location.pathname || '').toLowerCase();
  return (
    path.includes('/search') ||
    path.includes('/find') ||
    path.includes('/c/') ||
    path.includes('/s/') ||
    path.includes('/b/') ||
    path.includes('/fb/') ||
    path.includes('/fc/')
  );
}

function getListingProductBrick(product = {}) {
  return product.brickNameText || product.brickSubCategory || product.dimension39 || '';
}

function getListingProductVertical(product = {}) {
  return product.verticalNameText || product.brickName || product.dimension38 || '';
}

function getListingProductSegment(product = {}) {
  return product.segmentNameText || product.brickCategory || product.dimension36 || '';
}

function getListingProductMrp(product = {}) {
  return (
    product.wasPriceData?.price ||
    product.wasPriceData?.value ||
    product.wasProductPrice ||
    ''
  );
}

function getListingOfferPrice(product = {}) {
  if (product.offerPrice && typeof product.offerPrice === 'object') {
    const value = product.offerPrice.price ?? product.offerPrice.value;
    return value != null && value !== '' ? Number(value) : '';
  }
  if (product.offerPrice != null && product.offerPrice !== '') {
    return Number(product.offerPrice) || product.offerPrice;
  }
  const promo = product.potentialPromotions?.[0]?.maxSavingPrice;
  if (promo != null && promo !== '') return Number(promo);
  return '';
}

function getListingProductRating(product = {}) {
  return (
    product.averageRating ||
    product.ratingsResponse?.aggregateRating?.averageRating ||
    (product.dimension46 ? String(product.dimension46).split('|')[0] : '') ||
    ''
  );
}

function getSimilarProductIconAvailableFlag() {
  try {
    return !!getShowSimilarProducts(getAppStateForAnalytics(), '', 'PLP');
  } catch (e) {
    return false;
  }
}

function getPlpProductItmSource(product = {}) {
  const fromWidget = getTrendWidgetItmSourceProps() || {};
  const fromUrl = getItmSourceParams() || {};
  return {
    itm_source: product.itm_source || fromWidget.itm_source || fromUrl.itm_source || '',
    itm_source_detail:
      product.itm_source_detail || fromWidget.itm_source_detail || fromUrl.itm_source_detail || ''
  };
}

function getPlpRawQuery(fallback = '') {
  if (typeof window === 'undefined') return fallback || '';
  const params = new URLSearchParams(window.location.search || '');
  return params.get('query') || params.get('text') || fallback || getSearchPlpSearchTerm() || '';
}

function getListingWishlistedCount(product = {}) {
  const value =
    product.product_wishlisted_count ??
    product.wishlistCount ??
    product.wishlistedCount ??
    product.fnlColorVariantData?.wishlistCount ??
    product.dimension167;
  const count = Number(value);
  return Number.isFinite(count) ? count : 0;
}

function getPlpCollectionPosition(position) {
  const state = getAppStateForAnalytics();
  const gridColumns = Number(state?.grid?.gridColumns);
  const columns = gridColumns > 0 ? gridColumns : 2;
  const index = Number(position);
  if (!Number.isFinite(index) || index < 0) return 1;
  return (index % columns) + 1;
}

function enrichSearchPlpProductEvent(obj = {}) {
  if (
    (obj.event !== 'Product Clicked' && obj.event !== 'Product Viewed') ||
    !isCurrentSearchPlpScreen()
  ) {
    return;
  }
  const searchTerm = getSearchPlpSearchTerm(obj.search_term);
  const redirectionType = getSearchPlpRedirectionType();
  obj.screen_name = 'search plp screen';
  obj.search_term = searchTerm || '';
  obj.screen_title = getSearchPlpScreenTitle(searchTerm) || obj.screen_title || '';
  if (!Array.isArray(obj.products)) return;
  obj.products = obj.products.map(product => {
    const listingEntity = getListingProductEntity(product);
    const validToken = getValidAttributionToken();
    const isProductClicked = obj.event === 'Product Clicked';
    return {
      ...product,
      media_type:
        String(product.media_type || '').toLowerCase() === 'video'
          ? 'video'
          : product.media_type === 'Image' || product.media_type === 'image'
            ? 'Image'
            : getListingProductMediaType(listingEntity),
      product_oos_flag:
        typeof product.product_oos_flag === 'boolean'
          ? product.product_oos_flag
          : getListingProductOosFlag(listingEntity),
      redirection_type: product.redirection_type || redirectionType,
      ...(isProductClicked && {
        ad_id: product.ad_id || getListingProductAdId(listingEntity),
        attribution_token: product.attribution_token || validToken || '',
        collection_position:
          Number(product.collection_position) > 0
            ? Number(product.collection_position)
            : getPlpCollectionPosition(product.product_position)
      })
    };
  });
}

function enrichPlpProductViewedItems(obj = {}) {
  const isClicked = obj.event === 'Product Clicked';
  const isViewed = obj.event === 'Product Viewed';
  if ((!isClicked && !isViewed) || !isCurrentPlpListingScreen()) return;

  if (isClicked) {
    const urlParams =
      typeof window !== 'undefined'
        ? new URLSearchParams(window.location.search || '')
        : new URLSearchParams();
    obj.raw_query = obj.raw_query || getPlpRawQuery(obj.search_term);
    obj.search_type = obj.search_type || getResolvedSearchType('', urlParams, obj) || '';
  }

  if (!Array.isArray(obj.products)) return;
  const similarFlag = getSimilarProductIconAvailableFlag();
  const validToken = getValidAttributionToken();
  const redirectionType = getSearchPlpRedirectionType();
  obj.products = obj.products.map(product => {
    const listingEntity = getListingProductEntity(product);
    const itmSourceProps = getPlpProductItmSource(product);
    return {
      ...product,
      ad_id: product.ad_id || getListingProductAdId(listingEntity),
      itm_source: itmSourceProps.itm_source,
      itm_source_detail: itmSourceProps.itm_source_detail,
      similar_product_icon_available_flag:
        typeof product.similar_product_icon_available_flag === 'boolean'
          ? product.similar_product_icon_available_flag
          : similarFlag,
      ...(isViewed && {
        product_brick: product.product_brick || getListingProductBrick(listingEntity),
        product_mrp: product.product_mrp || getListingProductMrp(listingEntity),
        product_offer_price: product.product_offer_price || getListingOfferPrice(listingEntity),
        product_rating: product.product_rating || getListingProductRating(listingEntity),
        product_segment: product.product_segment || getListingProductSegment(listingEntity),
        product_vertical: product.product_vertical || getListingProductVertical(listingEntity)
      }),
      ...(isClicked && {
        attribution_token: product.attribution_token || validToken || '',
        collection_position:
          Number(product.collection_position) > 0
            ? Number(product.collection_position)
            : getPlpCollectionPosition(product.product_position),
        product_wishlisted_count:
          product.product_wishlisted_count ?? getListingWishlistedCount(listingEntity),
        redirection_type: product.redirection_type || redirectionType
      })
    };
  });
}

function getListingProductEntity(product = {}) {
  const state = getAppStateForAnalytics();
  const entities = state?.grid?.entities || {};
  const keys = [product.code, product.id, product.product_id, product.dimension12].filter(Boolean);
  for (const key of keys) {
    if (entities[key]) return { ...entities[key], ...product };
  }
  const colorGroup =
    product.fnlColorVariantData?.colorGroup || product.dimension163 || product.colorGroup;
  if (colorGroup) {
    const match = Object.values(entities).find(
      entity =>
        entity?.fnlColorVariantData?.colorGroup === colorGroup ||
        entity?.code === colorGroup ||
        entity?.code === product.code
    );
    if (match) return { ...match, ...product };
  }
  return product;
}

// Search PLP Banner Clicked: banners.banner_position as "1|0", "1|1"
function getSearchPlpBannerPosition(promoPosition) {
  if (promoPosition === undefined || promoPosition === null || promoPosition === '') {
    return promoPosition;
  }
  const value = String(promoPosition);
  if (!isCurrentSearchPlpScreen()) return promoPosition;
  if (value.includes('|')) return value;
  if (value.includes('/')) return value.replace('/', '|');
  return `1|${value}`;
}

function mapSearchTypeFromSource(detail = '') {
  const value = String(detail || '').toLowerCase();
  if (value.includes('auto suggestion') || value === 'autosuggested') return 'Auto Suggestion';
  if (value.includes('history')) return 'History Search';
  if (value.includes('user completed')) return 'User Completed Search';
  return '';
}

function getResolvedSearchType(search_type, urlParams, otherOptions = {}) {
  const fromUrl = urlParams?.get('search_type') || urlParams?.get('type') || '';
  const fromSource = mapSearchTypeFromSource(
    otherOptions.plp_source_details || otherOptions.screen_source_detail || ''
  );
  return search_type || fromUrl || fromSource || '';
}

function getVisualFilterStoreConfig(appState = {}) {
  const visualFiltersConfig = getVisualFiltersConfig(appState) || {};
  const storeName = String(getStoreName() || 'ajio').toLowerCase();
  const storeConfig =
    visualFiltersConfig?.stores?.[storeName] || visualFiltersConfig?.stores?.ajio || {};
  return { visualFiltersConfig, storeConfig };
}

// One object per on-screen filter chip. Do not explode tags/sla values into extra objects,
// and do not mix in a filter row that the UI is not rendering.
function getVisualFilterStateFlag(visualFiltersState = '', filterName = '') {
  if (!visualFiltersState || !filterName) return '';
  const escaped = filterName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = visualFiltersState.match(new RegExp(`${escaped}:(FC|TC|T|F|D)`));
  return match ? match[1] : '';
}

function getVisibleVisualFilterCount(facetsState = {}, appState = {}) {
  const { storeConfig } = getVisualFilterStoreConfig(appState);
  if (!storeConfig.compoundFilterEnabled) return 0;
  const visualFilters = Array.isArray(facetsState.visualFilters) ? facetsState.visualFilters : [];
  return visualFilters.filter(filter => String(filter?.defaultDisplayName || '').trim()).length;
}

function getPlpScreenSourceDetail(otherOptions = {}, urlParams) {
  const fromOptions = otherOptions.screen_source_detail || otherOptions.plp_source_details;
  if (fromOptions && fromOptions !== false) return String(fromOptions);
  return (
    urlParams?.get('plp_source_details') ||
    urlParams?.get('itm_source_detail') ||
    ''
  );
}

function getDisplayedQuickFilters(facetsState = {}, appState = {}) {
  const { visualFiltersConfig, storeConfig } = getVisualFilterStoreConfig(appState);
  if (!storeConfig.quickFilters) return [];

  const quickFilterOrder = visualFiltersConfig.quickFilterOrder || [];
  const { quickFilters = [], plpGender = '', currentFacets = {} } = facetsState;
  const allfacetsItems = currentFacets.allfacetsItems || [];
  const facetCodesSet = new Set(allfacetsItems.map(item => item && item.code));
  const showGender = !(plpGender && String(plpGender).toLowerCase() !== 'general');
  const quickFilterMetaMap = {};
  quickFilterOrder.forEach(item => {
    if (item?.code) quickFilterMetaMap[item.code] = item;
  });

  const displayed = [];

  // SLA / tags render as individual chips — send those visible chip names, not "SLA"/"Tags".
  (quickFilters || []).forEach(apiFilter => {
    if (!INLINE_QUICK_FILTER_CODES.has(apiFilter.code)) return;
    if (!quickFilterMetaMap[apiFilter.code]) return;
    const matchingFacet = allfacetsItems.find(item => item.code === apiFilter.code);
    const values = Array.isArray(apiFilter.values) ? apiFilter.values : [];
    values.forEach(valueCode => {
      const facetValue = matchingFacet?.values?.find(v => v.code === valueCode);
      if (apiFilter.code !== 'sla' && !facetValue) return;
      const chipName = (facetValue?.name || valueCode || '').trim();
      if (!chipName) return;
      displayed.push({
        code: apiFilter.code,
        name: chipName
      });
    });
  });

  // Price, Gender, Brand, Category, etc. — only if that chip is actually rendered.
  quickFilterOrder.forEach(filterMeta => {
    const { code, name } = filterMeta || {};
    if (!code || INLINE_QUICK_FILTER_CODES.has(code)) return;
    if (code === 'allfilters' || code === 'sort') return;
    if (code === 'genderfilter' && !showGender) return;
    const hasFacet =
      facetCodesSet.has(code) || (code === 'pricerange' && facetCodesSet.has('price'));
    if (!hasFacet) return;
    const chipName = (name || '').trim();
    if (!chipName) return;
    displayed.push({
      code,
      name: chipName
    });
  });

  return displayed;
}

function buildSearchPlpFilterAnalytics(facetsState = {}, appState = {}) {
  const { storeConfig } = getVisualFilterStoreConfig(appState);
  const compoundFilterEnabled = !!storeConfig.compoundFilterEnabled;
  const {
    visualFilters = [],
    visualFiltersState = ''
  } = facetsState;
  const filters = [];

  if (compoundFilterEnabled) {
    const seenNames = new Set();
    visualFilters.forEach(filter => {
      const name = filter.defaultDisplayName || '';
      const trimmedName = name.trim();
      if (!trimmedName) return;
      const stateFlag = getVisualFilterStateFlag(visualFiltersState, name);
      if (stateFlag === 'D') return;
      const dedupeKey = trimmedName.toLowerCase();
      if (seenNames.has(dedupeKey)) return;
      seenNames.add(dedupeKey);
      filters.push({
        section: '0',
        position: String(seenNames.size),
        value: trimmedName,
        facet_key: trimmedName,
        facet_value: trimmedName,
        selection_state: stateFlag === 'T' || stateFlag === 'TC' ? 'selected' : 'non_selected',
        filterid: filter.filterId || ''
      });
    });
  }

  const displayedQuickFilters = getDisplayedQuickFilters(facetsState, appState);
  displayedQuickFilters.forEach((filter, index) => {
    filters.push({
      section: '1',
      position: String(index + 1),
      value: filter.name,
      facet_key: filter.code,
      facet_value: filter.name,
      selection_state: 'non_selected',
      filterid: ''
    });
  });

  return {
    filters,
    quick_filter_name: displayedQuickFilters.map(item => item.name).filter(Boolean).join(','),
    quick_filter_count: displayedQuickFilters.length,
    visual_filter_count: getVisibleVisualFilterCount(facetsState, appState)
  };
}

function getAdsEnabledScreenFlag(appState, pathname = '') {
  const path = (pathname || '').toLowerCase();
  const isSearchPath = path.includes('/search') || path.includes('/find');
  return isSearchPath ? !!getEnableAdsOnSlp(appState) : !!getEnableAdsOnPlp(appState);
}

export function segmentPageView(page_name, page_type, env , otherOptions , segmentProps){

    const eventName = 'pageload';
    const { store = 'AJIO', serviceName = '', ...restOtherOptions } = otherOptions;
    const { 
      cart= {}, 
      order={},
      isAssuredGifts='', 
      internalWallets = {}, 
      coachmark_impression= '', 
      search_term='', 
      search_type='', 
      quick_filter_count='',
      quick_filter_name='',
      ads_enabled_screen_flag='',
      best_coupon_name='',
      best_coupon_discount='',
      current_experience='',
      number_of_ratings_collected='',
      number_of_color_variant_avialable='',
      bag_primary_cta_name='',
      bag_primary_cta_state= '',        
      bag_secondary_cta_name= '',
      bag_secondary_cta_status= '',
      ajioWalletData={},
      search_expansion_type='',
    } = segmentProps;
    const resolvedSearchExpansionType = search_expansion_type || getSearchExpansionType();
    restOtherOptions.store_type = store;
    const storeName = getStoreName();

    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);   

    const copilotRedirectionValue = urlParams?.get('plp_source_details') ?? '';
    const isRedirectedFromCopilot = page_type === 'plp screen' && copilotRedirectionValue === 'ai_assistant_widget';

    // itm_source=widget on the URL marks a PLP/PDP landing that came from a widget,
    // so the widget attribution travels on the Screen/Page Viewed event.
    const WIDGET_ATTRIBUTED_PAGE_TYPES = ['plp screen', 'pdp screen', 'product page'];
    const { pageType: pageTypeFromPath } = getPageInfo(
      window.location.pathname || '',
      false,
      storeName.toLowerCase() === FLEEK_STORE.toLowerCase()
    );
    const itmSource = urlParams?.get('itm_source') ?? '';
    const isWidgetSourcedScreen =
      itmSource === 'widget' &&
      (WIDGET_ATTRIBUTED_PAGE_TYPES.includes(page_type) ||
        WIDGET_ATTRIBUTED_PAGE_TYPES.includes(pageTypeFromPath));
    const widgetSourceProps = isWidgetSourcedScreen
      ? {
          screen_source: itmSource,
          widget_name: urlParams?.get('itm_source_detail') || '',
          widget_id: urlParams?.get('widget_id') || urlParams?.get('widgetId') || ''
        }
      : {};

    let visualFilterCount = '';
    let visualFiltersArray = null;
    const pathname = window.location.pathname || '';
    const { pageName: gaPageName } = getPageNameForGA(
      pathname,
      false,
      storeName.toLowerCase() === FLEEK_STORE.toLowerCase()
    );
    const isSearchPlp = isSearchPlpScreen(page_name, pathname, gaPageName);
    const appState = getAppStateForAnalytics();
    const plpPageTypes = ['plp screen', 'fleek curated plp screen '];
    if (plpPageTypes.includes(page_type) || isSearchPlp) {
      const facetsState = appState.facets || window?.__PRELOADED_STATE__?.facets || {};
      const { visualFilters = [], quickFilters = [], visualFiltersState = '' } = facetsState;
      const quickFilterValuesCount = (quickFilters || []).reduce((count, filter) => {
        if (INLINE_QUICK_FILTER_CODES.has(filter?.code)) {
          return count + (Array.isArray(filter.values) ? filter.values.length : 0);
        }
        return count;
      }, 0);
      if (visualFilters.length > 0 || quickFilters.length > 0 || quickFilterValuesCount > 0) {
        visualFilterCount = `${visualFilters.length}|${quickFilterValuesCount}`;
        visualFiltersArray = [];
        visualFilters.forEach((filter, index) => {
          const isSelected = visualFiltersState.includes(`${filter.defaultDisplayName}:T`);
          visualFiltersArray.push({
            section: 0,
            position: index + 1,
            facet_key: filter.defaultDisplayName,
            facet_value: filter.defaultDisplayName,
            value: filter.defaultDisplayName,
            selection_state: isSelected ? 'selected' : 'non_selected',
            filterid: filter.filterId || ''
          });
        });
        const allfacetsItems = facetsState.currentFacets?.allfacetsItems || [];
        let qfPosition = 1;
        quickFilters.forEach(filter => {
          if (INLINE_QUICK_FILTER_CODES.has(filter.code)) {
            const matchingFacet = allfacetsItems.find(f => f.code === filter.code);
            const values = Array.isArray(filter.values) ? filter.values : [];
            values.forEach(valueCode => {
              const facetValue = matchingFacet?.values?.find(v => v.code === valueCode);
              visualFiltersArray.push({
                section: 1,
                position: qfPosition++,
                facet_key: filter.code,
                facet_value: valueCode,
                value: facetValue?.name || valueCode,
                selection_state: 'non_selected',
                filterid: ''
              });
            });
          } else {
            visualFiltersArray.push({
              section: 1,
              position: qfPosition++,
              facet_key: filter.code,
              facet_value: Array.isArray(filter.values) ? filter.values.join('|') : '',
              value: filter.name || filter.code,
              selection_state: 'non_selected',
              filterid: ''
            });
          }
        });
      }
    }

    const resolvedSearchType = getResolvedSearchType(search_type, urlParams, restOtherOptions);
    const searchPlpFilterAnalytics = buildSearchPlpFilterAnalytics(
      appState.facets || window?.__PRELOADED_STATE__?.facets || {},
      appState
    );
    const resolvedQuickFilterName = quick_filter_name || searchPlpFilterAnalytics.quick_filter_name;
    const resolvedQuickFilterCount =
      quick_filter_count !== '' && quick_filter_count !== undefined && quick_filter_count !== null
        ? Number(quick_filter_count)
        : searchPlpFilterAnalytics.quick_filter_count;
    const resolvedAdsEnabledFlag =
      ads_enabled_screen_flag !== '' && ads_enabled_screen_flag !== undefined
        ? !!ads_enabled_screen_flag
        : typeof restOtherOptions.jio_ads_enabled === 'boolean'
          ? restOtherOptions.jio_ads_enabled
          : getAdsEnabledScreenFlag(appState, pathname);
    const isPlpPageViewed = plpPageTypes.includes(page_type) || isSearchPlp;
    const resolvedScreenSourceDetail = getPlpScreenSourceDetail(restOtherOptions, urlParams);
    const resolvedVisualFilterCount = getVisibleVisualFilterCount(
      appState.facets || window?.__PRELOADED_STATE__?.facets || {},
      appState
    );
    const searchPlpPageViewedProps = isSearchPlp
      ? {
          quick_filter_name: resolvedQuickFilterName || '',
          quick_filter_count: Number.isFinite(resolvedQuickFilterCount)
            ? resolvedQuickFilterCount
            : 0,
          ads_enabled_screen_flag: !!resolvedAdsEnabledFlag,
          search_type: resolvedSearchType || '',
          filters: searchPlpFilterAnalytics.filters
        }
      : {};
    const plpPageViewedProps = isPlpPageViewed
      ? {
          visual_filter_count: Number.isFinite(resolvedVisualFilterCount)
            ? resolvedVisualFilterCount
            : 0,
          screen_source_detail: resolvedScreenSourceDetail || ''
        }
      : {};

    const screenViewedToken = getValidAttributionToken();
    const resolvedPageCategory = getStoredPageCategory();
    segmentDatalayerPushData(
      {
        event: "Screen Viewed",
        screen_name: isBrandPlpScreen(pathname) ? 'brand plp screen' : page_name,
        screen_source: isRedirectedFromCopilot ? copilotRedirectionValue : page_type,
        ...restOtherOptions,
        ...widgetSourceProps,
        ...getPageUserInfoForSegment(),
        store_type: getStoreName(),
        ads_enabled_screen_flag: !!resolvedAdsEnabledFlag,
        ...screenViewedToken && { attribution_token: screenViewedToken },
        ...(resolvedPageCategory && { page_category: resolvedPageCategory }),
        ...cart && cart?.totalUnitCount > 0 && getCartItemProperty(cart, isAssuredGifts, internalWallets),
        ...best_coupon_discount && { best_coupon_discount: best_coupon_discount },
        ...best_coupon_name && { best_coupon_name: best_coupon_name },
        ...coachmark_impression && { coachmark_impression: coachmark_impression },               
        ...order && order?.orderSummary && {
          order_id : order.id,
          order_total: order?.orderSummary?.bagTotal?.value,       
          bag_item_count : order?.totalItems,               
          bag_savings: order?.orderSummary?.bagDiscount?.value, 
          coupon_savings: order?.orderSummary?.couponSaving?.value, 
          order_total: order?.orderSummary?.orderTotal?.value,     
          platform_fee: order?.orderSummary?.convenienceFee?.total?.netAmount?.value, 
          total_savings: order?.orderSummary?.totalSaving?.value,
          rone_balance: order?.relianceOneEarnedAmount?.value,    
          delivery_fee: order?.orderSummary?.deliveryCharge?.value,        
          payment_type: order?.payment_type,       
          delivery_fee: order?.orderSummary?.deliveryCharge?.value,
        },
        ...ajioWalletData && {
          //rone_balance: Number(rone_balance),
          //rone_balance_used: Number(rone_balance_used),
          //rone_points_applicable: Number(rone_points_applicable),        
          wallet_cash_balance: ajioWalletData?.ajioWalletBalanceDet?.totalWalletBalance?.total,
          wallet_points_balance: ajioWalletData?.ajioWalletBalanceDet?.totalWalletBalance?.totalPoints,
          //wallet_points_applicable: Number(wallet_points_applicable),        
          //wallet_points_used: Number(wallet_points_used),
        },
        ...getCartItemProperty(cart, isAssuredGifts, internalWallets),
        ...best_coupon_discount && { best_coupon_discount: best_coupon_discount },
        ...best_coupon_name && { best_coupon_name: best_coupon_name },
        ...coachmark_impression && { coachmark_impression: coachmark_impression },     
        ...number_of_ratings_collected && {number_of_ratings_collected : number_of_ratings_collected},  
        ...number_of_color_variant_avialable && {number_of_color_variant_avialable: number_of_color_variant_avialable},        
        is_entrance_flag: checkEntranceFlag(),
        //number_of_color_variant_available: '',
        //number_of_rating_collected: '',       
        page_url: window.location.href,
        ...current_experience && { persona_store_type: current_experience || '' },        
        //priority_delivery_fee: '',
        ...quick_filter_count && { quick_filter_count: quick_filter_count },
        ...quick_filter_name && { quick_filter_name: quick_filter_name },
        ...(!isPlpPageViewed && visualFilterCount && { visual_filter_count: visualFilterCount }),
        ...visualFiltersArray && { filters: visualFiltersArray },
        ...urlParams.get('query') && {raw_query: urlParams.get('query') },        
        //return_processing_fee: '',       
        screen_title: document.title,
        ...search_term && { search_term: search_term },
        ...search_type && { search_type: search_type },
        ...resolvedSearchExpansionType && { search_expansion_type: resolvedSearchExpansionType },
        ...searchPlpPageViewedProps,
        ...plpPageViewedProps,
        ...page_type=='bag screen' && {
          bag_primary_cta_name: bag_primary_cta_name,
          bag_primary_cta_state: bag_primary_cta_state,        
          bag_secondary_cta_name: bag_secondary_cta_name,
          bag_secondary_cta_status: bag_secondary_cta_status,
        }
       
       
      },
      'gtmPagePush'
    );
}
